/* ═══════════════════════════════════════════════════════════════════
   HARMONEE PULSE — NORTH STAR
   ═══════════════════════════════════════════════════════════════════
   When someone opens Harmonee, they should feel:

   1. "This understands my organization."
   2. "This is calm, not chaotic."
   3. "This shows me what I didn't know to look for."
   4. "This sees across silos."
   5. "This is ahead of the problem."
   6. "This is not software. This is infrastructure."

   Every change must pass the test: does it make someone feel one of these?
   See BRAND.md for the full constitution.
   ═══════════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import snapshot from './data/snapshot.json';
import { isConfigured, initAuth, signIn, signInSilent, signOut, getStoredToken, setGapiToken, getAccounts, upsertAccount, removeAccount, addGoogleAccount, migrateLegacyAccount, getPrimaryAccount, getTokenFor } from './google/auth';
import { fetchAllData, fetchAllDataMulti, withAccountToken, toggleTaskComplete, createGoogleTask, createGmailDraft, sendGmailEmail, createGoogleCalendarEvent, searchGmailMessages, searchDriveFiles, fetchGoogleUserInfo, fetchGmailMessageBody, deepResearch } from './google/api';
import { initMsAuth, msSignIn, msSignOut } from './microsoft/auth';
import { fetchAllMsData, fetchMsUserInfo, createMsDraft, sendMsEmail, searchMsEmails, searchMsDrive, createMsCalendarEvent } from './microsoft/api';
import { rememberConversation, rememberSession, getMemoryContext } from './memory';
import posthog from 'posthog-js';
// Quiet Intelligence components — reusable, presentational, real-data-driven
import SystemPulse from './components/SystemPulse';
import MorningIntelligence from './components/MorningIntelligence';
import ApprovalRow from './components/ApprovalRow';
import AriaStateIndicator from './components/AriaStateIndicator';
import PortfolioCommand from './components/PortfolioCommand';
import AccountMenu, { GIcon, MsIcon } from './components/AccountMenu';

// ──── Security: escape untrusted text before it goes into an HTML string ────
// Gmail subjects/senders, calendar titles, and Drive doc names are attacker-controllable
// (anyone can email you a subject like `<img src=x onerror=...>`). Any value from those
// sources that lands inside a string later rendered with dangerouslySetInnerHTML MUST pass
// through esc() first. (Plain JSX text nodes are already escaped by React — don't double-escape those.)
function esc(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

// ──── Time helpers ────
function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const diff = Date.now() - timestamp;
  const sec = Math.floor(diff / 1000);
  if (sec < 30) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

const PRIORITY_COLORS = {
  red: 'var(--red)',
  gold: 'var(--gold)',
  green: 'var(--green)',
  blue: 'var(--blue)',
  dim: 'var(--mid)',
};

const TAG_COLORS = {
  red: { bg: 'rgba(224,82,82,0.12)', color: 'var(--red)' },
  gold: { bg: 'rgba(201,168,76,0.12)', color: 'var(--gold)' },
  green: { bg: 'rgba(76,175,124,0.12)', color: 'var(--green)' },
  blue: { bg: 'rgba(74,158,255,0.12)', color: '#4a9eff' },
  purple: { bg: 'rgba(139,124,246,0.12)', color: '#8b7cf6' },
  dim: { bg: 'rgba(58,58,64,0.3)', color: 'var(--mid)' },
};

const LogoSVG = () => (
  <svg viewBox="0 0 28 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Harmonee H mark — interlocking L forms */}
    <path d="M4 2 L4 18 L10 18 L10 14 L8 14 L8 2 Z" fill="url(#hGrad)"/>
    <path d="M10 14 L10 18 L16 18 L16 2 L20 2 L20 30 L16 30 L16 18 L10 18 L10 14 Z" fill="url(#hGrad)"/>
    <path d="M20 2 L24 2 L24 18 L20 18 Z" fill="url(#hGrad)" opacity="0.85"/>
    <defs>
      <linearGradient id="hGrad" x1="4" y1="2" x2="24" y2="30" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#B8892A"/>
        <stop offset="1" stopColor="#C9A84C"/>
      </linearGradient>
    </defs>
  </svg>
);

const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1 13L13 7L1 1V6L9 7L1 8V13Z" fill="#000"/>
  </svg>
);

// ──── Custom Harmonee Icons — Swiss/Bauhaus geometric style ────

const HIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Envelope with bold geometric slit — Investor Outreach / Mail */}
    <rect x="2" y="5" width="20" height="14" rx="2" stroke={color} strokeWidth="2" fill="none"/>
    <path d="M2 7L12 14L22 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HIconBrief = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Clipboard with geometric block lines — Meeting Brief */}
    <rect x="5" y="3" width="14" height="18" rx="2" stroke={color} strokeWidth="2" fill="none"/>
    <rect x="8" y="1" width="8" height="4" rx="1" stroke={color} strokeWidth="2" fill={color} opacity="0.2"/>
    <line x1="8" y1="10" x2="16" y2="10" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="8" y1="14" x2="13" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const HIconRefresh = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Circular arrows — Follow-up / Refresh */}
    <path d="M4 12a8 8 0 0 1 14-5.3" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M20 12a8 8 0 0 1-14 5.3" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <polyline points="18 2 18 7 13 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="6 22 6 17 11 17" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HIconIndex = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Stacked geometric blocks — Indexer / Drive */}
    <rect x="3" y="3" width="8" height="8" rx="1" fill={color} opacity="0.25"/>
    <rect x="13" y="3" width="8" height="8" rx="1" fill={color} opacity="0.5"/>
    <rect x="3" y="13" width="8" height="8" rx="1" fill={color} opacity="0.5"/>
    <rect x="13" y="13" width="8" height="8" rx="1" fill={color} opacity="0.8"/>
  </svg>
);

const HIconMorning = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Rising sun with rays — Morning Brief */}
    <path d="M4 18h16" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M7 18a5 5 0 0 1 10 0" stroke={color} strokeWidth="2"/>
    <line x1="12" y1="7" x2="12" y2="9" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="7.05" y1="9.05" x2="8.46" y2="10.46" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="16.95" y1="9.05" x2="15.54" y2="10.46" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="5" y1="13" x2="6" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="18" y1="13" x2="19" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const HIconGear = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Geometric gear — Automations */}
    <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="2"/>
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const HIconCalendar = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Bold calendar block — Meeting Prep */}
    <rect x="3" y="5" width="18" height="16" rx="2" stroke={color} strokeWidth="2"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke={color} strokeWidth="2"/>
    <line x1="8" y1="3" x2="8" y2="7" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="16" y1="3" x2="16" y2="7" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <rect x="7" y="13" width="3" height="3" rx="0.5" fill={color} opacity="0.6"/>
  </svg>
);

const HIconDoc = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Document with folded corner — Documents */}
    <path d="M6 3h8l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke={color} strokeWidth="2" fill="none"/>
    <path d="M14 3v5h5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="8" y1="13" x2="16" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="8" y1="17" x2="13" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const HIconWrapUp = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Checkmark in circle — Wrap-Up */}
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2"/>
    <polyline points="8 12 11 15 16 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HIconBolt = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Lightning bolt — Quick action / Reply */}
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill={color} opacity="0.85"/>
  </svg>
);

const HIconChart = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Rising bars — Chart / Sheets / Prep */}
    <rect x="3" y="14" width="4" height="7" rx="0.5" fill={color} opacity="0.4"/>
    <rect x="10" y="9" width="4" height="12" rx="0.5" fill={color} opacity="0.65"/>
    <rect x="17" y="4" width="4" height="17" rx="0.5" fill={color} opacity="0.9"/>
  </svg>
);

const HIconNote = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Pencil writing — Notes / Edit */}
    <path d="M15 5l4 4-10 10H5v-4L15 5z" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
    <line x1="12" y1="8" x2="16" y2="12" stroke={color} strokeWidth="2"/>
  </svg>
);

const HIconPages = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Stacked pages — Multi-page doc */}
    <rect x="6" y="2" width="14" height="17" rx="1.5" stroke={color} strokeWidth="2" fill="none"/>
    <rect x="3" y="5" width="14" height="17" rx="1.5" stroke={color} strokeWidth="2" fill="none" opacity="0.5"/>
  </svg>
);

const HIconMail = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Small envelope — Gmail integration */}
    <rect x="2" y="5" width="20" height="14" rx="2" stroke={color} strokeWidth="2"/>
    <path d="M2 7l10 6 10-6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const HIconDrive = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Triangle/drive shape — Google Drive */}
    <path d="M8 4l8 14H4L8 4z" stroke={color} strokeWidth="2" fill={color} opacity="0.15" strokeLinejoin="round"/>
    <path d="M16 4l4 14" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <line x1="4" y1="18" x2="20" y2="18" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const HIconCheck = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Bold check — Tasks */}
    <rect x="3" y="3" width="18" height="18" rx="3" stroke={color} strokeWidth="2"/>
    <polyline points="7 12 10 16 17 8" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HIconBrain = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Abstract brain/neural — ARIA engine */}
    <circle cx="12" cy="8" r="4" stroke={color} strokeWidth="2"/>
    <path d="M6 16c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <circle cx="8" cy="16" r="2" fill={color} opacity="0.3"/>
    <circle cx="16" cy="16" r="2" fill={color} opacity="0.3"/>
    <circle cx="12" cy="20" r="2" fill={color} opacity="0.5"/>
  </svg>
);

const HIconLink = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Chain links — LCM Context Engine */}
    <path d="M10 14a3.5 3.5 0 0 0 5-5l-1-1" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <path d="M14 10a3.5 3.5 0 0 0-5 5l1 1" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <circle cx="7" cy="17" r="3" stroke={color} strokeWidth="1.5" opacity="0.5"/>
    <circle cx="17" cy="7" r="3" stroke={color} strokeWidth="1.5" opacity="0.5"/>
  </svg>
);

const HIconPredict = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Crystal/diamond — Prediction */}
    <path d="M12 3L4 10l8 11 8-11L12 3z" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
    <line x1="4" y1="10" x2="20" y2="10" stroke={color} strokeWidth="2"/>
    <line x1="12" y1="3" x2="9" y2="10" stroke={color} strokeWidth="1.5" opacity="0.5"/>
    <line x1="12" y1="3" x2="15" y2="10" stroke={color} strokeWidth="1.5" opacity="0.5"/>
  </svg>
);

const HIconWarn = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Triangle warning — bold geometric */}
    <path d="M12 3L2 21h20L12 3z" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
    <line x1="12" y1="10" x2="12" y2="15" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="12" cy="18" r="1" fill={color}/>
  </svg>
);

const HIconAlert = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Bell/alarm — Alert */}
    <path d="M12 3a6 6 0 0 0-6 6v4l-2 3h16l-2-3V9a6 6 0 0 0-6-6z" stroke={color} strokeWidth="2"/>
    <path d="M10 19a2 2 0 0 0 4 0" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const HIconInsight = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Lightbulb — Insight */}
    <path d="M9 21h6M12 3a6 6 0 0 0-4 10.5V17h8v-3.5A6 6 0 0 0 12 3z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="10" y1="17" x2="10" y2="19" stroke={color} strokeWidth="1.5"/>
    <line x1="14" y1="17" x2="14" y2="19" stroke={color} strokeWidth="1.5"/>
  </svg>
);

const HIconSection = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Target/bullseye — Attention Engine section marker */}
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2"/>
    <circle cx="12" cy="12" r="5" stroke={color} strokeWidth="2" opacity="0.6"/>
    <circle cx="12" cy="12" r="1.5" fill={color}/>
  </svg>
);

const HIconSync = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Cloud sync — task sync */}
    <path d="M6 16a4 4 0 0 1-.88-7.9A6 6 0 0 1 18 10h1a3 3 0 0 1 0 6H6z" stroke={color} strokeWidth="2"/>
  </svg>
);

const HIconUpload = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Upload arrow — unsync'd task */}
    <line x1="12" y1="19" x2="12" y2="8" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <polyline points="8 12 12 8 16 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="5" y1="21" x2="19" y2="21" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Icon resolver — maps string keys to components
const ICON_MAP = {
  // Automations
  'mail': HIcon,
  'brief': HIconBrief,
  'refresh': HIconRefresh,
  'index': HIconIndex,
  // Sidebar workspace
  'morning': HIconMorning,
  'gear': HIconGear,
  'calendar': HIconCalendar,
  'doc': HIconDoc,
  'wrapup': HIconWrapUp,
  // Actions
  'bolt': HIconBolt,
  'chart': HIconChart,
  'note': HIconNote,
  'pages': HIconPages,
  // Integrations
  'gmail': HIconMail,
  'cal': HIconCalendar,
  'drive': HIconDrive,
  'sheets': HIconChart,
  'tasks': HIconCheck,
  // Engines
  'brain': HIconBrain,
  'link': HIconLink,
  // Alerts
  'predict': HIconPredict,
  'warn': HIconWarn,
  'alert': HIconAlert,
  'insight': HIconInsight,
  'section': HIconSection,
  // Task sync
  'sync': HIconSync,
  'upload': HIconUpload,
};

const HarmoneeIcon = ({ name, size, color }) => {
  const IconComp = ICON_MAP[name];
  if (!IconComp) return <span>{name}</span>;
  return <IconComp size={size} color={color} />;
};

const HarmoneePulse = () => {
  // Theme
  const [theme, setTheme] = useState(() => localStorage.getItem('harmonee_theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('harmonee_theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  // Password gate
  const [isUnlocked, setIsUnlocked] = useState(() => sessionStorage.getItem('harmonee_access') === 'granted');
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (unlocking) return;
    setUnlocking(true);
    setPasswordError(false);
    try {
      // Validate server-side — the server sets a signed session cookie that the
      // API routes require. The password no longer lives in the client bundle.
      const resp = await fetch('/api/unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput }),
      });
      if (resp.ok) {
        sessionStorage.setItem('harmonee_access', 'granted');
        setIsUnlocked(true);
        setPasswordError(false);
        posthog.capture('dashboard_unlocked');
      } else {
        setPasswordError(true);
        setPasswordInput('');
        posthog.capture('password_failed');
      }
    } catch (err) {
      setPasswordError(true);
      posthog.capture('password_failed', { reason: 'network' });
    } finally {
      setUnlocking(false);
    }
  };

  const [activeNav, setActiveNav] = useState('Intelligence');

  // ──── Portfolio Command Plan (gated /api/portfolio) ────
  // Deal terms and security-incident detail never ship in the public
  // bundle; the plan is fetched only after the session is unlocked.
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioStatus, setPortfolioStatus] = useState('locked'); // locked | loading | ready | error
  const loadPortfolio = useCallback(async () => {
    setPortfolioStatus('loading');
    try {
      const resp = await fetch('/api/portfolio', { credentials: 'same-origin' });
      if (resp.status === 401) { setPortfolioStatus('locked'); return; }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setPortfolio(await resp.json());
      setPortfolioStatus('ready');
    } catch (err) {
      console.warn('Portfolio load failed:', err);
      setPortfolioStatus('error');
    }
  }, []);
  useEffect(() => { if (isUnlocked) loadPortfolio(); }, [isUnlocked, loadPortfolio]);
  const [activeSidebar, setActiveSidebar] = useState('Morning Brief');
  // setActivePanel is defined below with the responsive panel state — it
  // surfaces the ARIA panel at any viewport (drawer on tablet, tab on mobile).
  const [rcExpanded, setRcExpanded] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'aria',
      sender: 'ARIA · Living Context',
      text: `Good morning, Marcio. I've reviewed your overnight signals. The <span class="em">Caroline Casson thread</span> is the highest-leverage action today — she replied saying she hasn't run your solution by her team yet. This is your re-engagement window. Want me to draft a patient follow-up?`,
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => localStorage.getItem('harmonee_voice') === 'on');
  const recognitionRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const [tasks, setTasks] = useState(snapshot.tasks.map((t, i) => ({ ...t, id: i })));
  const [contactGraph, setContactGraph] = useState([]);
  const [briefModal, setBriefModal] = useState({ open: false, text: '', closing: false });
  const [mobilePanel, setMobilePanel] = useState('home'); // 'home' | 'aria' | 'menu'
  // Tablet-range (769-1180px) ARIA slide-over drawer, opened via the FAB
  const [ariaDrawerOpen, setAriaDrawerOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const rightPanelRef = useRef(null);

  // Live viewport tier — tracked with listeners (not sampled once) so the
  // focus trap, drawer, and inert state stay correct across window resizes.
  const [viewportTier, setViewportTier] = useState(() =>
    window.matchMedia('(max-width: 768px)').matches ? 'mobile'
      : window.matchMedia('(max-width: 1180px)').matches ? 'tablet' : 'desktop');
  useEffect(() => {
    const mMobile = window.matchMedia('(max-width: 768px)');
    const mTablet = window.matchMedia('(max-width: 1180px)');
    const update = () => setViewportTier(
      mMobile.matches ? 'mobile' : mTablet.matches ? 'tablet' : 'desktop');
    mMobile.addEventListener('change', update);
    mTablet.addEventListener('change', update);
    return () => {
      mMobile.removeEventListener('change', update);
      mTablet.removeEventListener('change', update);
    };
  }, []);
  // Leaving the tablet range closes the drawer so its Esc listener drops.
  useEffect(() => {
    if (viewportTier !== 'tablet' && ariaDrawerOpen) setAriaDrawerOpen(false);
  }, [viewportTier, ariaDrawerOpen]);

  // Surface the ARIA panel at any viewport. 18 call sites use this after
  // staging a message for the user. On desktop the panel is always visible,
  // so there is nothing to open. (Phase 2.2 regression fix: this previously
  // wrote to a state variable nothing read, so at <=1180px every "open
  // ARIA" action pointed at an off-screen panel.)
  const setActivePanel = useCallback((panel) => {
    if (panel !== 'aria') return;
    if (window.matchMedia('(max-width: 768px)').matches) {
      setMobilePanel('aria');
      setMobileSidebarOpen(false);
    } else if (window.matchMedia('(max-width: 1180px)').matches) {
      setAriaDrawerOpen(true);
    }
  }, []);

  // Focus management for the ARIA panel when it acts as an overlay
  // (tablet drawer or mobile full-screen): focus moves into the panel,
  // Tab cycles inside it, Esc closes, and focus returns on close.
  useEffect(() => {
    const overlayActive =
      (viewportTier === 'tablet' && ariaDrawerOpen) ||
      (viewportTier === 'mobile' && mobilePanel === 'aria');
    if (!overlayActive) return;
    const panel = rightPanelRef.current;
    const previouslyFocused = document.activeElement;
    const focusInput = setTimeout(() => inputRef.current?.focus(), 250);
    // Trap Tab only for the tablet drawer. On mobile the bottom nav sits
    // outside the panel and must stay keyboard-reachable.
    const trapTab = viewportTier === 'tablet';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setAriaDrawerOpen(false);
        setMobilePanel('home');
        return;
      }
      if (trapTab && e.key === 'Tab' && panel) {
        const focusables = Array.from(panel.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
        )).filter((el) => el.offsetParent !== null); // skip display:none elements
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(focusInput);
      document.removeEventListener('keydown', onKey);
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ariaDrawerOpen, mobilePanel, viewportTier]);

  // Onboarding tour state
  const [showTour, setShowTour] = useState(() => !localStorage.getItem('harmonee_tour_seen'));
  const [tourStep, setTourStep] = useState(0);

  // Push notification permission state
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const notifiedSignalsRef = useRef(new Set(JSON.parse(localStorage.getItem('harmonee_notified_signals') || '[]')));

  const requestNotifPermission = async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === 'granted') {
      new Notification('ARIA is watching', {
        body: 'I\'ll ping you when something needs your attention.',
        icon: '/logo192.png',
        badge: '/logo192.png',
      });
      posthog.capture('notifications_enabled');
    }
  };

  const fireNotification = (title, body, key) => {
    if (notifPermission !== 'granted') return;
    if (key && notifiedSignalsRef.current.has(key)) return;
    if (key) {
      notifiedSignalsRef.current.add(key);
      localStorage.setItem('harmonee_notified_signals', JSON.stringify([...notifiedSignalsRef.current].slice(-50)));
    }
    try {
      const n = new Notification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: 'harmonee-' + (key || Date.now()),
        silent: false,
      });
      n.onclick = () => { window.focus(); n.close(); };
      posthog.capture('notification_fired', { key });
    } catch (e) { /* swallow */ }
  };
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  // Auth + live data state
  const [isAuthed, setIsAuthed] = useState(false);
  const [googleAuthed, setGoogleAuthed] = useState(false);
  const [msAuthedState, setMsAuthedState] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(null);
  // Force re-render every 30s so "Checked X ago" stays accurate without a stored clock
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setClockTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const [liveData, setLiveData] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [googleUser, setGoogleUser] = useState(null);
  // Every connected Google account ({email, name, picture, token…}); first is primary.
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [addingAccount, setAddingAccount] = useState(false);
  // The account hub anchored to the profile chip (also opened by the provider pills)
  const [accountHubOpen, setAccountHubOpen] = useState(false);
  const userChipRef = useRef(null);

  // Microsoft auth state
  const [msUser, setMsUser] = useState(null);

  // Suggestions and automations state (mutable)
  const [suggestions, setSuggestions] = useState(snapshot.suggestions);
  const [automations, setAutomations] = useState(snapshot.automations);

  // Active project filter
  const [activeProject, setActiveProject] = useState(null);

  // Health score expanded state
  const [healthExpanded, setHealthExpanded] = useState(false);

  // Live brief state — dynamically generated from real data
  const [liveBrief, setLiveBrief] = useState(null);

  const data = liveData || snapshot;

  // Use signed-in user info when available, fall back to defaults
  const userName = googleUser?.name || null;
  const userFirstName = googleUser?.firstName || data.user.name.split(' ')[0];
  const userInitials = googleUser?.initials || null;
  const userPicture = googleUser?.picture || null;

  // ──── Generate Unified Morning Brief from All Connected Sources ────
  const generateLiveBrief = useCallback((liveResult, userTasks, firstName) => {
    const cal = liveResult.calendar || [];
    const emails = liveResult.gmail?.priorityThreads || [];
    const drive = liveResult.drive || [];
    const taskList = userTasks || [];

    // ── Paragraph 1: Your Day at a Glance (Calendar + Urgent Emails) ──
    const parts1 = [];

    // Meetings — unified across Google Calendar + Outlook Calendar
    if (cal.length > 0) {
      // Sort by start time for consistent ordering
      const sorted = [...cal].sort((a, b) => {
        const timeA = a.start === 'All day' ? '00:00' : a.start;
        const timeB = b.start === 'All day' ? '00:00' : b.start;
        return timeA.localeCompare(timeB);
      });
      const firstMeeting = sorted[0];
      if (cal.length === 1) {
        parts1.push(`You have <strong>1 meeting today</strong> — <strong>${esc(firstMeeting.summary)}</strong> at ${esc(firstMeeting.start)}.`);
      } else {
        parts1.push(`You have <strong>${cal.length} meetings today</strong>. Your first is <strong>${esc(firstMeeting.summary)}</strong> at ${esc(firstMeeting.start)}.`);
        // Highlight the most notable meeting (most attendees or longest name = likely most important)
        const notable = sorted.find(m => m.sub && m.sub.includes('attendee'));
        if (notable && notable.id !== firstMeeting.id) {
          parts1.push(`<strong>${esc(notable.summary)}</strong> is at ${esc(notable.start)}${notable.sub ? ` — ${esc(notable.sub)}` : ''}.`);
        }
      }
    } else {
      parts1.push(`<strong>No meetings today</strong> — a clear calendar for deep work.`);
    }

    // Emails — unified across Gmail + Outlook, ranked by urgency
    const urgentEmails = emails.filter(e => e.priority === 'red' || e.priority === 'gold');
    const regularEmails = emails.filter(e => e.priority !== 'red' && e.priority !== 'gold' && e.priority !== 'dim');

    if (urgentEmails.length > 0) {
      const topEmail = urgentEmails[0];
      parts1.push(`The <span class="highlight">${esc(topEmail.from)} thread</span> needs attention — "${esc(topEmail.subject)}".`);
      if (urgentEmails.length > 1) {
        const others = urgentEmails.slice(1, 3).map(e => `<strong>${esc(e.from)}</strong>`).join(' and ');
        parts1.push(`${others} also flagged as priority.`);
      }
    } else if (emails.length > 0) {
      parts1.push(`${emails.length} email thread${emails.length > 1 ? 's' : ''} across your inboxes — nothing urgent, but <strong>${esc(emails[0].from)}</strong> sent "${esc(emails[0].subject)}".`);
    }

    // ── Paragraph 2: Operational View (Docs + Tasks) ──
    const parts2 = [];

    if (drive.length > 0) {
      const recentDoc = drive[0];
      parts2.push(`On the operational side, <strong>${esc(recentDoc.modifiedBy)}</strong> updated "${esc(recentDoc.title)}" ${esc(recentDoc.modified)}.`);
      if (drive.length > 1) {
        parts2.push(`${drive.length - 1} other document${drive.length - 1 > 1 ? 's' : ''} modified recently across your workspace.`);
      }
    }

    const overdue = taskList.filter(t => !t.done && (t.tag === 'Overdue' || t.tagColor === 'red'));
    const todayTasks = taskList.filter(t => !t.done && t.tag === 'Today');
    const pendingCount = taskList.filter(t => !t.done).length;

    if (overdue.length > 0) {
      parts2.push(`<strong>${overdue.length} overdue task${overdue.length > 1 ? 's' : ''}</strong> need${overdue.length === 1 ? 's' : ''} attention: ${overdue.slice(0, 2).map(t => t.text.split('—')[0].trim()).join(', ')}.`);
    } else if (todayTasks.length > 0) {
      parts2.push(`<strong>${todayTasks.length} task${todayTasks.length > 1 ? 's' : ''}</strong> due today. ${pendingCount} total in your queue.`);
    } else if (pendingCount > 0) {
      parts2.push(`${pendingCount} task${pendingCount > 1 ? 's' : ''} in your queue — nothing overdue.`);
    }

    // ── Smart Actions — best next moves across all sources ──
    const actions = [];

    // Priority 1: Reply to most urgent email
    if (urgentEmails.length > 0) {
      actions.push({
        icon: 'mail',
        label: `Draft ${urgentEmails[0].from.split(' ')[0]} follow-up`,
        key: urgentEmails[0].from.toLowerCase().split(' ')[0],
      });
    }

    // Priority 2: Prep for next meeting
    if (cal.length > 0) {
      const sorted = [...cal].sort((a, b) => {
        const timeA = a.start === 'All day' ? '00:00' : a.start;
        const timeB = b.start === 'All day' ? '00:00' : b.start;
        return timeA.localeCompare(timeB);
      });
      actions.push({
        icon: 'calendar',
        label: `Prep for ${sorted[0].summary.slice(0, 25)}`,
        key: 'meeting_prep',
      });
    }

    // Priority 3: Review most recent doc
    if (drive.length > 0) {
      actions.push({
        icon: 'brief',
        label: `Review ${drive[0].title.slice(0, 25)}`,
        key: 'review_doc',
      });
    }

    // Priority 4: Reply to a non-urgent email
    if (regularEmails.length > 0 && actions.length < 4) {
      actions.push({
        icon: 'bolt',
        label: `Reply to ${regularEmails[0].from.split(' ')[0]}`,
        key: regularEmails[0].from.toLowerCase().split(' ')[0],
      });
    }

    // Fallback actions
    if (actions.length === 0) {
      actions.push({ icon: 'check', label: 'Review tasks', key: 'tasks' });
      actions.push({ icon: 'brief', label: 'Check documents', key: 'docs' });
    }

    return {
      text: parts1.join(' '),
      text2: parts2.join(' ') || 'All clear on the operational front — no pending items flagged.',
      actions: actions.slice(0, 4),
    };
  }, []);

  // ──── Business Health Score (computed from real data) ────
  const computeHealthScore = () => {
    const tasksDone = tasks.filter(t => t.done).length;
    const tasksTotal = tasks.length || 1;
    const taskScore = (tasksDone / tasksTotal) * 100;

    const urgentEmails = data.gmail.priorityThreads.filter(t => t.priority === 'red' || t.priority === 'gold').length;
    const emailScore = Math.max(0, 100 - (urgentEmails * 20));

    const meetingsToday = data.calendar.length;
    const meetingScore = meetingsToday <= 4 ? 90 : meetingsToday <= 6 ? 70 : 50;

    const autoRunning = automations.filter(a => a.active).length;
    const autoScore = (autoRunning / Math.max(automations.length, 1)) * 100;

    const overall = Math.round((taskScore * 0.3) + (emailScore * 0.25) + (meetingScore * 0.25) + (autoScore * 0.2));

    let status, color, label;
    if (overall >= 75) { status = 'green'; color = 'var(--green)'; label = 'Aligned'; }
    else if (overall >= 50) { status = 'gold'; color = 'var(--gold)'; label = 'Attention'; }
    else { status = 'red'; color = 'var(--red)'; label = 'Misaligned'; }

    return {
      overall,
      status,
      color,
      label,
      breakdown: {
        tasks: { score: Math.round(taskScore), label: 'Task Completion', detail: `${tasksDone}/${tasksTotal} done` },
        email: { score: Math.round(emailScore), label: 'Email Health', detail: `${urgentEmails} urgent threads` },
        meetings: { score: Math.round(meetingScore), label: 'Calendar Load', detail: `${meetingsToday} meetings` },
        automations: { score: Math.round(autoScore), label: 'Automation Coverage', detail: `${autoRunning}/${automations.length} active` },
      },
    };
  };

  const healthScore = computeHealthScore();

  // ──── Predictive Alerts ────
  const predictiveAlerts = (() => {
    const alerts = [];
    // Check for emails that might go cold
    const goldEmails = data.gmail.priorityThreads.filter(t => t.priority === 'gold');
    goldEmails.forEach(email => {
      alerts.push({
        type: 'prediction',
        icon: 'predict',
        title: `${email.from.split('·')[0].trim()} may go cold`,
        desc: `Based on communication patterns, this thread needs a response within 24 hours to maintain engagement.`,
        urgency: 'high',
        color: 'var(--gold)',
      });
    });
    // Check for overloaded calendar
    if (data.calendar.length >= 4) {
      alerts.push({
        type: 'warning',
        icon: 'warn',
        title: 'Calendar overload detected',
        desc: `${data.calendar.length} meetings today. ARIA recommends blocking 30 min for deep work between meetings.`,
        urgency: 'medium',
        color: 'var(--blue)',
      });
    }
    // Check for overdue tasks
    const overdueTasks = tasks.filter(t => !t.done && (t.tag === 'Urgent' || t.tag === 'Overdue' || t.tagColor === 'red'));
    if (overdueTasks.length > 0) {
      alerts.push({
        type: 'alert',
        icon: 'alert',
        title: `${overdueTasks.length} urgent task${overdueTasks.length > 1 ? 's' : ''} need attention`,
        desc: overdueTasks.map(t => t.text.split('—')[0].trim()).join(', '),
        urgency: 'high',
        color: 'var(--red)',
      });
    }
    // Cross-project intelligence
    const projectCounts = {};
    (data.drive || []).forEach(doc => { projectCounts[doc.project] = (projectCounts[doc.project] || 0) + 1; });
    const topProject = Object.entries(projectCounts).sort((a, b) => b[1] - a[1])[0];
    if (topProject) {
      alerts.push({
        type: 'insight',
        icon: 'insight',
        title: `${topProject[0]} has the most activity`,
        desc: `${topProject[1]} documents modified recently. Cross-referencing with email threads for strategic alignment.`,
        urgency: 'low',
        color: 'var(--green)',
      });
    }
    return alerts;
  })();

  const today = new Date();
  const hour = today.getHours();
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = today.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Initialize Google Auth on mount
  useEffect(() => {
    if (!isConfigured()) {
      setAuthReady(true);
      return;
    }
    // Init Google with silent re-auth (no popup if already authorized).
    // Multi-account: hydrate every stored account; refresh expired tokens
    // silently per-account via login_hint.
    initAuth().then(async () => {
      setAuthReady(true);

      // Fold the pre-multi-account keys into the account store once.
      const cachedUser = localStorage.getItem('harmonee_user');
      if (cachedUser) {
        try { migrateLegacyAccount(JSON.parse(cachedUser)); } catch { /* bad cache */ }
      }

      const accounts = getAccounts();

      // Legacy path: a valid single token but no identified account yet.
      if (accounts.length === 0) {
        const token = getStoredToken();
        if (token) {
          setGapiToken(token);
          const userInfo = await fetchGoogleUserInfo();
          if (userInfo) {
            upsertAccount({ ...userInfo, ...token });
            localStorage.setItem('harmonee_user', JSON.stringify(userInfo));
          }
        }
      }

      // Ensure each stored account has a live token (silent, per-account).
      const live = [];
      for (const account of getAccounts()) {
        let token = getTokenFor(account.email);
        if (!token) {
          try {
            token = await signInSilent(account.email);
          } catch (e) {
            console.log('Silent re-auth failed for', account.email, e?.error || e?.message || e);
          }
        }
        if (token) live.push({ ...account, ...token });
      }

      if (live.length > 0) {
        // Primary token stays active on the shared gapi client.
        setGapiToken(getTokenFor(live[0].email) || live[0]);
        setGoogleAccounts(getAccounts());
        setGoogleAuthed(true);
        setIsAuthed(true);
        setGoogleUser(live[0]);
        // Refresh the primary profile in the background.
        fetchGoogleUserInfo().then(userInfo => {
          if (userInfo) {
            setGoogleUser(prev => ({ ...prev, ...userInfo }));
            upsertAccount(userInfo);
            localStorage.setItem('harmonee_user', JSON.stringify(userInfo));
          }
        }).catch(() => {});
      }
    }).catch(() => setAuthReady(true));

    // Init Microsoft (check for existing session)
    initMsAuth().then(async (account) => {
      if (account) {
        setMsAuthedState(true);
        setIsAuthed(true);
        const userInfo = await fetchMsUserInfo();
        if (userInfo) {
          setMsUser(userInfo);
          setGoogleUser(prev => prev || userInfo);
          posthog.identify(userInfo.email, { name: userInfo.name, email: userInfo.email });
          posthog.capture('outlook_connected', { user: userInfo.name });
        }
      }
    }).catch(e => console.warn('MS init error:', e));
  }, []);

  // Fetch live data from ALL connected providers and merge
  const loadLiveData = useCallback(async () => {
    if (!isAuthed) return;
    setIsLoading(true);
    const accounts = getAccounts();
    const providers = [];
    if (googleAuthed) providers.push(accounts.length > 1 ? `${accounts.length} Google accounts` : 'Gmail · Calendar · Drive · Tasks');
    if (msAuthedState) providers.push('Outlook');
    setCheckingStatus(`Scanning ${providers.join(' + ')}…`);
    try {
      // Google accounts fetch SERIALLY (one shared gapi token); Outlook in parallel.
      const googlePromise = googleAuthed
        ? (accounts.length > 0
            ? fetchAllDataMulti(accounts, (email) => setCheckingStatus(`Scanning ${email}…`)).catch(() => null)
            : fetchAllData().catch(() => null))
        : Promise.resolve(null);
      const msPromise = msAuthedState ? fetchAllMsData().catch(() => null) : Promise.resolve(null);

      const [googleResult, msResult] = await Promise.all([googlePromise, msPromise]);

      if (googleResult?.staleAccounts?.length) {
        addMessage('aria', 'ARIA · Reconnect needed', `I couldn't refresh ${googleResult.staleAccounts.map(esc).join(', ')} — open the Google menu in the top bar and reconnect ${googleResult.staleAccounts.length === 1 ? 'it' : 'them'}.`);
      }

      // Report what came back
      const summary = [];
      if (googleResult) {
        const threadCount = (googleResult.gmail?.priorityThreads?.length || 0) + (googleResult.gmail?.unansweredThreads?.length || 0);
        const calCount = googleResult.calendar?.length || 0;
        const driveCount = googleResult.drive?.length || 0;
        if (threadCount) summary.push(`${threadCount} emails`);
        if (calCount) summary.push(`${calCount} meetings`);
        if (driveCount) summary.push(`${driveCount} docs`);
      }
      if (msResult) {
        const msMail = msResult.gmail?.priorityThreads?.length || 0;
        if (msMail) summary.push(`${msMail} Outlook`);
      }
      setCheckingStatus(summary.length ? `Scanned ${summary.join(' · ')}` : 'Scan complete');

      // Merge results: combine emails, calendar, drive from both providers
      const mergedCalendar = [
        ...(googleResult?.calendar || []),
        ...(msResult?.calendar || []),
      ];
      const mergedEmails = [
        ...(googleResult?.gmail?.priorityThreads || []),
        ...(msResult?.gmail?.priorityThreads || []),
      ];
      const mergedDrive = [
        ...(googleResult?.drive || []),
        ...(msResult?.drive || []),
      ];
      const mergedTasks = googleResult?.tasks || [];
      const mergedSheets = googleResult?.sheets || [];

      const liveCal = mergedCalendar.length > 0 ? mergedCalendar : snapshot.calendar;
      const liveGmail = mergedEmails.length > 0 ? {
        priorityThreads: mergedEmails,
        unreadCount: (googleResult?.gmail?.unreadCount || 0) + (msResult?.gmail?.unreadCount || 0),
        // Preserve unanswered threads + my email — the approval queue's email-reply pipeline depends on these
        unansweredThreads: [...(googleResult?.gmail?.unansweredThreads || []), ...(msResult?.gmail?.unansweredThreads || [])],
        myEmail: googleResult?.gmail?.myEmail || msResult?.gmail?.myEmail,
      } : snapshot.gmail;
      const liveDrive = mergedDrive.length > 0 ? mergedDrive : snapshot.drive;
      const liveTasks = mergedTasks.length > 0 ? mergedTasks : null;

      const gStats = googleResult?.stats || {};
      const mStats = msResult?.stats || {};

      setLiveData({
        ...snapshot,
        calendar: liveCal,
        gmail: liveGmail,
        drive: liveDrive,
        stats: {
          ...snapshot.stats,
          emailsToday: (gStats.emailsToday || 0) + (mStats.emailsToday || 0) || snapshot.stats.emailsToday,
          emailsFlagged: (gStats.emailsFlagged || 0) + (mStats.emailsFlagged || 0) || snapshot.stats.emailsFlagged,
          meetings: (gStats.meetings || 0) + (mStats.meetings || 0) || snapshot.stats.meetings,
          meetingsTotal: (gStats.meetingsTotal || 0) + (mStats.meetingsTotal || 0) || snapshot.stats.meetingsTotal,
        },
      });
      if (liveTasks) {
        // Merge live tasks with local state — preserve completed status for tasks toggled locally
        setTasks(prev => {
          const localDone = new Set(prev.filter(t => t.done).map(t => t.id));
          const localUndone = new Set(prev.filter(t => !t.done).map(t => t.id));
          // Keep locally-created tasks that aren't in live data
          const localOnly = prev.filter(t => !t.listId && !liveTasks.find(lt => lt.text === t.text));
          // Merge: use live task data but preserve local done status
          const merged = liveTasks.map(lt => ({
            ...lt,
            done: localDone.has(lt.id) ? true : localUndone.has(lt.id) ? false : lt.done,
          }));
          return [...localOnly, ...merged];
        });
      }
      setSheets(mergedSheets);

      // Generate dynamic suggestions from live data
      const dynSuggestions = [];
      const urgentEmails = (liveGmail.priorityThreads || []).filter(
        e => e.priority === 'red' || e.priority === 'gold'
      );
      if (urgentEmails.length > 0) {
        dynSuggestions.push({
          type: 'alert',
          typeLabel: 'Urgent',
          impact: 'high',
          title: `${urgentEmails.length} high-priority email${urgentEmails.length > 1 ? 's' : ''} need attention`,
          desc: `You have ${urgentEmails.length} urgent email${urgentEmails.length > 1 ? 's' : ''} flagged red or gold. Review and respond to keep projects on track.`,
        });
      }
      if (liveCal.length > 0) {
        dynSuggestions.push({
          type: 'pattern',
          typeLabel: 'Prep',
          impact: 'medium',
          title: `${liveCal.length} meeting${liveCal.length > 1 ? 's' : ''} today \u2014 review agendas`,
          desc: `You have ${liveCal.length} meeting${liveCal.length > 1 ? 's' : ''} scheduled. Prepare talking points and check related documents before each one.`,
        });
      }
      const recentDocs = (liveDrive || []).filter(d => {
        if (!d.modified) return false;
        const diff = Date.now() - new Date(d.modified).getTime();
        return diff < 7 * 24 * 60 * 60 * 1000;
      });
      if (recentDocs.length > 0) {
        dynSuggestions.push({
          type: 'opportunity',
          typeLabel: 'Docs',
          impact: 'medium',
          title: `${recentDocs.length} document${recentDocs.length > 1 ? 's' : ''} updated recently`,
          desc: `${recentDocs.length} document${recentDocs.length > 1 ? 's' : ''} were modified in the last 7 days. Review changes to stay aligned with your team.`,
        });
      }
      if (dynSuggestions.length >= 2) {
        setSuggestions(dynSuggestions);
      }

      // Generate a fresh morning brief from the live data
      const brief = generateLiveBrief(
        { calendar: liveCal, gmail: liveGmail, drive: liveDrive },
        liveTasks || tasks,
        userFirstName,
      );
      setLiveBrief(brief);

      // Build relationship intelligence graph
      try {
        const contactResp = await fetch('/api/contacts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            emails: liveGmail.priorityThreads || [],
            calendar: liveCal,
            drive: liveDrive,
            tasks: liveTasks || tasks,
          }),
        });
        if (contactResp.ok) {
          const { contacts } = await contactResp.json();
          setContactGraph(contacts || []);
        }
      } catch (cErr) {
        console.warn('Contact graph build failed:', cErr);
      }
    } catch (e) {
      console.error('Failed to fetch live data:', e);
      setCheckingStatus('Scan failed — retrying in 5 min');
    } finally {
      setIsLoading(false);
      setLastChecked(Date.now());
      // Clear status after 4 seconds so it doesn't linger
      setTimeout(() => setCheckingStatus(null), 4000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, googleAuthed, msAuthedState, generateLiveBrief, userFirstName]);

  // Load live data once on auth, then auto-refresh every 5 minutes while authed
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (isAuthed && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      const timer = setTimeout(() => loadLiveData(), 300);
      // Always-on background refresh — Jarvis layer
      const interval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          loadLiveData();
        }
      }, 5 * 60 * 1000); // 5 minutes
      return () => { clearTimeout(timer); clearInterval(interval); };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  // Refresh when tab regains focus after being away
  useEffect(() => {
    const onFocus = () => {
      if (isAuthed && document.visibilityState === 'visible') {
        loadLiveData();
      }
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const handleSignIn = () => {
    signIn(
      async (token) => {
        setGapiToken(token);
        setGoogleAuthed(true);
        setIsAuthed(true);
        const userInfo = await fetchGoogleUserInfo();
        if (userInfo) {
          setGoogleUser(userInfo);
          upsertAccount({ ...userInfo, ...token });
          setGoogleAccounts(getAccounts());
          localStorage.setItem('harmonee_user', JSON.stringify(userInfo));
          posthog.identify(userInfo.email, { name: userInfo.name, email: userInfo.email });
          posthog.capture('google_connected', { user: userInfo.name });
        }
      },
      (err) => console.error('Sign in failed:', err)
    );
  };

  // Connect an ADDITIONAL Google account (forces the account chooser).
  const handleAddGoogleAccount = () => {
    setAddingAccount(true);
    addGoogleAccount(
      async (token) => {
        try {
          // Identify which inbox this token belongs to before storing it.
          setGapiToken(token);
          const userInfo = await fetchGoogleUserInfo();
          if (!userInfo?.email) throw new Error('Could not identify the new account');
          upsertAccount({ ...userInfo, ...token });
          const accounts = getAccounts();
          setGoogleAccounts(accounts);
          posthog.capture('google_account_added', { count: accounts.length });
          addMessage('aria', 'ARIA · Account added', `Connected <strong>${esc(userInfo.email)}</strong>. I'm rescanning across ${accounts.length} Google account${accounts.length === 1 ? '' : 's'} now.`);
          // Restore primary token, then rescan everything.
          const primary = getPrimaryAccount();
          const primaryToken = primary ? getTokenFor(primary.email) : null;
          if (primaryToken) setGapiToken(primaryToken);
          loadLiveData();
        } catch (err) {
          console.warn('Add account failed:', err);
          addMessage('aria', 'ARIA', `Couldn't add that Google account — ${esc(err?.message || 'the sign-in was cancelled')}. If this account isn't a test user on the OAuth app yet, Google will refuse it.`);
        } finally {
          setAddingAccount(false);
        }
      },
      (err) => {
        setAddingAccount(false);
        // User closing Google's popup is normal — stay quiet unless it's a real error.
        if (err?.type !== 'popup_closed') console.warn('Add account error:', err);
      }
    );
  };

  const handleRemoveGoogleAccount = (email) => {
    const next = removeAccount(email);
    setGoogleAccounts(next);
    posthog.capture('google_account_removed', { remaining: next.length });
    if (next.length === 0) {
      handleGoogleSignOut();
      return;
    }
    const primary = next[0];
    const primaryToken = getTokenFor(primary.email);
    if (primaryToken) setGapiToken(primaryToken);
    setGoogleUser(primary);
    localStorage.setItem('harmonee_user', JSON.stringify(primary));
    loadLiveData();
  };

  const handleMsSignIn = async () => {
    // This will redirect the page to Microsoft login
    // When it comes back, initMsAuth() handles the response via handleRedirectPromise()
    await msSignIn();
  };

  const handleGoogleSignOut = () => {
    posthog.capture('google_disconnected');
    posthog.reset();
    signOut(); // revokes and clears EVERY connected account
    setGoogleAuthed(false);
    setGoogleUser(null);
    setGoogleAccounts([]);
    localStorage.removeItem('harmonee_user');
    if (!msAuthedState) {
      setIsAuthed(false);
      setLiveData(null);
      setSheets([]);
      setTasks(snapshot.tasks.map((t, i) => ({ ...t, id: i })));
    }
  };

  const handleMsSignOut = () => {
    posthog.capture('outlook_disconnected');
    msSignOut();
    setMsAuthedState(false);
    setMsUser(null);
    if (!googleAuthed) {
      setIsAuthed(false);
      setLiveData(null);
      setGoogleUser(null);
      localStorage.removeItem('harmonee_user');
      setTasks(snapshot.tasks.map((t, i) => ({ ...t, id: i })));
    }
  };

  // Update ARIA greeting when user signs in — quick static greeting first, then AI-powered proactive brief
  const hasGreetedRef = useRef(false);
  useEffect(() => {
    if (googleUser && isAuthed) {
      const firstName = googleUser.firstName;
      // Immediate static greeting while AI loads
      setMessages(prev => {
        const updated = [...prev];
        if (updated.length > 0 && updated[0].type === 'aria') {
          updated[0] = {
            type: 'aria',
            sender: 'ARIA · Scanning',
            text: `${timeGreeting}, ${firstName}. I'm connected to your workspace — analyzing your day now...`,
          };
        }
        return updated;
      });
    }
  }, [googleUser, isAuthed, timeGreeting]);

  // Once live data is loaded, ask AI to generate a proactive brief
  useEffect(() => {
    if (!liveBrief || !isAuthed || hasGreetedRef.current) return;
    hasGreetedRef.current = true;

    const firstName = userFirstName || 'there';
    const pendingTasks = tasks.filter(t => !t.done);
    const urgentEmails = (data.gmail?.priorityThreads || []).filter(e => e.tagColor === 'red' || e.tagColor === 'gold');

    // Build a rich context for the AI greeting
    const greetingContext = [
      `Time of day: ${timeGreeting.replace('Good ', '')}`,
      `User's name: ${firstName}`,
      `Meetings today: ${data.calendar?.length || 0}`,
      data.calendar?.length > 0 ? `First meeting: ${data.calendar[0]?.summary} at ${data.calendar[0]?.start?.split('T')[1]?.slice(0,5) || 'soon'}` : 'No meetings today',
      `Pending tasks: ${pendingTasks.length}`,
      pendingTasks.length > 0 ? `Top tasks: ${pendingTasks.slice(0, 3).map(t => t.text).join(', ')}` : '',
      `Urgent emails: ${urgentEmails.length}`,
      urgentEmails.length > 0 ? `From: ${urgentEmails.slice(0, 3).map(e => e.from.split('<')[0].trim()).join(', ')}` : '',
      `Active projects: ${sidebarProjects.map(p => p.name).join(', ') || 'None detected'}`,
      `Business health: ${healthScore?.overall || 'N/A'}/100`,
      `Connected providers: ${[googleAuthed ? 'Google' : '', msAuthedState ? 'Outlook' : ''].filter(Boolean).join(' + ') || 'None'}`,
    ].filter(Boolean).join('\n');

    const proactivePrompt = `Generate a proactive morning/afternoon/evening briefing for ${firstName}. You are ARIA, their chief of staff. Be specific — reference their actual meetings, tasks, and emails by name. Structure it as:
1. One warm greeting line with the time of day
2. The #1 most important thing they should focus on right now and why
3. A quick scan of what else is on their plate (2-3 bullets, keep it tight)
4. End with one sharp question or recommendation

Keep it under 120 words. Be direct, not generic. This should feel like a real chief of staff who studied their schedule.`;

    callARIA(proactivePrompt, greetingContext, 'greeting').then(aiGreeting => {
      // Show the brief in the premium lightbox
      setTimeout(() => {
        setBriefModal({ open: true, text: aiGreeting, closing: false });
      }, 5000); // 5 seconds after login — subtle entrance

      // Also store it in the chat for reference
      setMessages(prev => {
        const updated = [...prev];
        if (updated.length > 0 && updated[0].type === 'aria') {
          updated[0] = {
            type: 'aria',
            sender: 'ARIA · Intelligence Brief',
            text: aiGreeting,
          };
        }
        return updated;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveBrief, isAuthed]);

  useEffect(() => {
    // Scroll chat to bottom after DOM updates
    const timer = setTimeout(() => {
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [messages, isTyping]);

  // Save session memory on page unload
  useEffect(() => {
    const handleUnload = () => {
      if (messages.length > 2) {
        rememberSession(messages);
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [messages]);

  const addMessage = (type, sender, text, context, id) => {
    setMessages(prev => [...prev, { type, sender, text, context, id: id || undefined }]);
    // If voice mode is on, speak ARIA replies
    if (type === 'aria' && voiceEnabled && text && !text.includes('research-live')) {
      setTimeout(() => speakAria(text), 200);
    }
    return id;
  };

  const updateMessage = (id, updates) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  };

  // Create a task from ARIA context — syncs to Google Tasks when authed
  const createTaskFromContext = (taskText, tag, tagColor, sourceKey) => {
    const newId = Date.now();
    const newTask = {
      id: newId,
      text: taskText,
      tag: tag || 'Today',
      tagColor: tagColor || 'gold',
      done: false,
      synced: false,
      sourceKey: sourceKey || null,
    };
    setTasks(prev => [newTask, ...prev]);

    // Sync to Google Tasks if authenticated
    if (isAuthed) {
      syncTaskToGoogle(newId, taskText, tag);
    }

    return newTask;
  };

  // Sync a single task to Google Tasks
  const syncTaskToGoogle = (localId, taskText, tag) => {
    createGoogleTask(taskText, `Created by ARIA · Harmonee Pulse · ${tag || 'Today'}`)
      .then((gTask) => {
        setTasks(prev => prev.map(t =>
          t.id === localId ? { ...t, id: gTask.id, listId: gTask.listId, synced: true } : t
        ));
        addMessage('aria', 'ARIA · Synced', `"<span class="em">${taskText}</span>" is now in Google Tasks — visible to your team.`);
      })
      .catch((e) => {
        console.warn('Failed to sync task to Google Tasks:', e);
        addMessage('aria', 'ARIA', `Couldn't sync "${taskText}" to Google Tasks. ${e.message || 'Check your connection and try again.'}`);
      });
  };

  // Sync all unsynced tasks to Google Tasks
  const syncAllTasks = () => {
    const unsynced = tasks.filter(t => !t.synced && !t.listId);
    if (unsynced.length === 0) {
      addMessage('aria', 'ARIA', 'All tasks are already synced to Google Tasks.');
      return;
    }
    setActivePanel('aria');
    addMessage('aria', 'ARIA · Syncing', `Pushing ${unsynced.length} task${unsynced.length > 1 ? 's' : ''} to Google Tasks...`);
    unsynced.forEach(t => {
      syncTaskToGoogle(t.id, t.text, t.tag);
    });
  };

  // Compose email drafts based on context from emails and conversations
  const composeDraft = (context) => {
    const topic = context?.topic || '';
    const topicLower = topic.toLowerCase();

    // Find the matching email thread for more context
    const matchingThread = data.gmail.priorityThreads.find(t =>
      topicLower.includes(t.from.split(' ')[0].toLowerCase()) ||
      t.subject.toLowerCase().includes(topicLower.split(' ')[0])
    );

    const recipientName = matchingThread?.from?.split('·')[0]?.trim() || topic.split(' ')[0] || 'there';
    const firstName = recipientName.split(' ')[0];

    // Draft templates based on context clues
    const drafts = {
      caroline: {
        to: 'caroline.casson@maximus.com',
        subject: 'Re: Following up — no rush',
        body: `Hi Caroline,\n\nThanks for the update — totally understand the timing. These things move at their own pace and I want to make sure it's the right fit for your team before anything else.\n\nWhenever you've had a chance to loop in your team, I'm happy to do a quick walkthrough of how the solution works in practice. We just deployed a version for the Klamath County Event Center that might be a useful reference point.\n\nNo pressure at all — just let me know when the timing feels right.\n\nBest,\nMarcio`,
      },
      fusion: {
        to: 'celeste@fusionfund.com',
        subject: 'Re: Looking forward to April 14',
        body: `Hi Celeste,\n\nLooking forward to connecting on the 14th. I wanted to share a bit of context ahead of time so we can make the most of our conversation.\n\nHarmonee is building what we call Ambient Business Intelligence — a strategic operating system that integrates real-time AI with human signal intelligence. We're already deployed with government clients (Klamath County Event Center, Oregon Tech) and have a working product generating data.\n\nI'll walk you through the ALYGN engine, our traction, and how we see the market evolving. Happy to tailor the conversation to what's most useful for you.\n\nSee you on the 14th!\n\nBest,\nMarcio`,
      },
      haley: {
        to: 'haley.huffman@klamathcounty.org',
        subject: 'Re: Harmonee & the Planning Department',
        body: `Hi Haley,\n\nGreat to hear from you! I'd love to explore how Harmonee could support the Planning Department. We've been working closely with the Event Center team and seeing strong results — I think there's a natural fit for your department as well.\n\nWould you be open to a 30-minute call this week to discuss? I can walk you through what we've built and hear about the challenges you're facing.\n\nLet me know what works for your schedule.\n\nBest,\nMarcio`,
      },
      zach: {
        to: 'zach@harmonee.ai',
        subject: 'Re: SCORI space planning one-sheet',
        body: `Hey Zach,\n\nJust reviewed the one-sheet — good work pulling this together. A few thoughts:\n\n1. The lab configuration section looks solid. Let's make sure we're accounting for the equipment placement feedback from the OIT team.\n2. On the partnership structure — I want to double-check the terms before we finalize. Can you flag any open items?\n3. Let's sync on this before sending to Hallie.\n\nThanks,\nMarcio`,
      },
    };

    // Match to a known draft template
    if (topicLower.includes('caroline') || topicLower.includes('casson')) return drafts.caroline;
    if (topicLower.includes('fusion') || topicLower.includes('celeste')) return drafts.fusion;
    if (topicLower.includes('haley') || topicLower.includes('huffman') || topicLower.includes('planning')) return drafts.haley;
    if (topicLower.includes('zach') || topicLower.includes('scori')) return drafts.zach;

    // Generic smart draft based on available context
    const emailSubject = context?.emailSubject || matchingThread?.subject || null;
    const subject = emailSubject ? `Re: ${emailSubject.replace(/^Re:\s*/i, '')}` : `Following up — ${topic}`;
    const emailTo = context?.recipientEmail || matchingThread?.email || '';
    return {
      to: emailTo,
      subject,
      body: `Hi ${firstName},\n\nFollowing up on our recent conversation about ${topic.toLowerCase()}. I wanted to check in and see if there's anything I can do to move this forward.\n\nHappy to jump on a quick call or provide any additional information that would be helpful.\n\nBest,\n${userFirstName}`,
    };
  };

  // Save draft to Gmail and/or Outlook via API
  const saveDraftToGmail = async (draft) => {
    if (!isAuthed) return null;
    try {
      let result = null;
      // Save to whichever providers are connected
      if (googleAuthed) {
        result = await createGmailDraft(draft.to, draft.subject, draft.body);
      }
      if (msAuthedState) {
        const msResult = await createMsDraft(draft.to, draft.subject, draft.body);
        result = result || msResult;
      }
      return result;
    } catch (e) {
      console.warn('Failed to save draft:', e);
      return null;
    }
  };

  // Parse user intent from chat message
  // RULE: If there's ANY ambiguity, return 'general' and let the AI handle it.
  // Only match clear, explicit action commands. Conversational language goes to AI.
  const parseUserIntent = (text) => {
    const lower = text.toLowerCase().trim();
    const words = lower.split(/\s+/);

    // Very short affirmative responses after ARIA suggestion — let AI handle with context
    if (words.length <= 4 && /^(yes|yep|yeah|sure|ok|okay|do it|go ahead|go for it|let'?s do it|let'?s go|please|absolutely)\b/i.test(lower)) {
      // Only treat as add_task if the last ARIA message had a specific task suggestion
      const lastCtx = getLastAriaContext();
      if (lastCtx?.taskText) return 'add_task';
      // Otherwise let AI interpret what "yes" means in context
      return 'general';
    }

    // Explicit task creation — must clearly say "add task" / "create task" etc.
    if (/\b(add|create|make|new)\b.*(task|todo|to-do|to do)\b/i.test(lower)) {
      return 'add_task';
    }
    // Save draft to Gmail
    if (/\bsave\b.*(draft|gmail)|\bpush\b.*(draft|gmail)/i.test(lower)) {
      return 'save_draft';
    }
    // SEND email — must explicitly say "send"
    if (/\bsend\b.*(email|message|mail|that|it)\b|\bemail\b.*\bsend\b/i.test(lower)) {
      return 'send_email';
    }
    // Draft — must explicitly say "draft" / "compose" / "write an email"
    if (/\b(draft|compose)\b|\bwrite\b.*(email|message|mail|reply)\b|\breply to\b/i.test(lower)) {
      return 'draft';
    }
    // Calendar / block time — explicit scheduling commands
    if (/\bblock\b.*(time|hour|calendar)|\bcreate\b.*(event|meeting)|\bschedule\b.*(time|block|hour)|\bbook\b.*(time|slot)/i.test(lower)) {
      return 'create_event';
    }
    // Remind me — must say "remind"
    if (/\bremind\s+me\b|\bset\b.*(reminder|alert)|\bdon'?t\b.*\bforget\b/i.test(lower)) {
      return 'remind';
    }
    // Research / deep analysis — questions about meetings, people, documents, transcripts
    if (/\b(what happened|what did|what was|recap|summarize|analyze|review|tell me about|brief me|catch me up|debrief|meeting with|call with|transcript|notes from|meeting notes)\b/i.test(lower)) {
      return 'research';
    }
    // Build / create document — wants output in Google Docs, slides, etc.
    if (/\b(build|create|make|put)\b.*(in |into |as )?(google doc|doc|slide|sheet|spreadsheet|presentation|one.?pager|memo|brief|report)\b/i.test(lower)) {
      return 'research';
    }
    // Search / find — explicit search commands
    if (/\b(search|find|look\s*(for|up)|pull\s*up|show\s*me)\b/i.test(lower)) {
      return 'search';
    }
    // Dismiss — must be a clear standalone rejection, not a substring
    if (/^(skip|dismiss|ignore|nah|never\s*mind|not now|pass)\b/i.test(lower) || /^\bno\b[.,!]?\s*$/i.test(lower) || /^no\s+(thanks|thank|don'?t|i'?m good)/i.test(lower)) {
      return 'dismiss';
    }
    // Everything else → let the real AI brain handle it
    return 'general';
  };

  // Get the last ARIA message context for reference
  const getLastAriaContext = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === 'aria' && messages[i].context) {
        return messages[i].context;
      }
    }
    // Fallback: extract topic from last ARIA message text
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === 'aria') {
        const text = messages[i].text;
        // Try to extract the topic from <span class="em">...</span>
        const emMatch = text.match(/class="em">([^<]+)</);
        if (emMatch) return { topic: emMatch[1], fullText: text };
        // Try to extract from bold
        const strongMatch = text.match(/<strong>([^<]+)</);
        if (strongMatch) return { topic: strongMatch[1], fullText: text };
        // Fallback to first sentence
        const stripped = text.replace(/<[^>]+>/g, '');
        return { topic: stripped.split('.')[0].slice(0, 60), fullText: stripped };
      }
    }
    return null;
  };

  // ──── ARIA AI Backend (with conversation memory + smart routing + persistent memory) ────
  const callARIA = async (message, context, intent) => {
    try {
      // Build conversation history from messages state
      const history = messages
        .filter(m => m.type === 'user' || m.type === 'aria')
        .slice(-15)
        .map(m => ({
          role: m.type === 'user' ? 'user' : 'aria',
          text: m.text.replace(/<[^>]+>/g, '').slice(0, 500), // strip HTML, cap length
        }));

      // Inject persistent memory context
      const memoryContext = getMemoryContext();
      const enrichedContext = [context, memoryContext].filter(Boolean).join('\n\n');

      const resp = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', // send the session cookie
        body: JSON.stringify({ message, context: enrichedContext, model: 'auto', history, intent }),
      });
      if (resp.status === 401) {
        // Session missing/expired — force a fresh unlock so the cookie gets set
        sessionStorage.removeItem('harmonee_access');
        setIsUnlocked(false);
        return `Quick reconnect needed — please re-enter your access code and I'll be right back.`;
      }
      if (!resp.ok) {
        // Surface the server's real error — a generic "warming up" message here
        // hid a five-week provider outage. The owner should see what actually broke.
        let serverMsg = '';
        try { serverMsg = (await resp.json())?.error || ''; } catch { /* non-JSON body */ }
        throw new Error(serverMsg || `API error (HTTP ${resp.status})`);
      }
      const result = await resp.json();

      // Remember this conversation exchange
      rememberConversation(message, result.reply);
      posthog.capture('aria_message', { intent, model: result.model, message_length: message.length });

      // The model sometimes emits markdown bold; the chat panel renders HTML,
      // so "**Focus now:**" was showing its literal asterisks. Convert.
      return String(result.reply || '').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    } catch (err) {
      console.error('ARIA backend error:', err);
      posthog.capture('aria_error', { intent, error: String(err?.message || err).slice(0, 200) });
      // Plain text — this string is also rendered outside chat (greeting
      // lightbox), where HTML would show up literally.
      const reason = esc(String(err?.message || 'connection failed').slice(0, 240));
      return `I couldn't reach my AI engine just now — ${reason}. Try again in a moment; if this keeps happening, the AI service needs attention (not just warming up).`;
    }
  };

  // ──── Voice input via Web Speech API ────
  const startVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice input not supported in this browser. Try Chrome, Edge, or Safari.');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(r => r[0].transcript)
        .join('');
      setChatInput(transcript);
      if (event.results[event.results.length - 1].isFinal) {
        recognition.stop();
        setTimeout(() => {
          if (transcript.trim()) {
            handleChatSubmit(transcript.trim());
          }
        }, 100);
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    posthog.capture('voice_input_started');
  };

  // ──── ARIA speaks back — uses OpenAI Nova voice (natural, warm) ────
  // Falls back to browser TTS only if the API endpoint fails.
  const ariaAudioRef = useRef(null);

  const speakAria = async (text) => {
    if (!voiceEnabled) return;
    const clean = text.replace(/<[^>]+>/g, '').replace(/[*_`#]/g, '').trim();
    if (!clean) return;

    // Stop any in-flight playback
    if (ariaAudioRef.current) {
      ariaAudioRef.current.pause();
      ariaAudioRef.current = null;
    }
    window.speechSynthesis?.cancel();

    try {
      const resp = await fetch('/api/voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          text: clean.slice(0, 1500),
          voice: localStorage.getItem('harmonee_voice_name') || 'shimmer',
          speed: parseFloat(localStorage.getItem('harmonee_voice_speed') || '0.95'),
        }),
      });

      if (!resp.ok) throw new Error(`TTS API ${resp.status}`);

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.volume = 0.95;
      ariaAudioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); ariaAudioRef.current = null; };
      audio.onerror = () => { URL.revokeObjectURL(url); ariaAudioRef.current = null; };
      await audio.play();
    } catch (err) {
      console.warn('Nova TTS failed, falling back to browser voice:', err.message);
      // Fallback to browser TTS
      if (!window.speechSynthesis) return;
      const utter = new SpeechSynthesisUtterance(clean.slice(0, 600));
      utter.rate = 1.05;
      utter.volume = 0.9;
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(v => /samantha|jenny|aria|google us english|natural/i.test(v.name)) || voices.find(v => v.lang.startsWith('en'));
      if (preferred) utter.voice = preferred;
      window.speechSynthesis.speak(utter);
    }
  };

  const toggleVoiceMode = () => {
    const newVal = !voiceEnabled;
    setVoiceEnabled(newVal);
    localStorage.setItem('harmonee_voice', newVal ? 'on' : 'off');
    if (newVal) {
      // Slight delay so the state update propagates before speakAria checks voiceEnabled
      setTimeout(() => {
        // Manually call without checking voiceEnabled since we just set it
        const text = `Hi ${userFirstName || ''}. I'm here.`;
        fetch('/api/voice', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ text, voice: localStorage.getItem('harmonee_voice_name') || 'shimmer', speed: 0.95 }),
        }).then(r => r.ok ? r.blob() : null).then(blob => {
          if (blob) {
            const audio = new Audio(URL.createObjectURL(blob));
            audio.volume = 0.95;
            ariaAudioRef.current = audio;
            audio.play().catch(() => {});
          }
        }).catch(() => {});
      }, 50);
    } else {
      if (ariaAudioRef.current) { ariaAudioRef.current.pause(); ariaAudioRef.current = null; }
      window.speechSynthesis?.cancel();
    }
    posthog.capture('voice_mode_toggled', { enabled: newVal });
  };

  // Global keyboard shortcut — Cmd/Ctrl + Shift + Space = push to talk
  // (Cmd+Space alone is reserved by macOS Spotlight)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === 'Space') {
        e.preventDefault();
        startVoiceInput();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListening, voiceEnabled]);

  const handleChatSubmit = (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : chatInput).trim();
    if (!text) return;
    addMessage('user', userFirstName, text);
    setChatInput('');
    // Reset textarea height after send
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setIsTyping(true);

    const intent = parseUserIntent(text);
    const lastContext = getLastAriaContext();

    setTimeout(() => {
      setIsTyping(false);

      if (intent === 'save_draft' && lastContext?.isDraft && lastContext?.draft) {
        if (isAuthed) {
          saveDraftToGmail(lastContext.draft).then(result => {
            if (result) {
              addMessage('aria', 'ARIA · Saved', `Draft saved to your Gmail Drafts. Open Gmail to review and send it.`);
            } else {
              addMessage('aria', 'ARIA', `Couldn't save the draft — please check your Gmail permissions and try again.`);
            }
          });
          addMessage('aria', 'ARIA · Saving', `Pushing your draft to Gmail...`);
        } else {
          addMessage('aria', 'ARIA', `You'll need to connect Google or Outlook first to save drafts. Use the login buttons in the top right.`);
        }
      } else if (intent === 'add_task' && lastContext) {
        // Actually create the task
        const taskText = lastContext.taskText || lastContext.topic || 'Follow up on ARIA suggestion';
        const sourceKey = lastContext.recipientName?.split(' ')[0]?.toLowerCase() || lastContext.topic?.split(' ')[0]?.toLowerCase() || null;
        const task = createTaskFromContext(taskText, lastContext.tag || 'Today', lastContext.tagColor || 'gold', sourceKey);
        const syncMsg = isAuthed ? ' Synced to Google Tasks — visible to your team.' : '';
        addMessage('aria', 'ARIA · Task Created', `Done. I've added "<span class="em">${esc(task.text)}</span>" to your task list with a <strong>${esc(task.tag)}</strong> deadline.${syncMsg} You can see it in the Tasks panel. What's next?`);
        // Flash the tasks tab
        setActivePanel('tasks');
        setTimeout(() => setActivePanel('aria'), 1500);
      } else if (intent === 'draft' && lastContext) {
        const draft = composeDraft(lastContext);
        const draftHtml = `
          <div class="email-draft">
            <div class="draft-header">
              <div class="draft-field"><span class="draft-label">To:</span> ${esc(draft.to) || '(fill in recipient)'}</div>
              <div class="draft-field"><span class="draft-label">Subject:</span> ${esc(draft.subject)}</div>
            </div>
            <div class="draft-body">${esc(draft.body).replace(/\n/g, '<br>')}</div>
            <div class="draft-actions">
              <button class="draft-btn draft-save" onclick="document.dispatchEvent(new CustomEvent('aria-save-draft'))">Save to Gmail Drafts</button>
              <button class="draft-btn draft-edit" onclick="document.dispatchEvent(new CustomEvent('aria-edit-draft'))">Edit & Send</button>
            </div>
          </div>`;
        addMessage('aria', 'ARIA · Draft Ready', `Here's your draft for <span class="em">${esc(lastContext.topic)}</span>:${draftHtml}`, {
          ...lastContext,
          draft,
          isDraft: true,
        });
        // Save to Gmail if authenticated
        if (isAuthed && draft.to) {
          saveDraftToGmail(draft).then(result => {
            if (result) {
              addMessage('aria', 'ARIA · Saved', `Draft saved to your Gmail Drafts. Open Gmail to review and send it.`);
            }
          });
        }
      } else if (intent === 'send_email') {
        // Actually send an email (not just draft)
        if (!isAuthed) {
          addMessage('aria', 'ARIA', `You'll need to connect Google or Outlook first to send emails.`);
        } else if (lastContext?.draft) {
          const { to, subject, body } = lastContext.draft;
          if (!to) {
            addMessage('aria', 'ARIA', `I need a recipient email address. Who should I send this to?`);
          } else {
            addMessage('aria', 'ARIA · Sending', `Sending email to <strong>${esc(to)}</strong>...`);
            const sendIt = async () => {
              try {
                let sent = false;
                if (googleAuthed) {
                  await sendGmailEmail(to, subject, body);
                  sent = true;
                }
                if (msAuthedState && !sent) {
                  await sendMsEmail(to, subject, body);
                  sent = true;
                }
                if (sent) {
                  addMessage('aria', 'ARIA · Sent', `Email sent to <strong>${esc(to)}</strong> — "${esc(subject)}". What's next?`);
                }
              } catch (err) {
                addMessage('aria', 'ARIA', `Couldn't send — ${err.message}. Want me to save it as a draft instead?`);
              }
            };
            sendIt();
          }
        } else {
          // No draft context — ask ARIA to help compose first
          addMessage('aria', 'ARIA', `I don't have a draft ready yet. Tell me who you want to email and what about, and I'll compose it for you. Then you can say "send it."`);
        }
      } else if (intent === 'create_event') {
        // Create a calendar event
        if (!isAuthed) {
          addMessage('aria', 'ARIA', `Connect Google or Outlook first so I can add to your calendar.`);
        } else {
          // Parse time from text — ask ARIA to extract details
          const contextSummary = buildContextSummary();
          const eventPrompt = `The user said: "${text}". Extract the event details and respond in EXACTLY this format (nothing else):
EVENT_TITLE: [title]
EVENT_DATE: [YYYY-MM-DD]
EVENT_START: [HH:MM in 24h format]
EVENT_DURATION: [minutes]
If any detail is missing, use reasonable defaults based on today's date and context.`;
          callARIA(eventPrompt, contextSummary, 'create_event').then(async (reply) => {
            setIsTyping(false);
            try {
              const titleMatch = reply.match(/EVENT_TITLE:\s*(.+)/);
              const dateMatch = reply.match(/EVENT_DATE:\s*(\d{4}-\d{2}-\d{2})/);
              const startMatch = reply.match(/EVENT_START:\s*(\d{2}:\d{2})/);
              const durationMatch = reply.match(/EVENT_DURATION:\s*(\d+)/);
              if (titleMatch && dateMatch && startMatch) {
                const title = titleMatch[1].trim();
                const date = dateMatch[1];
                const startTime = startMatch[1];
                const duration = parseInt(durationMatch?.[1] || '60');
                const startDT = `${date}T${startTime}:00`;
                const endDate = new Date(`${date}T${startTime}:00`);
                endDate.setMinutes(endDate.getMinutes() + duration);
                // Format end time in LOCAL wall-clock — toISOString() converts to UTC and
                // would shift the end by the timezone offset (a 60-min event became 8 hours)
                const p = (n) => String(n).padStart(2, '0');
                const endDT = `${endDate.getFullYear()}-${p(endDate.getMonth() + 1)}-${p(endDate.getDate())}T${p(endDate.getHours())}:${p(endDate.getMinutes())}:00`;
                if (googleAuthed) {
                  await createGoogleCalendarEvent(title, startDT, endDT);
                } else if (msAuthedState) {
                  await createMsCalendarEvent(title, startDT, endDT);
                }
                addMessage('aria', 'ARIA · Calendar', `Done — "<strong>${esc(title)}</strong>" blocked on your calendar for ${date} at ${startTime} (${duration} min). Anything else?`);
              } else {
                addMessage('aria', 'ARIA', reply, { topic: text, taskText: text });
              }
            } catch (err) {
              addMessage('aria', 'ARIA', `I understood the event but couldn't create it — ${err.message}. Want me to try again?`);
            }
          });
          return;
        }
      } else if (intent === 'remind') {
        // Create a reminder as a task with a due date
        const taskText = text.replace(/remind me (to )?/i, '').trim() || 'Reminder from ARIA';
        const task = createTaskFromContext(`Reminder: ${taskText}`, 'Today', 'blue');
        addMessage('aria', 'ARIA · Reminder Set', `I'll remind you: "<strong>${task.text}</strong>". Added to your tasks.${isAuthed ? ' Synced to Google Tasks.' : ''} What else?`);
      } else if (intent === 'search') {
        // Search across email, drive, tasks
        if (!isAuthed) {
          addMessage('aria', 'ARIA', `Connect Google or Outlook so I can search your workspace.`);
        } else {
          addMessage('aria', 'ARIA · Searching', `Scanning your email, drive, and tasks...`);
          // Extract content keywords instead of only stripping a leading verb —
          // "can you find docs about the pitch deck" previously went to Gmail/Drive
          // as the literal full sentence and matched nothing.
          const SEARCH_FILLER = new Set(['can', 'could', 'would', 'will', 'you', 'please', 'aria', 'hey', 'ok', 'okay', 'search', 'find', 'look', 'looking', 'show', 'pull', 'get', 'where', 'is', 'are', 'was', 'were', 'the', 'a', 'an', 'any', 'all', 'my', 'i', 'me', 'we', 'need', 'needed', 'want', 'to', 'do', 'does', 'did', 'have', 'has', 'had', 'docs', 'doc', 'documents', 'document', 'files', 'file', 'emails', 'email', 'messages', 'message', 'about', 'on', 'for', 'up', 'regarding', 'related', 'anything', 'something', 'stuff', 'things', 'thing', 'that', 'this', 'with', 'and', 'or', 'of', 'in', 'be', 'it', 'what', 'which', 'from', 'he', 'she', 'they', 'his', 'her', 'their', 'really', 'wants', 'would', 'apart', 'part']);
          const keywords = text
            .replace(/[^\w\s@.'-]/g, ' ')
            .split(/\s+/)
            .filter(w => w && !SEARCH_FILLER.has(w.toLowerCase()));
          // Proper nouns from the original text are the strongest retry signal
          const properNouns = (text.match(/(?<!^)(?<![.!?]\s)\b[A-Z][a-z]{2,}\b/g) || [])
            .filter(w => !SEARCH_FILLER.has(w.toLowerCase()));
          const query = (keywords.join(' ') || text).trim();
          const runProviderSearch = async (q) => {
            const results = { emails: [], docs: [] };
            if (googleAuthed) {
              // Search EVERY connected Google account, serially (shared gapi token).
              const searchAccounts = getAccounts();
              const targets = searchAccounts.length > 0 ? searchAccounts : [null];
              for (const acct of targets) {
                const run = async () => {
                  const [emails, docs] = await Promise.allSettled([
                    searchGmailMessages(q),
                    searchDriveFiles(q),
                  ]);
                  const short = acct ? acct.email.split('@')[1]?.split('.')[0] : null;
                  if (emails.status === 'fulfilled') results.emails.push(...emails.value.map(e => ({ ...e, account: acct?.email, accountShort: short })));
                  if (docs.status === 'fulfilled') results.docs.push(...docs.value.map(d => ({ ...d, account: acct?.email, accountShort: short })));
                };
                if (acct) await withAccountToken(acct.email, run);
                else await run();
              }
            }
            if (msAuthedState) {
              const [msEmails, msDocs] = await Promise.allSettled([
                searchMsEmails(q),
                searchMsDrive(q),
              ]);
              if (msEmails.status === 'fulfilled') results.emails.push(...msEmails.value);
              if (msDocs.status === 'fulfilled') results.docs.push(...msDocs.value);
            }
            return results;
          };
          const doSearch = async () => {
            try {
              let usedQuery = query;
              let results = await runProviderSearch(query);
              // No hits on the multi-word query? Retry on the most distinctive term.
              if (results.emails.length === 0 && results.docs.length === 0 && keywords.length > 1) {
                const retryTerm = properNouns[0] || keywords.slice().sort((a, b) => b.length - a.length)[0];
                if (retryTerm && retryTerm.toLowerCase() !== query.toLowerCase()) {
                  results = await runProviderSearch(retryTerm);
                  if (results.emails.length > 0 || results.docs.length > 0) usedQuery = retryTerm;
                }
              }
              // Also search local tasks
              const matchedTasks = tasks.filter(t => t.text.toLowerCase().includes(usedQuery.toLowerCase()));

              let responseHtml = `Here's what I found for "<strong>${esc(usedQuery)}</strong>":<br><br>`;
              if (results.emails.length > 0) {
                responseHtml += `<strong>Emails (${results.emails.length}):</strong><br>`;
                results.emails.slice(0, 4).forEach(e => {
                  responseHtml += `• <span class="em">${esc(e.subject)}</span> — from ${esc(e.from?.split('<')[0]?.trim() || 'Unknown')}<br>`;
                });
                responseHtml += '<br>';
              }
              if (results.docs.length > 0) {
                responseHtml += `<strong>Documents (${results.docs.length}):</strong><br>`;
                results.docs.slice(0, 4).forEach(d => {
                  // Only allow http(s) Drive links — block javascript:/data: URLs
                  const safeUrl = /^https?:\/\//i.test(d.url || '') ? d.url : '#';
                  responseHtml += `• <a href="${esc(safeUrl)}" target="_blank" rel="noopener noreferrer" style="color:var(--gold-ink)">${esc(d.title)}</a> — updated by ${esc(d.modifiedBy)}<br>`;
                });
                responseHtml += '<br>';
              }
              if (matchedTasks.length > 0) {
                responseHtml += `<strong>Tasks (${matchedTasks.length}):</strong><br>`;
                matchedTasks.slice(0, 3).forEach(t => {
                  responseHtml += `• ${t.done ? '✓' : '○'} ${t.text}<br>`;
                });
                responseHtml += '<br>';
              }
              if (results.emails.length === 0 && results.docs.length === 0 && matchedTasks.length === 0) {
                responseHtml = `No results found for "<strong>${esc(usedQuery)}</strong>". Try a different search term, or ask me something specific like "find emails from Celeste" or "find the pitch deck."`;
              } else {
                responseHtml += `Want me to do anything with these results?`;
              }
              addMessage('aria', 'ARIA · Search Results', responseHtml, { topic: usedQuery, taskText: usedQuery });
            } catch (err) {
              addMessage('aria', 'ARIA', `Search hit an error — ${err.message}. Try a simpler query?`);
            }
          };
          doSearch();
        }
      } else if (intent === 'research') {
        // ──── Agentic Deep Research with Live Progress ────
        if (!isAuthed) {
          addMessage('aria', 'ARIA', `Connect Google or Outlook so I can research across your workspace.`);
        } else {
          const progressId = 'research-' + Date.now();
          const stepLog = [];

          const renderSteps = () => {
            return stepLog.map(s => {
              const icons = {
                search: '🔍', found: '✓', detail: '┃', empty: '○',
                warn: '⚠', retry: '↻', read: '📖', done: '✓', analyze: '🧠',
              };
              const classes = {
                search: 'research-step-active', found: 'research-step-found',
                detail: 'research-step-detail', empty: 'research-step-empty',
                warn: 'research-step-warn', retry: 'research-step-retry',
                read: 'research-step-active', done: 'research-step-done',
                analyze: 'research-step-analyze',
              };
              return `<div class="research-step-line ${classes[s.type] || ''}"><span class="research-icon">${icons[s.type] || '→'}</span> ${s.text}</div>`;
            }).join('');
          };

          addMessage('aria', 'ARIA · Researching', `<div class="research-live">${renderSteps()}<div class="research-pulse">Starting deep research…</div></div>`, null, progressId);

          const onStep = (step) => {
            stepLog.push(step);
            const pulse = step.type === 'analyze'
              ? '<div class="research-pulse research-pulse-gold">Analyzing with ARIA…</div>'
              : '<div class="research-pulse">Working…</div>';
            updateMessage(progressId, {
              text: `<div class="research-live">${renderSteps()}${pulse}</div>`,
            });
          };

          const doResearch = async () => {
            try {
              posthog.capture('deep_research_started', { query: text.slice(0, 100) });
              const research = await deepResearch(text, onStep);

              // Finalize progress — remove pulse
              updateMessage(progressId, {
                sender: 'ARIA · Research Complete',
                text: `<div class="research-live research-complete">${renderSteps()}</div>`,
              });

              if (research.contents.length === 0) {
                addMessage('aria', 'ARIA', `I searched Gmail and Drive but couldn't find content matching "${text}". Try being more specific — mention a person's name, project, or date.`);
                setIsTyping(false);
                return;
              }

              // Build context from fetched content
              let researchContext = `The user asked: "${text}"\n\nHere is what I found in their workspace:\n\n`;
              for (const item of research.contents) {
                if (item.type === 'email') {
                  researchContext += `── EMAIL ──\nFrom: ${item.from}\nTo: ${item.to}\nSubject: ${item.subject}\nDate: ${item.date}\n\n${item.body}\n\n`;
                } else if (item.type === 'document') {
                  researchContext += `── DOCUMENT: ${item.title} ──\nLast modified: ${item.modified} by ${item.modifiedBy}\nURL: ${item.url}\n\n${item.body}\n\n`;
                }
              }
              if (research.emails.length > 2) {
                researchContext += `\nAdditional emails found:\n`;
                research.emails.slice(2).forEach(e => { researchContext += `• ${e.subject} — from ${e.from}\n`; });
              }
              if (research.docs.length > 2) {
                researchContext += `\nAdditional documents found:\n`;
                research.docs.slice(2).forEach(d => { researchContext += `• ${d.title} — by ${d.modifiedBy}\n`; });
              }

              // Send to AI for analysis
              const analysisPrompt = `Based on the workspace data I found, give the user a thorough, strategic analysis.

Key guidelines:
- Lead with the most important insight or answer to their question
- Use specific names, dates, numbers, and quotes from the source material
- Structure with clear sections if the content warrants it
- End with 1-2 actionable next steps
- If they asked to build a document (one-pager, memo, brief), draft that document structure
- Be specific — reference actual content from the documents/emails, don't generalize

${researchContext}`;

              const contextSummary = buildContextSummary();
              const reply = await callARIA(analysisPrompt, contextSummary + '\n\n' + researchContext.slice(0, 4000), 'research');

              // Build source citations
              let sourcesHtml = '<div class="research-sources">';
              sourcesHtml += '<div class="research-sources-title">Sources</div>';
              for (const item of research.contents) {
                if (item.type === 'email') {
                  sourcesHtml += `<div class="research-source">📧 <strong>${esc(item.subject)}</strong> — ${esc(item.from?.split('<')[0]?.trim())} · ${esc(item.date?.split(',')[0] || '')}</div>`;
                } else if (item.type === 'document') {
                  const safeUrl = /^https?:\/\//i.test(item.url || '') ? item.url : '#';
                  sourcesHtml += `<div class="research-source">📄 <a href="${esc(safeUrl)}" target="_blank" rel="noopener noreferrer" style="color:var(--gold-ink)">${esc(item.title)}</a> — ${esc(item.modifiedBy)}</div>`;
                }
              }
              sourcesHtml += '</div>';

              setIsTyping(false);
              addMessage('aria', 'ARIA · Deep Research', reply + sourcesHtml, {
                topic: text, taskText: text, researchData: research,
              });
            } catch (err) {
              setIsTyping(false);
              addMessage('aria', 'ARIA', `Research hit an error — ${err.message}. Want me to try a simpler search?`);
            }
          };
          doResearch();
          return;
        }
      } else if (intent === 'schedule') {
        const taskText = lastContext ? `Schedule time for: ${lastContext.topic}` : `Block time: ${text}`;
        createTaskFromContext(taskText, 'Today', 'blue');
        const schedSync = isAuthed ? ' Task synced to Google Tasks.' : '';
        addMessage('aria', 'ARIA · Calendar', `I've added a scheduling task for this.${schedSync} What else?`);
      } else if (intent === 'dismiss') {
        addMessage('aria', 'ARIA', `Got it, moving on. What else can I help with today?`);
      } else {
        // Real AI — call ARIA backend
        const contextSummary = buildContextSummary();
        callARIA(text, contextSummary, intent).then(reply => {
          setIsTyping(false);
          addMessage('aria', 'ARIA', reply, { topic: text, taskText: text });
        });
        return; // skip the setIsTyping(false) below — async handles it
      }
    }, 1400);
  };

  // Build context summary for AI calls
  const buildContextSummary = () => {
    const contactContext = contactGraph.length > 0
      ? `Key contacts: ${contactGraph.slice(0, 8).map(c => {
          const days = c.daysSinceContact;
          const status = days === 0 ? 'today' : days === 1 ? 'yesterday' : days != null ? `${days}d ago` : 'unknown';
          return `${c.name} (${c.urgency} priority, last: ${status}, via ${c.channels.join('+')}${c.strength > 50 ? ', strong relationship' : ''})`;
        }).join('; ')}`
      : '';

    return [
      `User: ${userFirstName || 'Founder'}`,
      `Today: ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`,
      `Meetings today: ${data.calendar?.length || 0}`,
      data.calendar?.length > 0 ? `Meetings: ${data.calendar.slice(0, 5).map(m => `${m.summary} (${m.start?.split('T')[1]?.slice(0,5) || ''})`).join(', ')}` : '',
      `Pending tasks: ${tasks.filter(t => !t.done).length}`,
      tasks.filter(t => !t.done).length > 0 ? `Top tasks: ${tasks.filter(t => !t.done).slice(0, 5).map(t => t.text).join(', ')}` : '',
      `Active projects: ${sidebarProjects.map(p => p.name).join(', ') || 'None detected'}`,
      `Business health: ${healthScore?.overall || 'N/A'}/100`,
      data.calendar?.length > 0 ? `Next meeting: ${data.calendar[0]?.summary}` : '',
      `Recent emails from: ${(data.gmail?.priorityThreads || []).slice(0, 5).map(e => e.from.split('<')[0].trim()).join(', ') || 'none'}`,
      contactContext,
      `Connected: ${[googleAuthed ? 'Google' : '', msAuthedState ? 'Outlook' : ''].filter(Boolean).join(' + ')}`,
    ].filter(Boolean).join('\n');
  };

  const handleActionClick = (key) => {
    setActivePanel('aria');
    setIsTyping(true);

    // Navigate-only actions
    if (key === 'meeting_prep') {
      setTimeout(() => { setIsTyping(false); setActiveSidebar('Meeting Prep'); }, 600);
      return;
    }
    if (key === 'fusion' || key === 'investor_prep') {
      setTimeout(() => { setIsTyping(false); setActiveSidebar('Investor Prep'); }, 600);
      return;
    }
    if (key === 'review_doc' || key === 'docs') {
      setTimeout(() => { setIsTyping(false); setActiveSidebar('Documents'); }, 600);
      return;
    }
    if (key === 'tasks') {
      setTimeout(() => { setIsTyping(false); setActivePanel('tasks'); }, 600);
      return;
    }

    // Dynamic: find matching email by first name or org keyword
    const allEmails = data.gmail?.priorityThreads || [];
    const matchedEmail = allEmails.find(e => {
      const fromLower = e.from.toLowerCase();
      return fromLower.split(' ')[0] === key || fromLower.includes(key);
    });

    // Dynamic: find matching calendar event
    const matchedMeeting = (data.calendar || []).find(m =>
      m.summary.toLowerCase().includes(key)
    );

    // Dynamic: find matching drive doc
    const matchedDoc = (data.drive || []).find(d =>
      d.title.toLowerCase().includes(key)
    );

    let actionContext;
    let autoDraft = false;

    if (matchedEmail) {
      // Email action — compose a draft reply
      const fromName = matchedEmail.from.split('·')[0].trim();
      actionContext = {
        topic: `${fromName} follow-up`,
        taskText: `Reply to ${fromName} — re: ${matchedEmail.subject}`,
        tag: 'Today',
        tagColor: 'gold',
        recipientName: fromName,
        recipientEmail: matchedEmail.email || null,
        emailSubject: matchedEmail.subject,
      };
      autoDraft = true;
    } else if (matchedMeeting) {
      // Meeting prep action
      actionContext = {
        topic: `${matchedMeeting.summary} prep`,
        taskText: `Prep for ${matchedMeeting.summary}`,
        tag: 'Today',
        tagColor: 'blue',
      };
      const text = `<strong>${esc(matchedMeeting.summary)}</strong> is at ${esc(matchedMeeting.start)}${matchedMeeting.sub ? ` — ${esc(matchedMeeting.sub)}` : ''}. Want me to add prep to your tasks or draft a message to attendees?`;
      setTimeout(() => {
        setIsTyping(false);
        addMessage('aria', 'ARIA · Review', text, actionContext);
      }, 1800);
      return;
    } else if (matchedDoc) {
      // Document review action
      setTimeout(() => {
        setIsTyping(false);
        setActiveSidebar('Documents');
        addMessage('aria', 'ARIA · Review', `Opening <strong>${esc(matchedDoc.title)}</strong> — last modified ${esc(matchedDoc.modified)} by ${esc(matchedDoc.modifiedBy) || 'a collaborator'}. Want me to summarize the key changes or add a review task?`, {
          topic: `Review ${matchedDoc.title}`,
          taskText: `Review: ${matchedDoc.title}`,
          tag: 'Today',
          tagColor: 'gold',
        });
      }, 1200);
      return;
    } else {
      // Fallback: treat unknown key as a draft request with best-guess context
      actionContext = {
        topic: `${key} follow-up`,
        taskText: `Follow up: ${key}`,
        tag: 'Today',
        tagColor: 'gold',
      };
      autoDraft = true;
    }

    setTimeout(() => {
      setIsTyping(false);

      if (autoDraft) {
        const draft = composeDraft(actionContext);
        const draftHtml = `
          <div class="email-draft">
            <div class="draft-header">
              <div class="draft-field"><span class="draft-label">To:</span> ${esc(draft.to) || '(fill in recipient)'}</div>
              <div class="draft-field"><span class="draft-label">Subject:</span> ${esc(draft.subject)}</div>
            </div>
            <div class="draft-body">${esc(draft.body).replace(/\n/g, '<br>')}</div>
          </div>
          <div style="margin-top:8px; opacity:0.7; font-size:12px;">Say <strong>"save to drafts"</strong> to push to Gmail, or <strong>"edit"</strong> to modify.</div>`;
        addMessage('aria', 'ARIA · Draft Ready', draftHtml, {
          ...actionContext,
          draft,
          isDraft: true,
        });
        if (isAuthed && draft.to) {
          saveDraftToGmail(draft).then(result => {
            if (result) {
              addMessage('aria', 'ARIA · Saved', `Draft saved to your Gmail Drafts. Open Gmail to review and send.`);
            }
          });
        }
      } else {
        addMessage('aria', 'ARIA · Review', 'Ready to help with this. Want me to draft a message or add it as a task?', actionContext);
      }
    }, 1800);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSubmit();
    }
  };

  const toggleTask = async (id) => {
    const task = tasks.find(t => t.id === id);
    const newDone = !task?.done;
    setTasks(prev => prev.map(t => t.id === id ? { ...t, done: newDone } : t));
    posthog.capture('task_toggled', { task: task?.text?.slice(0, 60), completed: newDone });
    if (isAuthed && task?.listId) {
      try {
        await toggleTaskComplete(task.listId, task.id, newDone);
      } catch (e) {
        console.warn('Failed to sync task:', e);
      }
    }
  };

  // Suggestion actions
  const handleDismissSuggestion = (index) => {
    posthog.capture('suggestion_dismissed');
    setSuggestions(prev => prev.filter((_, i) => i !== index));
  };

  const handleReviewSuggestion = (sug) => {
    posthog.capture('suggestion_reviewed', { type: sug.type, title: sug.title });
    setActivePanel('aria');
    setIsTyping(true);
    const reviewResponses = {
      'alert': `I've analyzed the situation: <span class="em">${sug.title}</span>. ${sug.desc} I can draft a response right now, or add it to your tasks for later. What would you prefer?`,
      'pattern': `Here's what I found on <span class="em">${sug.title}</span>: ${sug.desc} Want me to start building the prep materials, or should I schedule time on your calendar for this?`,
      'automate': `Regarding the automation: <span class="em">${sug.title}</span>. ${sug.desc} I can activate this right now — it'll run in the background using your connected Google APIs. Enable it?`,
      'draft': `I've prepared a draft for: <span class="em">${sug.title}</span>. ${sug.desc} Want me to open the draft editor, or should I save it directly to Gmail Drafts for your review?`,
    };
    setTimeout(() => {
      setIsTyping(false);
      addMessage('aria', 'ARIA · Review', reviewResponses[sug.type] || `Reviewing: ${sug.title}. ${sug.desc}`, {
        topic: sug.title,
        taskText: sug.title,
        tag: sug.type === 'alert' ? 'Urgent' : 'Today',
        tagColor: sug.type === 'alert' ? 'red' : 'gold',
      });
    }, 1500);
  };

  // Automation toggle
  const toggleAutomation = (index) => {
    setAutomations(prev => prev.map((auto, i) =>
      i === index ? { ...auto, active: !auto.active } : auto
    ));
  };

  // Sidebar click handler
  // ──── Email me my brief now ────
  const [briefEmailStatus, setBriefEmailStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const handleEmailBrief = async () => {
    const recipient = googleUser?.email;
    if (!recipient) {
      setBriefEmailStatus('error');
      setTimeout(() => setBriefEmailStatus(null), 3000);
      return;
    }
    setBriefEmailStatus('sending');
    try {
      const briefData = {
        calendar: data.calendar || [],
        gmail: data.gmail || {},
        drive: data.drive || [],
        deskActions: computeMarcioActions(),
        ambientSignals: computeAmbientSignals(),
        liveBrief: liveBrief?.text || null,
      };
      const resp = await fetch('/api/email-brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: recipient,
          firstName: userFirstName,
          briefData,
        }),
      });
      if (!resp.ok) throw new Error('Send failed');
      setBriefEmailStatus('sent');
      posthog.capture('brief_emailed', { to: recipient });
      setTimeout(() => setBriefEmailStatus(null), 4000);
    } catch (err) {
      console.error('Brief email failed:', err);
      setBriefEmailStatus('error');
      setTimeout(() => setBriefEmailStatus(null), 4000);
    }
  };

  const handleSidebarClick = (name) => {
    setActiveSidebar(name);
    posthog.capture('sidebar_clicked', { section: name });
    if (name === 'ARIA Chat') {
      setActivePanel('aria');
    }
  };

  // Project click — OTV opens its own client dashboard
  const handleProjectClick = (name) => {
    if (name === 'On The Verge') {
      setActiveNav('Intelligence');
      setActiveSidebar('OTV Clients');
      return;
    }
    setActiveProject(prev => prev === name ? null : name);
  };

  // Tag drive docs with smart project names if they only have generic labels
  const PROJECT_KW_MAP = [
    { keywords: ['harmonee', 'pulse', 'aria', 'alygn', 'board meeting'], name: 'Harmonee AI' },
    { keywords: ['otv', 'on the verge', 'verge'], name: 'On The Verge' },
    { keywords: ['scori', 'oregon tech', 'subaward'], name: 'SCORI' },
    { keywords: ['k lounge', 'klounge', '4fc', 'four corners', 'turgesen', 'lease'], name: 'K Lounge / 4FC' },
    { keywords: ['kwua', 'klamath water'], name: 'KWUA' },
    { keywords: ['kce', 'klamath county', 'event center', 'fair grounds'], name: 'Klamath County' },
    { keywords: ['fusion', 'investor', 'seed', 'pitch'], name: 'Investor Pipeline' },
  ];
  const taggedDrive = (data.drive || []).map(doc => {
    // If doc already has a meaningful project tag (not generic), keep it
    if (doc.project && doc.project !== 'Google Drive' && doc.project !== 'OneDrive') return doc;
    // Otherwise, scan title for project keywords
    const titleLower = (doc.title || '').toLowerCase();
    const match = PROJECT_KW_MAP.find(p => p.keywords.some(kw => titleLower.includes(kw)));
    return { ...doc, project: match ? match.name : (doc.project || 'Other') };
  });

  // Filter drive docs by project
  const filteredDrive = activeProject
    ? taggedDrive.filter(doc => doc.project === activeProject)
    : taggedDrive;

  const navTabs = ['Intelligence', 'Command Center', 'Workflows', 'Deployments', 'Vault'];
  const sidebarWorkspace = [
    { name: 'Morning Brief', badge: data.calendar.length, badgeType: 'gold', icon: 'morning' },
    { name: 'Suggestions', badge: suggestions.length, badgeType: suggestions.length > 0 ? 'gold' : 'dim', icon: 'bolt' },
    { name: 'Tasks', badge: tasks.filter(t => !t.done).length, badgeType: 'red', icon: 'wrapup' },
    { name: 'Automations', badge: automations.filter(a => a.active).length, badgeType: 'green', icon: 'gear' },
    { name: 'Meeting Prep', badge: data.calendar.length, badgeType: 'blue', icon: 'calendar' },
    { name: 'Documents', badge: (data.drive || []).length, badgeType: 'gold', icon: 'doc' },
    { name: 'Wrap-Up', badge: tasks.filter(t => !t.done).length, badgeType: 'red', icon: 'wrapup' },
  ];
  // ──── Smart Project Detection — scans all data sources for real project names ────
  const sidebarProjects = (() => {
    const fallback = [
      { name: 'Harmonee AI', color: '#4CAF7C' },
      { name: 'On The Verge', color: '#4a9eff' },
      { name: 'SCORI Hub', color: '#C9A84C' },
      { name: 'K Lounge / 4FC', color: '#8b7cf6' },
    ];

    if (!isAuthed) return fallback;

    // Known project keywords → display name mapping
    // ARIA scans all data for these patterns
    const PROJECT_PATTERNS = [
      { keywords: ['harmonee', 'pulse', 'aria', 'alygn'], name: 'Harmonee AI', color: '#4CAF7C' },
      { keywords: ['otv', 'on the verge', 'verge'], name: 'On The Verge', color: '#4a9eff' },
      { keywords: ['scori', 'oregon tech', 'subaward'], name: 'SCORI', color: '#C9A84C' },
      { keywords: ['k lounge', 'klounge', '4fc', 'four corners', 'turgesen', 'lease'], name: 'K Lounge / 4FC', color: '#8b7cf6' },
      { keywords: ['kwua', 'klamath water'], name: 'KWUA', color: '#E57373' },
      { keywords: ['kce', 'klamath county', 'event center', 'fair grounds', 'fairgrounds'], name: 'Klamath County', color: '#FF8A65' },
      { keywords: ['fusion fund', 'celeste', 'investor', 'seed'], name: 'Investor Pipeline', color: '#4DB6AC' },
    ];

    // Collect all text signals from every data source
    const allText = [];

    // Drive doc titles
    (data.drive || []).forEach(d => {
      if (d.title) allText.push(d.title.toLowerCase());
    });

    // Email subjects and senders
    (data.gmail?.priorityThreads || []).forEach(e => {
      if (e.subject) allText.push(e.subject.toLowerCase());
      if (e.from) allText.push(e.from.toLowerCase());
    });

    // Calendar event names and organizers
    (data.calendar || []).forEach(c => {
      if (c.summary) allText.push(c.summary.toLowerCase());
      if (c.organizer) allText.push(c.organizer.toLowerCase());
      if (c.sub) allText.push(c.sub.toLowerCase());
    });

    // Task text
    tasks.forEach(t => {
      if (t.text) allText.push(t.text.toLowerCase());
    });

    const combinedText = allText.join(' ');

    // Score each project by how many keyword matches it has
    const detected = PROJECT_PATTERNS
      .map(p => ({
        ...p,
        score: p.keywords.reduce((acc, kw) => {
          const regex = new RegExp(kw, 'gi');
          const matches = (combinedText.match(regex) || []).length;
          return acc + matches;
        }, 0),
      }))
      .filter(p => p.score > 0)
      .sort((a, b) => b.score - a.score);

    if (detected.length >= 2) {
      return detected.map(p => ({ name: p.name, color: p.color }));
    }

    return fallback;
  })();

  // ──── Real-Data Ambient Signals ────
  // Computes patterns from actual connected Gmail/Calendar/Drive data when available.
  // Falls back to demo content when not authed. Fires push notifications for new urgent signals.
  const computeAmbientSignals = useCallback(() => {
    const signals = [];
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    // ── Signal: Email silence (real data) ──
    const threads = data.gmail?.priorityThreads || [];
    const dated = threads.filter(t => t.date).map(t => ({ ...t, ageMs: now - new Date(t.date).getTime() }));
    const silent = dated.filter(t => t.ageMs > 2 * oneDay && (t.priority === 'gold' || t.priority === 'red')).sort((a, b) => b.ageMs - a.ageMs)[0];
    if (silent) {
      const days = Math.round(silent.ageMs / oneDay);
      const senderName = silent.from.split('·')[0].split('<')[0].trim();
      const sig = {
        kind: 'silence',
        title: `${senderName} thread quiet ${days} days`,
        detail: `Last message was ${silent.subject.slice(0, 80)}. Re-engagement window narrowing.`,
        sources: ['Gmail'],
        action: null,
        notifKey: `silence-${silent.email || senderName}-${days}`,
      };
      signals.push(sig);
      if (days >= 3) fireNotification(`ARIA noticed: ${senderName} thread quiet ${days} days`, silent.subject.slice(0, 100), sig.notifKey);
    } else if (!isAuthed && threads.length > 0) {
      // Fallback demo signal
      signals.push({
        kind: 'silence',
        title: 'Caroline thread quiet 3 days',
        detail: 'Your usual response window with her is 18 hours. Re-engagement window narrowing.',
        sources: ['Gmail', 'LCM history'],
        action: 'caroline',
      });
    }

    // ── Signal: Drive activity spike (real data) ──
    const drive = data.drive || [];
    if (drive.length >= 3) {
      const recent = drive.filter(d => {
        const t = d.modifiedTime ? new Date(d.modifiedTime).getTime() : 0;
        return t > now - 7 * oneDay;
      });
      if (recent.length >= 2) {
        signals.push({
          kind: 'momentum',
          title: `${recent.length} documents touched this week`,
          detail: recent.length >= 5 ? 'Decision velocity accelerating. Cross-domain coordination active.' : `${recent.slice(0, 2).map(d => d.name).join(' · ')}. Worth a sync read.`,
          sources: ['Drive', 'Calendar'],
          action: null,
        });
      }
    } else if (!isAuthed) {
      signals.push({
        kind: 'momentum',
        title: 'Klamath docs activity up this week',
        detail: '7 documents touched across Planning + LDC. Decision velocity increasing.',
        sources: ['Drive', 'Calendar'],
        action: null,
      });
    }

    // ── Signal: Calendar concentration (real data) ──
    const cal = data.calendar || [];
    const today = new Date();
    // The Calendar API already scopes results to today, so a live event with a valid
    // startISO counts. (e.start is a display string like "11:00 AM" — never date-parse it.)
    const todayMeetings = cal.filter(e => {
      if (!e.startISO) return false;
      const t = new Date(e.startISO);
      return !isNaN(t) && t.toDateString() === today.toDateString();
    });
    if (todayMeetings.length >= 4) {
      const sig = {
        kind: 'drift',
        title: 'Deep work compression detected',
        detail: `${todayMeetings.length} meetings today. Context switching at this rate degrades decision quality ~40%. Consider blocking AM tomorrow.`,
        sources: ['Calendar', 'ALYGN'],
        action: null,
        notifKey: `compression-${today.toDateString()}`,
      };
      signals.push(sig);
      if (todayMeetings.length >= 5) fireNotification('ARIA noticed: heavy meeting day', `${todayMeetings.length} meetings — protect your deep work window.`, sig.notifKey);
    } else if (!isAuthed) {
      signals.push({
        kind: 'drift',
        title: 'Deep work compression detected',
        detail: '4 meetings today vs. your 7-day avg of 2.3. Pattern repeats Tuesdays — consider blocking AM.',
        sources: ['Calendar', 'ALYGN'],
        action: null,
      });
    }

    // ── Signal: Cross-silo momentum (real data) ──
    if (threads.length >= 2 && cal.length >= 1 && drive.length >= 1) {
      // Find a name that appears across multiple sources
      const senderNames = threads.map(t => t.from.split('·')[0].split('<')[0].trim().toLowerCase());
      const attendeeNames = cal.flatMap(e => (e.attendees || []).map(a => (a.name || a.email || '').toLowerCase()));
      const docNames = drive.map(d => (d.name || '').toLowerCase());
      const matched = senderNames.find(s => s && s.length > 4 && (attendeeNames.some(a => a.includes(s.split(' ')[0])) || docNames.some(n => n.includes(s.split(' ')[0]))));
      if (matched) {
        const proper = matched.replace(/\b\w/g, c => c.toUpperCase());
        signals.push({
          kind: 'trajectory',
          title: `${proper} appearing across silos`,
          detail: 'Email + calendar + doc activity all converging on this contact. Worth a unified review.',
          sources: ['Gmail', 'Calendar', 'Drive'],
          action: null,
        });
      }
    } else if (!isAuthed && data.stats?.investorPipeline) {
      signals.push({
        kind: 'trajectory',
        title: 'Fusion Fund momentum building',
        detail: 'Email + calendar + doc activity all converging. Statistically similar to your 2 prior conversion patterns.',
        sources: ['Gmail', 'Calendar', 'Drive', 'Pipeline'],
        action: 'fusion',
      });
    }

    return signals;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isAuthed, notifPermission]);

  // ══════════════════════════════════════════════════════════════════
  // APPROVAL QUEUE — Human in the Loop
  // ══════════════════════════════════════════════════════════════════
  // ARIA prepares the work. Marcio approves. The queue is where they meet.
  // Each item represents an action ARIA has staged but won't execute without sign-off.
  // ══════════════════════════════════════════════════════════════════

  // Approved/rejected/snoozed approval IDs persist in localStorage so the queue learns
  const [approvalState, setApprovalState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('harmonee_approvals') || '{}'); }
    catch { return {}; }
  });
  const [executingApprovalId, setExecutingApprovalId] = useState(null);
  // Cache AI-written drafts by approval ID so we don't refetch on every render
  const [aiDrafts, setAiDrafts] = useState({});
  // Track approval IDs we've already requested a draft for
  const draftRequestsRef = useRef(new Set());

  // Fetch an AI-written draft for an email-reply approval item
  const fetchAIDraft = useCallback(async (item) => {
    if (item.type !== 'email_reply') return;
    if (aiDrafts[item.id]) return;
    if (draftRequestsRef.current.has(item.id)) return;
    draftRequestsRef.current.add(item.id);

    try {
      // Read the sender's ACTUAL last message so ARIA drafts from content,
      // not just "N days waiting · subject". Routed via the owning account.
      let threadBody = null;
      if (item.meta?.lastMessageId) {
        try {
          threadBody = await withAccountToken(item.meta.account, () => fetchGmailMessageBody(item.meta.lastMessageId));
        } catch (e) {
          console.warn('Could not fetch thread body, drafting from summary:', e);
        }
      }

      const resp = await fetch('/api/draft-reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderName: item.title.replace(/^(Send reply|Reply) to /, ''),
          senderEmail: item.meta?.email,
          subject: item.meta?.subject,
          threadSummary: item.context,
          threadBody: threadBody ? String(threadBody).slice(0, 5000) : undefined,
          daysWaiting: parseInt((item.context.match(/(\d+)\s+day/) || [])[1] || '1', 10),
          userFirstName,
        }),
      });
      if (!resp.ok) return;
      const { draft } = await resp.json();
      if (draft) {
        setAiDrafts(prev => ({ ...prev, [item.id]: draft }));
      }
    } catch (e) {
      console.warn('AI draft fetch failed:', e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDrafts, userFirstName]);

  // Auto-fetch AI drafts for any new email-reply items in the queue
  useEffect(() => {
    if (!isAuthed) return;
    const queue = computeApprovalQueue();
    queue.forEach(item => {
      if (item.type === 'email_reply' && !aiDrafts[item.id]) {
        fetchAIDraft(item);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isAuthed]);

  const persistApprovalState = (next) => {
    setApprovalState(next);
    localStorage.setItem('harmonee_approvals', JSON.stringify(next));
  };

  // Build the list of pending approvals from real signals + drafts
  const computeApprovalQueue = useCallback(() => {
    const queue = [];
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    // ── 1. Reply drafts for unanswered emails ──
    const unanswered = data.gmail?.unansweredThreads || [];
    unanswered.slice(0, 3).forEach((t) => {
      if (!t.date) return;
      const id = `reply-${t.threadId || t.email}`;
      // Drop once acted on — approved OR rejected — so approving doesn't leave a re-approvable card (duplicate drafts)
      if (approvalState[id]?.status === 'approved' || approvalState[id]?.status === 'rejected') return;
      if (approvalState[id]?.snoozeUntil && approvalState[id].snoozeUntil > now) return;
      const days = Math.max(1, Math.round((now - new Date(t.date).getTime()) / oneDay));
      queue.push({
        id,
        type: 'email_reply',
        icon: 'mail',
        title: `Reply to ${t.from}`,
        context: `${days} day${days === 1 ? '' : 's'} waiting · "${(t.subject || '').slice(0, 80)}"`,
        preview: `Hi ${t.from?.split(' ')[0] || 'there'},\n\nThanks for your patience — I wanted to make sure I gave this the thought it deserved before responding. ${t.subject ? `On "${t.subject.slice(0, 60)}":` : ''} happy to set up time this week to work through it. Does Thursday or Friday afternoon work?\n\n— Marcio`,
        meta: { threadId: t.threadId, email: t.email, subject: t.subject, account: t.account, lastMessageId: t.lastMessageId, rfcMessageId: t.rfcMessageId },
        urgency: days >= 5 ? 'high' : 'med',
        source: t.accountShort ? `Gmail · ${t.accountShort}` : 'Gmail',
        sourceDetail: `Triggered by ${days}d unanswered thread${t.account ? ` in ${t.account}` : ''}`,
      });
    });

    // ── 2. Calendar prep blocks for today's meetings ──
    const cal = data.calendar || [];
    const today = new Date();
    cal.slice(0, 3).forEach((e) => {
      if (!e.startISO || e.isAllDay) return;
      const eventTime = new Date(e.startISO);
      if (isNaN(eventTime) || eventTime.toDateString() !== today.toDateString()) return;
      const hoursUntil = (eventTime.getTime() - now) / (60 * 60 * 1000);
      if (hoursUntil < 0.5 || hoursUntil > 8) return;
      const title = e.summary || 'Untitled meeting';
      const id = `prep-${e.id || title.slice(0, 30)}`;
      if (approvalState[id]?.status === 'approved') return;
      if (approvalState[id]?.status === 'rejected') return;
      if (approvalState[id]?.snoozeUntil && approvalState[id].snoozeUntil > now) return;
      queue.push({
        id,
        type: 'calendar_prep',
        icon: 'calendar',
        title: `Block prep time for ${title}`,
        context: `Starts in ${Math.round(hoursUntil)}h · ${(e.attendees || []).length} attendees`,
        preview: `Add a 30-minute prep block to your calendar at ${new Date(eventTime.getTime() - 30 * 60 * 1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}.\n\nI'll attach: the prior thread history, related Drive docs, attendee context, and a one-page brief on what to bring to the room.`,
        meta: { eventTitle: title, eventTime: e.startISO },
        urgency: hoursUntil < 2 ? 'high' : 'med',
        source: 'Calendar',
        sourceDetail: `Triggered by upcoming meeting`,
      });
    });

    // ── 3. Doc creation for active deals ──
    // Demo-only: snapshot's investorPipeline is mock content. Real pipeline items
    // come from live signals once the brain is wired to the dashboard.
    if (!isAuthed && data.stats?.investorPipeline && (data.stats.investorPipeline || '').toLowerCase().includes('fusion')) {
      const id = 'doc-fusion-onepager';
      if (!approvalState[id] || (approvalState[id].status !== 'approved' && approvalState[id].status !== 'rejected' && !(approvalState[id].snoozeUntil > now))) {
        queue.push({
          id,
          type: 'doc_create',
          icon: 'doc',
          title: 'Generate one-pager for Fusion Fund',
          context: 'Their deck activity is up · meeting Apr 14',
          preview: `I'll create a one-page Fusion-tailored Harmonee brief in Drive:\n\n• Market thesis matched to their stage focus\n• Three traction proof points from your last 90 days\n• Use of funds aligned to your 18-month runway plan\n• Risk framing matched to their portfolio pattern\n\nLocation: /Harmonee AI/Investor Materials/Fusion-Onepager-${new Date().toISOString().slice(0, 10)}.docx`,
          meta: { docName: `Fusion-Onepager-${new Date().toISOString().slice(0, 10)}` },
          urgency: 'med',
          source: 'Drive',
          sourceDetail: 'Triggered by pipeline activity signal',
        });
      }
    }

    // ── 4. Task creation from email signals ──
    const priority = (data.gmail?.priorityThreads || []).slice(0, 2);
    priority.forEach((t) => {
      if (!t.from) return;
      const id = `task-${(t.email || t.from).replace(/[^a-z0-9]/gi, '').slice(0, 30)}`;
      if (approvalState[id]?.status === 'approved' || approvalState[id]?.status === 'rejected') return;
      if (approvalState[id]?.snoozeUntil && approvalState[id].snoozeUntil > now) return;
      const senderName = t.from.split('·')[0].split('<')[0].trim();
      queue.push({
        id,
        type: 'task_create',
        icon: 'check',
        title: `Add task: Follow up with ${senderName}`,
        context: `${(t.subject || '').slice(0, 80)}`,
        preview: `New task: "Follow up with ${senderName} on ${(t.subject || '').replace(/^(Re:|Fwd?:)\s*/i, '').slice(0, 60)}"\nDue: end of week\nTag: Today\nSynced to Google Tasks.`,
        meta: { taskText: `Follow up with ${senderName} — ${(t.subject || '').slice(0, 60)}` },
        urgency: 'low',
        source: 'Gmail',
        sourceDetail: 'Triggered by priority thread',
      });
    });

    // ── Demo fallback when not authed ──
    if (!isAuthed && queue.length === 0) {
      queue.push(
        {
          id: 'demo-reply-caroline',
          type: 'email_reply',
          icon: 'mail',
          title: 'Reply to Caroline Casson',
          context: '3 days waiting · "Re: Maximus partnership"',
          preview: `Hi Caroline,\n\nThanks for the patience. I wanted to make sure your team had real space to evaluate the SCORI fit before we set time. Happy to walk through a tailored deck on Thursday — does 2pm PT work?\n\n— Marcio`,
          meta: {},
          urgency: 'high',
          source: 'Gmail',
          sourceDetail: '3-day silence on priority thread',
        },
        {
          id: 'demo-prep-fusion',
          type: 'calendar_prep',
          icon: 'calendar',
          title: 'Block prep time for Fusion Fund call',
          context: 'Starts in 4h · Celeste Mercado + 2 partners',
          preview: `I'll add a 30-minute prep block at 10:45 AM with: the prior email thread, your tailored one-pager (draft below), Celeste's portfolio activity from the last 60 days, and three opening questions to lead with.`,
          meta: {},
          urgency: 'med',
          source: 'Calendar',
          sourceDetail: 'Triggered by upcoming meeting',
        },
        {
          id: 'demo-doc-scori',
          type: 'doc_create',
          icon: 'doc',
          title: 'Update SCORI space planning one-sheet',
          context: 'Zach finalized v3 yesterday',
          preview: `Replace the current /SCORI/Operations/space-planning-onesheet.docx with the v3 version Zach pushed. Adds the Linman Hall layout, KWUA section notes, and the updated cost breakdown.`,
          meta: {},
          urgency: 'med',
          source: 'Drive',
          sourceDetail: 'Triggered by recent doc activity',
        },
        {
          id: 'demo-task-haley',
          type: 'task_create',
          icon: 'check',
          title: 'Add task: Reply to Haley Huffman',
          context: 'Klamath County · Planning Dept Mtg',
          preview: `New task: "Confirm meeting details with Haley + send updated proposal"\nDue: end of week\nTag: Today\nSynced to Google Tasks.`,
          meta: {},
          urgency: 'low',
          source: 'Gmail',
          sourceDetail: 'Triggered by priority thread',
        },
      );
    }

    // Sort by urgency, then by source priority
    const urgencyOrder = { high: 0, med: 1, low: 2 };
    return queue.sort((a, b) => urgencyOrder[a.urgency] - urgencyOrder[b.urgency]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isAuthed, approvalState]);

  // ── Approval handlers ──
  const approveAction = async (item) => {
    setExecutingApprovalId(item.id);
    posthog.capture('approval_approved', { type: item.type, urgency: item.urgency });

    try {
      let executed = false;
      // Real execution for things we can actually do
      if (item.type === 'email_reply' && isAuthed && item.meta?.threadId) {
        // Save as draft in Gmail (use AI draft if available, fallback to template)
        try {
          const draftBody = aiDrafts[item.id] || item.preview;
          // createGmailDraft takes positional args (to, subject, body, opts).
          // Route through the account that owns the thread, and thread the
          // draft into the original conversation (threadId + In-Reply-To).
          await withAccountToken(item.meta.account, () =>
            createGmailDraft(item.meta.email, `Re: ${item.meta.subject || ''}`, draftBody, {
              threadId: item.meta.threadId,
              inReplyTo: item.meta.rfcMessageId,
            })
          );
          executed = true;
          addMessage('aria', 'ARIA · Draft saved', `Reply to <span class="em">${esc(item.title.replace(/^(Send reply|Reply) to /, ''))}</span> is threaded into the conversation in ${item.meta.account ? `<strong>${esc(item.meta.account)}</strong>'s` : 'your'} Gmail Drafts. Open Gmail → Drafts to review and send.`);
        } catch (e) {
          console.warn('Failed to save draft:', e);
          throw e;
        }
      } else if (item.type === 'calendar_prep' && isAuthed && item.meta?.eventTime) {
        // Actually create the calendar prep block
        try {
          const eventTime = new Date(item.meta.eventTime);
          const prepStart = new Date(eventTime.getTime() - 30 * 60 * 1000);
          const prepEnd = new Date(eventTime.getTime());
          await createGoogleCalendarEvent(
            `[Prep] ${item.meta.eventTitle}`,
            prepStart.toISOString(),
            prepEnd.toISOString(),
            `30-minute prep block created by ARIA.\n\nUpcoming meeting: ${item.meta.eventTitle}\n\nUse this time to: review prior thread context, scan related Drive docs, set clear outcome for the room.`,
          );
          executed = true;
          addMessage('aria', 'ARIA · Prep block on calendar', `30-minute prep for <span class="em">${esc(item.meta.eventTitle)}</span> is now on your calendar.`);
        } catch (e) {
          console.warn('Failed to create calendar event:', e);
          throw e;
        }
      } else if (item.type === 'doc_create' && isAuthed && item.meta?.docName) {
        // Create a real Google Doc with the proposed content
        try {
          const { createGoogleDoc } = await import('./google/api');
          const docUrl = await createGoogleDoc(item.meta.docName, item.preview);
          executed = true;
          addMessage('aria', 'ARIA · Doc created', `<a href="${docUrl}" target="_blank" rel="noopener" style="color:var(--gold-ink)">${item.meta.docName}</a> created in your Drive.`);
        } catch (e) {
          console.warn('Failed to create doc:', e);
          throw e;
        }
      } else if (item.type === 'task_create' && item.meta?.taskText) {
        createTaskFromContext(item.meta.taskText, 'Today', 'gold', null);
        executed = true;
        addMessage('aria', 'ARIA · Task created', `<span class="em">${esc(item.meta.taskText)}</span> is on your task list.`);
      } else {
        // Not authed, or missing meta — log the approval but flag for the user
        addMessage('aria', 'ARIA · Approved', `Logged: <span class="em">${esc(item.title)}</span>. Sign in with Google to let me actually execute these.`);
        executed = true;
      }

      persistApprovalState({ ...approvalState, [item.id]: { status: 'approved', timestamp: Date.now(), executed } });
    } catch (e) {
      console.error('Approval execution failed:', e);
      addMessage('aria', 'ARIA', `Couldn't execute that one — ${e.message || 'try again in a moment'}.`);
    } finally {
      setExecutingApprovalId(null);
    }
  };

  // Approve & SEND — the deliberate two-step path (the button in ApprovalRow
  // requires a second confirming click before this ever runs). Sends the
  // reply through the account that owns the thread, threaded into the
  // original conversation. Drafting stays the default; sending is opt-in.
  const sendApprovedReply = async (item) => {
    if (item.type !== 'email_reply' || !isAuthed || !item.meta?.email) return;
    setExecutingApprovalId(item.id);
    try {
      const body = aiDrafts[item.id] || item.preview;
      await withAccountToken(item.meta.account, () =>
        sendGmailEmail(item.meta.email, `Re: ${item.meta.subject || ''}`, body, {
          threadId: item.meta.threadId,
          inReplyTo: item.meta.rfcMessageId,
        })
      );
      persistApprovalState({ ...approvalState, [item.id]: { status: 'approved', timestamp: Date.now(), executed: true, sent: true } });
      addMessage('aria', 'ARIA · Sent', `Reply to <span class="em">${esc(item.title.replace(/^(Send reply|Reply) to /, ''))}</span> sent from <strong>${esc(item.meta.account || 'your account')}</strong>, threaded into the conversation.`);
      posthog.capture('approval_sent', { type: item.type });
    } catch (e) {
      console.warn('Send failed:', e);
      addMessage('aria', 'ARIA', `Couldn't send that reply — ${esc(e?.result?.error?.message || e?.message || 'Gmail rejected the request')}. Nothing went out; try Approve & draft instead.`);
    } finally {
      setExecutingApprovalId(null);
    }
  };

  const rejectAction = (item) => {
    persistApprovalState({ ...approvalState, [item.id]: { status: 'rejected', timestamp: Date.now() } });
    posthog.capture('approval_rejected', { type: item.type });
  };

  const snoozeAction = (item, hours = 4) => {
    persistApprovalState({
      ...approvalState,
      [item.id]: { status: 'snoozed', snoozeUntil: Date.now() + hours * 60 * 60 * 1000 },
    });
    posthog.capture('approval_snoozed', { type: item.type, hours });
  };

  const editApprovalPreview = (item) => {
    // Load into ARIA chat for refinement
    setActivePanel('aria');
    setChatInput(`Refine this draft before I approve it:\n\n${item.preview}\n\nWhat I want to change: `);
    setTimeout(() => inputRef.current?.focus(), 200);
    posthog.capture('approval_edit_requested', { type: item.type });
  };

  // ──── ARIA's Action List for Marcio — "Only You Can Do This" ────
  // Generates a personal action list framed for the operator. Filters for things
  // only Marcio can do: relationships, decisions, signoffs, strategic outreach.
  const computeMarcioActions = useCallback(() => {
    const actions = [];
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    // ── 1. UNANSWERED emails — real "needs response" detection ──
    // Pulls actual threads where the LAST message was from someone else and you haven't replied.
    const unanswered = data.gmail?.unansweredThreads || [];
    unanswered.forEach(t => {
      if (!t.date) return;
      const age = now - new Date(t.date).getTime();
      // Only surface threads that are at least 1 day old (give yourself time to reply naturally)
      if (age < 1 * oneDay) return;
      const days = Math.max(1, Math.round(age / oneDay));

      // Score for urgency: important + starred + age all increase urgency
      let urgency = 'low';
      if (days >= 5 || t.isImportant) urgency = 'high';
      else if (days >= 2 || t.isStarred) urgency = 'med';

      const verb = days >= 5 ? 'Re-engage' : (days >= 2 ? 'Reply to' : 'Respond to');
      const reasonBits = [];
      if (days === 1) reasonBits.push('1 day waiting');
      else reasonBits.push(`${days} days waiting`);
      if (t.messageCount > 1) reasonBits.push(`${t.messageCount} message thread`);
      if (t.isImportant) reasonBits.push('marked important');
      if (t.isStarred) reasonBits.push('starred');

      actions.push({
        verb,
        target: t.from,
        reason: `${reasonBits.join(' · ')} — "${t.subject.slice(0, 80)}"`,
        whyYou: 'Direct correspondence — only you can answer',
        consequence: days >= 5
          ? 'Relationship cooling. Recovery cost grows nonlinearly past day 5.'
          : 'Response window still open. Don\'t let it close.',
        urgency,
        source: 'Gmail',
        actionKey: t.email ? `email-${t.email}` : null,
        taskText: `Reply to ${t.from} — ${t.subject.slice(0, 70)}`,
        taskTag: urgency === 'high' ? 'Urgent' : urgency === 'med' ? 'Today' : 'This week',
        taskTagColor: urgency === 'high' ? 'red' : 'gold',
        _ageMs: age, // for sorting
      });
    });

    // ── 2. Prep for today's meetings without prep docs (only you decide what to bring) ──
    const cal = data.calendar || [];
    const today = new Date();
    cal.forEach(e => {
      if (!e.startISO || e.isAllDay) return;
      const eventTime = new Date(e.startISO);
      if (isNaN(eventTime) || eventTime.toDateString() !== today.toDateString()) return;
      const hoursUntil = (eventTime.getTime() - now) / (60 * 60 * 1000);
      if (hoursUntil < 0 || hoursUntil > 12) return;
      const title = e.summary || e.title || 'Untitled meeting';
      const attendees = (e.attendees || []).map(a => a.name || a.email).filter(Boolean);
      const lead = attendees[0] || 'this meeting';
      actions.push({
        verb: 'Prep',
        target: title,
        reason: `Starts in ${Math.round(hoursUntil)}h with ${attendees.length} attendees`,
        whyYou: 'Only you know what outcome you want from this room',
        consequence: hoursUntil < 2 ? 'Window to prep closing fast.' : 'Walk in with intent — not just calendar awareness.',
        urgency: hoursUntil < 2 ? 'high' : 'med',
        source: 'Calendar',
        actionKey: null,
        taskText: `Prep for ${title}${attendees.length ? ` (with ${lead})` : ''}`,
        taskTag: hoursUntil < 2 ? 'Now' : 'Today',
        taskTagColor: hoursUntil < 2 ? 'red' : 'blue',
      });
    });

    // ── 3. Investor signals requiring founder voice ──
    // Demo-only: snapshot's investorPipeline is mock content. Real investor
    // actions will come from the brain once it's wired to the dashboard.
    if (!isAuthed && data.stats?.investorPipeline) {
      const pipeline = (data.stats.investorPipeline || '').toLowerCase();
      if (pipeline.includes('fusion') || pipeline.includes('fund')) {
        actions.push({
          verb: 'Personal note to',
          target: 'Fusion Fund',
          reason: 'Deck activity + calendar momentum — they\'re leaning in',
          whyYou: 'A founder note moves the needle. An associate note doesn\'t.',
          consequence: 'Momentum windows decay in 72h if not reinforced personally.',
          urgency: 'med',
          source: 'Pipeline',
          actionKey: 'fusion',
          taskText: 'Send personal note to Fusion Fund — reinforce momentum',
          taskTag: 'This week',
          taskTagColor: 'gold',
        });
      }
    }

    // ── 4. Decisions on substantive doc changes (only you can approve) ──
    const drive = data.drive || [];
    const recentlyModified = drive.filter(d => {
      const t = d.modifiedTime ? new Date(d.modifiedTime).getTime() : 0;
      return t > now - 2 * oneDay;
    });
    if (recentlyModified.length >= 2) {
      actions.push({
        verb: 'Review & approve',
        target: `${recentlyModified.length} recently-modified docs`,
        reason: recentlyModified.slice(0, 2).map(d => d.name).join(' · '),
        whyYou: 'Document signoff requires founder authority',
        consequence: 'Team is waiting on you. Blocks downstream work.',
        urgency: 'med',
        source: 'Drive',
        actionKey: null,
        taskText: `Review ${recentlyModified.length} updated docs — ${recentlyModified[0].name}`,
        taskTag: 'Today',
        taskTagColor: 'gold',
      });
    }

    // ── Demo content when not authed (replaces any partial real-data items so demo is consistent) ──
    if (!isAuthed) {
      actions.length = 0;
      actions.push(
        {
          verb: 'Re-engage',
          target: 'Caroline Casson',
          reason: '3 days of silence on Maximus thread',
          whyYou: 'Founder relationship — not delegatable',
          consequence: 'Re-engagement window narrowing.',
          urgency: 'high',
          source: 'Gmail',
          actionKey: 'caroline',
          taskText: 'Reply to Caroline Casson — Maximus follow-up',
          taskTag: 'Today',
          taskTagColor: 'gold',
        },
        {
          verb: 'Personal note to',
          target: 'Fusion Fund',
          reason: 'Deck opened twice — Celeste leaning in',
          whyYou: 'Founder note moves the needle. Associate note doesn\'t.',
          consequence: 'Momentum windows decay in 72h.',
          urgency: 'med',
          source: 'Pipeline',
          actionKey: 'fusion',
          taskText: 'Send personal note to Celeste at Fusion Fund',
          taskTag: 'This week',
          taskTagColor: 'gold',
        },
        {
          verb: 'Approve',
          target: 'SCORI space planning one-sheet',
          reason: 'Zach finalized — waiting on your sign-off',
          whyYou: 'Founder signoff required for deployment',
          consequence: 'Team blocked on deployment until you decide.',
          urgency: 'med',
          source: 'Drive',
          actionKey: null,
          taskText: 'Approve SCORI space planning one-sheet',
          taskTag: 'Today',
          taskTagColor: 'gold',
        },
      );
    }

    // Sort by urgency first, then by age (oldest unanswered bubbles up within a tier)
    const order = { high: 0, med: 1, low: 2 };
    return actions.sort((a, b) => {
      const uDiff = order[a.urgency] - order[b.urgency];
      if (uDiff !== 0) return uDiff;
      return (b._ageMs || 0) - (a._ageMs || 0); // older first within same urgency
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isAuthed]);

  // ──── Content Renderers ────

  const renderMorningBrief = () => {
    const deskActions = computeMarcioActions();
    const approvalCount = computeApprovalQueue().length;
    const deskCount = deskActions.length;
    const meetingCount = data.calendar?.length || 0;
    const signalCount = computeAmbientSignals().length;

    // Derive ARIA's recommended focus from the top-ranked desk action —
    // real signal data, not copy. Null when there's nothing ranked.
    // (Ranking = urgency + age; not yet an outcome-level judgment.)
    const topAction = deskActions[0];
    const recommendedFocus = topAction
      ? `${topAction.verb} ${topAction.target}${topAction.reason ? ` — ${topAction.reason.toLowerCase().startsWith('the') ? topAction.reason : topAction.reason} ` : ''}`.trim()
      : null;
    // Context provenance: the actual thread + meeting the brief draws on.
    const briefContext = [
      ...(data.gmail?.unansweredThreads || []).slice(0, 1).map((t) => {
        const days = t.date ? Math.max(1, Math.round((Date.now() - new Date(t.date).getTime()) / 86400000)) : null;
        return { source: 'Gmail', label: (t.subject || t.from || '').slice(0, 42), age: days ? `${days}d waiting` : null };
      }),
      ...(data.calendar || []).slice(0, 1).map((e) => ({
        source: 'Calendar', label: (e.summary || '').slice(0, 42), age: e.start || null,
      })),
    ].filter((c) => c.label);

    return (
    <>
      {/* ══════════════════════════════════════════════════════════════ */}
      {/* STANDING STATUS — Heads-up display, first thing you see       */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <SystemPulse
        items={[
          {
            label: 'Awaiting approval',
            value: approvalCount,
            sub: approvalCount === 0 ? 'All clear' : approvalCount === 1 ? 'needs your sign-off' : 'need your sign-off',
            status: approvalCount > 0 ? 'needs-action' : 'clear',
            title: 'Jump to approval queue',
            onClick: () => document.querySelector('.approval-queue')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
          },
          {
            label: 'On your desk',
            value: deskCount,
            sub: deskCount === 0 ? 'Nothing pending' : 'only you can do',
            status: deskCount > 0 ? 'important' : 'clear',
            title: 'Jump to your desk',
            onClick: () => document.querySelector('.marcio-desk')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
          },
          {
            label: 'Meetings today',
            value: meetingCount,
            sub: meetingCount === 0 ? 'Open calendar' : meetingCount > 4 ? 'heavy day' : 'manageable',
            status: meetingCount > 4 ? 'busy' : 'moderate',
            title: 'Open meeting prep',
            onClick: () => { setActiveSidebar('Meeting Prep'); },
          },
          {
            label: 'ARIA noticed',
            value: signalCount,
            sub: signalCount === 0 ? 'no new patterns' : 'cross-source patterns',
            status: signalCount > 0 ? 'aria' : 'clear',
            title: "See ARIA's noticed patterns",
            onClick: () => document.querySelector('.ambient-signals')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
          },
        ]}
      />

      {/* Morning Brief Header */}
      <div className="brief-header">
        <div className="brief-date">{dateStr} · Klamath Falls, Oregon</div>
        <div className="brief-title">{timeGreeting}, <em>{userFirstName}.</em></div>
        <div className="brief-subtitle">
          {isAuthed ? ('ARIA has scanned ' + (googleAuthed && msAuthedState ? 'Google + Outlook' : googleAuthed ? 'Google' : 'Outlook') + '. Here\'s your unified brief.') : 'ARIA has read your overnight signals. Here\'s what matters today.'}
        </div>
      </div>

      {/* ARIA Brief */}
      <div className="aria-brief">
        <div className="aria-brief-header">
          <div className="aria-orb"></div>
          <span className="aria-label">ARIA · Morning Intelligence Brief</span>
          <span className="aria-timestamp">Generated {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
        </div>
        <MorningIntelligence recommendedFocus={recommendedFocus} context={briefContext} />
        <div className="brief-text" dangerouslySetInnerHTML={{ __html: (liveBrief || data.brief).text }} />
        <hr className="brief-divider" />
        <div className="brief-text" dangerouslySetInnerHTML={{ __html: (liveBrief || data.brief).text2 }} />
        <div className="brief-actions">
          {(liveBrief || data.brief).actions.map((action, i) => (
            <div key={i} className="action-chip" onClick={() => handleActionClick(action.key)}>
              <span className="chip-icon"><HarmoneeIcon name={action.icon} size={13} color="var(--gold)" /></span> {action.label}
            </div>
          ))}
          {isAuthed && (
            <div
              className="action-chip exec-workflow-chip"
              onClick={() => {
                setActivePanel('aria');
                setChatInput('COS this: ');
                setTimeout(() => inputRef.current?.focus(), 200);
                posthog.capture('cos_workflow_prompt_loaded');
              }}
              title="Chief of staff this — paste a trigger (email/thread/screenshot context)"
            >
              <span className="chip-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </span>
              Chief of staff this
            </div>
          )}
          {isAuthed && (
            <div
              className="action-chip investor-workflow-chip"
              onClick={() => {
                setActivePanel('aria');
                setChatInput('Investor intel on ');
                setTimeout(() => inputRef.current?.focus(), 200);
                posthog.capture('investor_workflow_prompt_loaded');
              }}
              title="Investor intelligence — paste a name, email, calendar invite, or thread"
            >
              <span className="chip-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </span>
              Investor intel
            </div>
          )}
          {isAuthed && googleUser?.email && (
            <div
              className={`action-chip brief-email-chip ${briefEmailStatus || ''}`}
              onClick={handleEmailBrief}
              title={`Send this brief to ${googleUser.email}`}
            >
              <span className="chip-icon">
                {briefEmailStatus === 'sending' ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25"/>
                    <path d="M22 12a10 10 0 0 1-10 10" />
                  </svg>
                ) : briefEmailStatus === 'sent' ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.4"><polyline points="20 6 9 17 4 12"/></svg>
                ) : briefEmailStatus === 'error' ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12" y2="16"/></svg>
                ) : (
                  <HarmoneeIcon name="mail" size={13} color="var(--gold)" />
                )}
              </span>
              {briefEmailStatus === 'sending' ? 'Sending…' : briefEmailStatus === 'sent' ? 'Sent! Check your inbox' : briefEmailStatus === 'error' ? 'Failed — try again' : 'Email me this brief'}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* APPROVAL QUEUE — Human in the Loop                              */}
      {/* The seam between what ARIA prepares and what Marcio approves    */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {(() => {
        const queue = computeApprovalQueue();
        if (queue.length === 0) {
          // Designed calm state instead of unmounting — the "all clear"
          // moment is the payoff of the approval loop. Keeps the
          // .approval-queue anchor so the SystemPulse jump still lands.
          return (
            <div className="approval-queue hm-approval-empty">
              <span className="hm-approval-empty-line" aria-hidden="true" />
              <span className="hm-approval-empty-text">Nothing needs your signature. ARIA is watching.</span>
            </div>
          );
        }

        return (
          <div className="approval-queue">
            <div className="approval-header">
              <div className="approval-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <polyline points="9 11 12 14 22 4"/>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
              </div>
              <div className="approval-titles">
                <div className="approval-title">Awaiting your approval</div>
                <div className="approval-subtitle">{queue.length} action{queue.length === 1 ? '' : 's'} ARIA prepared · approve, edit, or dismiss</div>
              </div>
              <div className="approval-count"><span>{queue.length}</span></div>
            </div>

            <div className="approval-list">
              {queue.slice(0, 8).map((item) => (
                <ApprovalRow
                  key={item.id}
                  item={item}
                  draftText={aiDrafts[item.id]}
                  isExecuting={executingApprovalId === item.id}
                  isAuthed={isAuthed}
                  onApprove={approveAction}
                  onEdit={editApprovalPreview}
                  onSnooze={snoozeAction}
                  onDismiss={rejectAction}
                  onSend={sendApprovedReply}
                />
              ))}
            </div>
          </div>
        );
      })()}

      {/* ──── On Your Desk — Action list ARIA built for Marcio ──── */}
      {/* "Things only you can do" — relationships, decisions, signoffs */}
      {(() => {
        const marcioActions = computeMarcioActions();
        if (marcioActions.length === 0) return null;
        return (
          <div className="marcio-desk">
            <div className="desk-header">
              <div className="desk-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
              </div>
              <div className="desk-titles">
                <div className="desk-title">{userFirstName}, on your desk today</div>
                <div className="desk-subtitle">{marcioActions.length} action{marcioActions.length === 1 ? '' : 's'} only you can do · ARIA prioritized by consequence</div>
              </div>
              <div className="desk-count">
                <span className="desk-count-num">{marcioActions.length}</span>
              </div>
            </div>
            <div className="desk-list">
              {marcioActions.slice(0, 10).map((a, i) => (
                <div key={i} className={`desk-action urgency-${a.urgency}`}>
                  <div className="desk-action-num">{i + 1}</div>
                  <div className="desk-action-body">
                    <div className="desk-action-headline">
                      <span className="desk-verb">{a.verb}</span> <span className="desk-target">{a.target}</span>
                    </div>
                    <div className="desk-action-reason">{a.reason}</div>
                    <div className="desk-action-why">
                      <span className="desk-why-dot">●</span> {a.whyYou}
                    </div>
                    <div className="desk-action-consequence">
                      <em>If ignored:</em> {a.consequence}
                    </div>
                    <div className="desk-action-meta">
                      <span className={`desk-urgency desk-urgency-${a.urgency}`}>{a.urgency === 'high' ? 'Urgent' : a.urgency === 'med' ? 'Today' : 'This week'}</span>
                      <span className="desk-source">{a.source}</span>
                    </div>
                  </div>
                  <div className="desk-action-buttons">
                    <button className="desk-btn desk-btn-primary" onClick={() => {
                      createTaskFromContext(a.taskText, a.taskTag, a.taskTagColor, a.actionKey);
                      posthog.capture('desk_action_added', { verb: a.verb, urgency: a.urgency });
                    }}>
                      Add to tasks
                    </button>
                    {a.actionKey && (
                      <button className="desk-btn desk-btn-ghost" onClick={() => {
                        handleActionClick(a.actionKey);
                        posthog.capture('desk_action_opened', { verb: a.verb });
                      }}>
                        Open with ARIA
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ──── Ambient Signals — patterns ARIA noticed without being asked ──── */}
      {/* "Shows me what I didn't know to look for" + "Sees across silos" + "Ahead of the problem" */}
      <div className="ambient-signals">
        <div className="ambient-header">
          <div className="ambient-pulse"></div>
          <span className="ambient-label">ARIA Noticed</span>
          <span className="ambient-sublabel">{isAuthed ? 'live cross-source patterns' : 'cross-source patterns from the last 7 days'}</span>
          {notifPermission !== 'granted' && isAuthed && (
            <button className="ambient-notif-btn" onClick={requestNotifPermission}>
              🔔 Enable alerts
            </button>
          )}
        </div>
        <div className="ambient-grid">
          {computeAmbientSignals().slice(0, 4).map((s, i) => (
            <div key={i} className={`ambient-card ambient-${s.kind} ${s.action ? 'has-action' : ''}`} onClick={() => s.action && handleActionClick(s.action)}>
              <div className="ambient-card-kind">{s.kind === 'silence' ? '◐' : s.kind === 'momentum' ? '▲' : s.kind === 'drift' ? '◇' : '◈'}</div>
              <div className="ambient-card-body">
                <div className="ambient-card-title">{s.title}</div>
                <div className="ambient-card-detail">{s.detail}</div>
                <div className="ambient-card-sources">
                  {s.sources.map((src, j) => (
                    <span key={j} className="ambient-source">{src}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Attention Engine — ranked by consequence */}
      <div className="attention-engine">
        <div className="section-header">
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name="section" size={16} color="var(--gold)" /> Attention Engine</div>
          <span className="card-action">{predictiveAlerts.length + data.gmail.priorityThreads.filter(t => t.priority === 'gold' || t.priority === 'red').length} signals ranked by consequence</span>
        </div>
        <div className="attention-subtitle">What deserves human attention now — ranked by organizational impact.</div>

        {/* Highest Leverage Thread */}
        {data.gmail.priorityThreads.filter(t => t.priority === 'gold' || t.priority === 'red').slice(0, 1).map((thread, i) => (
          <div key={i} className="attention-item critical">
            <div className="attn-rank">1</div>
            <div className="attn-body">
              <div className="attn-title">{thread.from.split('·')[0].trim()} — requires response</div>
              <div className="attn-desc">{thread.subject}</div>
              <div className="attn-consequence">If ignored: relationship cooling, re-engagement window closes within 48h</div>
              <div className="attn-provenance">
                <span className="prov-chip gmail">Gmail thread</span>
                <span className="prov-chip calendar">Calendar cross-ref</span>
                <span className="prov-chip pattern">Engagement pattern</span>
                <span className="prov-chip lcm">LCM context</span>
              </div>
            </div>
            <button className="attn-act" onClick={() => handleActionClick('caroline')}>Intervene</button>
          </div>
        ))}

        {/* Calendar Overload Detection */}
        {data.calendar.length >= 3 && (
          <div className="attention-item warning">
            <div className="attn-rank">2</div>
            <div className="attn-body">
              <div className="attn-title">Predicted attention bottleneck</div>
              <div className="attn-desc">{data.calendar.length} meetings today. ARIA estimates {Math.round(data.calendar.length * 0.75 * 100) / 100}h of deep work remaining.</div>
              <div className="attn-consequence">If unmanaged: context switching degrades decision quality by ~40%</div>
              <div className="attn-provenance">
                <span className="prov-chip calendar">Calendar analysis</span>
                <span className="prov-chip alygn">ALYGN score</span>
                <span className="prov-chip pattern">Historical pattern</span>
              </div>
            </div>
            <button className="attn-act" onClick={() => setActiveSidebar('Meeting Prep')}>Review</button>
          </div>
        )}

        {/* Investor Pipeline Signal */}
        <div className="attention-item signal">
          <div className="attn-rank">3</div>
          <div className="attn-body">
            <div className="attn-title">Investor pipeline — {data.stats.investorPipeline}</div>
            <div className="attn-desc">{data.stats.investorStatus}. Prep materials needed for Fusion Fund call (Apr 14).</div>
            <div className="attn-consequence">If unprepped: first impression with seed-stage fund weakened</div>
            <div className="attn-provenance">
              <span className="prov-chip gmail">Email matched</span>
              <span className="prov-chip calendar">Calendar event</span>
              <span className="prov-chip drive">Drive docs</span>
              <span className="prov-chip pipeline">Pipeline tracker</span>
            </div>
          </div>
          <button className="attn-act" onClick={() => handleActionClick('fusion')}>Prep</button>
        </div>

        {/* Cross-Project Activity */}
        {(data.drive || []).length > 4 && (
          <div className="attention-item signal">
            <div className="attn-rank">4</div>
            <div className="attn-body">
              <div className="attn-title">Cross-project activity spike</div>
              <div className="attn-desc">{(data.drive || []).length} documents modified across {sidebarProjects.length} deployments this week. Decisions may be waiting on you.</div>
              <div className="attn-consequence">If unseen: team decisions made without your input</div>
              <div className="attn-provenance">
                <span className="prov-chip drive">Drive activity</span>
                <span className="prov-chip lcm">Cross-project scan</span>
                <span className="prov-chip alygn">Alignment check</span>
              </div>
            </div>
            <button className="attn-act" onClick={() => setActiveSidebar('Documents')}>Review</button>
          </div>
        )}

        {/* Pending Tasks */}
        {tasks.filter(t => !t.done && t.tagColor === 'red').length > 0 && (
          <div className="attention-item warning">
            <div className="attn-rank">5</div>
            <div className="attn-body">
              <div className="attn-title">{tasks.filter(t => !t.done && t.tagColor === 'red').length} urgent task{tasks.filter(t => !t.done && t.tagColor === 'red').length > 1 ? 's' : ''} overdue</div>
              <div className="attn-desc">{tasks.filter(t => !t.done && t.tagColor === 'red').map(t => t.text).join(', ')}</div>
              <div className="attn-consequence">If delayed: cascading delays to dependent work streams</div>
              <div className="attn-provenance">
                <span className="prov-chip tasks">Task engine</span>
                <span className="prov-chip alygn">Deadline analysis</span>
              </div>
            </div>
            <button className="attn-act" onClick={() => setActivePanel('tasks')}>Resolve</button>
          </div>
        )}
      </div>

      {/* System Status Bar */}
      <div className="system-status-bar">
        <div className="sys-stat">
          <span className="sys-label">Emails</span>
          <span className="sys-value">{data.stats.emailsToday}</span>
          <span className="sys-detail">{data.stats.emailsFlagged} flagged</span>
        </div>
        <div className="sys-divider"></div>
        <div className="sys-stat">
          <span className="sys-label">Meetings</span>
          <span className="sys-value">{data.stats.meetings}/{data.stats.meetingsTotal}</span>
          <span className="sys-detail" style={{ color: 'var(--green)' }}>confirmed</span>
        </div>
        <div className="sys-divider"></div>
        <div className="sys-stat">
          <span className="sys-label">Pipeline</span>
          <span className="sys-value" style={{ fontSize: '13px' }}>{data.stats.investorPipeline}</span>
          <span className="sys-detail">{data.stats.investorStatus?.split(' ').slice(0, 3).join(' ')}</span>
        </div>
        <div className="sys-divider"></div>
        <div className="sys-stat">
          <span className="sys-label">Health</span>
          <span className="sys-value" style={{ color: healthScore.color }}>{healthScore.overall}</span>
          <span className="sys-detail">{healthScore.label}</span>
        </div>
      </div>

      {/* Email + Calendar Grid */}
      <div className="grid-2">
        {/* Gmail */}
        <div className="card">
          <div className="card-header">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><HarmoneeIcon name="gmail" size={14} color="var(--mid)" /> Gmail — Priority Threads</span>
            <a className="card-action" href="https://mail.google.com" target="_blank" rel="noreferrer">Open in Gmail →</a>
          </div>
          {data.gmail.priorityThreads.map((email, i) => (
            <div key={i} className="email-item">
              <div className="email-avatar" style={{ background: email.color }}>{email.initials}</div>
              <div className="email-body">
                <div className="email-from">{email.from}</div>
                <div className="email-subject">{email.subject}</div>
              </div>
              <div className="email-meta">
                {googleAccounts.length > 1 && email.accountShort && (
                  <div className="hm-acct-tag" title={email.account}>{email.accountShort}</div>
                )}
                <div className="email-time">{email.time}</div>
                <div className="priority-dot" style={{ background: PRIORITY_COLORS[email.priority] }}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Calendar */}
        <div className="card">
          <div className="card-header">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><HarmoneeIcon name="cal" size={14} color="var(--mid)" /> Today's Calendar</span>
            <a className="card-action" href="https://calendar.google.com" target="_blank" rel="noreferrer">Open Calendar →</a>
          </div>
          {data.calendar.map((event, i) => (
            <div key={i} className="cal-item" style={{ borderColor: event.color }} onClick={() => {
              setActiveSidebar('Meeting Prep');
            }}>
              <div className="cal-time">{event.start}</div>
              <div className="cal-info">
                <div className="cal-title">{event.summary}</div>
                <div className="cal-sub">{event.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Google Drive Documents */}
      <div className="section-full">
        <div className="section-header">
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name="doc" size={16} color="var(--mid)" /> Google Drive — Recent Documents</div>
          <a className="card-action" href="https://drive.google.com" target="_blank" rel="noreferrer">Open Drive →</a>
        </div>
        <div className="grid-2">
          <div>
            {filteredDrive.slice(0, 4).map((doc, i) => (
              <a key={i} className="drive-item" href={doc.url} target="_blank" rel="noreferrer">
                <div className="drive-icon" style={{ background: `${doc.color}20` }}><HarmoneeIcon name={doc.icon} size={17} color={doc.color} /></div>
                <div className="drive-info">
                  <div className="drive-title">{doc.title}</div>
                  <div className="drive-meta">
                    <span className="drive-author">{doc.modifiedBy}</span>
                    <span className="drive-project" style={{ background: `${doc.color}20`, color: doc.color }}>{doc.project}</span>
                  </div>
                </div>
                <span className="drive-time">{doc.modified}</span>
              </a>
            ))}
          </div>
          <div>
            {filteredDrive.slice(4).map((doc, i) => (
              <a key={i} className="drive-item" href={doc.url} target="_blank" rel="noreferrer">
                <div className="drive-icon" style={{ background: `${doc.color}20` }}><HarmoneeIcon name={doc.icon} size={17} color={doc.color} /></div>
                <div className="drive-info">
                  <div className="drive-title">{doc.title}</div>
                  <div className="drive-meta">
                    <span className="drive-author">{doc.modifiedBy}</span>
                    <span className="drive-project" style={{ background: `${doc.color}20`, color: doc.color }}>{doc.project}</span>
                  </div>
                </div>
                <span className="drive-time">{doc.modified}</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Google Sheets (only when authed) */}
      {isAuthed && sheets.length > 0 && (
        <div className="section-full">
          <div className="section-header">
            <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name="sheets" size={16} color="var(--mid)" /> Google Sheets — Recent Spreadsheets</div>
            <a className="card-action" href="https://docs.google.com/spreadsheets" target="_blank" rel="noreferrer">Open Sheets →</a>
          </div>
          {sheets.map((sheet, i) => (
            <a key={i} className="sheet-item" href={sheet.url} target="_blank" rel="noreferrer">
              <div className="sheet-icon"><HarmoneeIcon name="sheets" size={15} color="var(--green)" /></div>
              <div className="sheet-info">
                <div className="sheet-name">{sheet.title}</div>
                <div className="sheet-meta">{sheet.modifiedBy}</div>
              </div>
              <span className="sheet-time">{sheet.modified}</span>
            </a>
          ))}
        </div>
      )}

      {/* Automations */}
      <div className="auto-scroll">
        <div className="section-header">
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name="bolt" size={16} color="var(--gold)" /> Active Automations — Running Now</div>
          <span className="card-action" onClick={() => setActiveSidebar('Automations')} style={{ cursor: 'pointer' }}>View all {automations.length} →</span>
        </div>
        {automations.filter(a => a.active).map((auto, i) => {
          const realIndex = automations.indexOf(auto);
          return (
            <div key={i} className="auto-card">
              <div className="auto-header">
                <div className="auto-icon"><HarmoneeIcon name={auto.icon} size={18} color="var(--gold)" /></div>
                <div>
                  <div className="auto-title">{auto.title}</div>
                  <div className="auto-desc">{auto.desc}</div>
                </div>
              </div>
              <div className="auto-footer">
                <div className="auto-engines">
                  {auto.engines.map(e => (
                    <span key={e} className={`engine-tag ${e}`}>{e === 'align' ? 'ALYGNMENT' : e.toUpperCase()}</span>
                  ))}
                </div>
                <button className="auto-btn active" onClick={() => toggleAutomation(realIndex)}>
                  ● Running
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
    );
  };

  // Suggested automations — ARIA detects patterns from user behavior
  const suggestedAutomations = [
    {
      icon: 'mail',
      title: 'Weekly Client Check-In Drafter',
      desc: 'You email Zach about OTV client status every few days. ARIA can auto-draft a check-in email every Monday and Thursday with the latest from each account.',
      engines: ['aria', 'lcm'],
      reason: 'You\'ve sent 6 client status requests in the last 2 weeks',
    },
    {
      icon: 'calendar',
      title: 'Meeting Notes Auto-Summary',
      desc: 'After each calendar event ends, ARIA pulls the related email threads and Drive docs, then generates a one-paragraph summary and next steps — saved to your Drive.',
      engines: ['aria', 'align'],
      reason: 'You review meeting transcripts manually 3–4x per week',
    },
    {
      icon: 'bolt',
      title: 'Stale Thread Escalator',
      desc: 'When a priority email thread goes quiet for 72+ hours, ARIA drafts a follow-up and adds it to your morning brief as a drift signal. No lead goes cold.',
      engines: ['lcm'],
      reason: 'You\'ve had 4 threads go cold in the past 10 days',
    },
  ];

  const [customAutoName, setCustomAutoName] = useState('');
  const [customAutoDesc, setCustomAutoDesc] = useState('');
  const [showAutoForm, setShowAutoForm] = useState(false);

  const addCustomAutomation = () => {
    if (!customAutoName.trim()) return;
    const newAuto = {
      icon: 'gear',
      title: customAutoName.trim(),
      desc: customAutoDesc.trim() || `Custom automation: ${customAutoName.trim()}`,
      engines: ['aria'],
      active: true,
    };
    setAutomations(prev => [...prev, newAuto]);
    setCustomAutoName('');
    setCustomAutoDesc('');
    setShowAutoForm(false);
  };

  const enableSuggestedAutomation = (suggested) => {
    const newAuto = {
      icon: suggested.icon,
      title: suggested.title,
      desc: suggested.desc,
      engines: suggested.engines,
      active: true,
    };
    setAutomations(prev => [...prev, newAuto]);
  };

  const renderAutomations = () => (
    <>
      <div className="brief-header">
        <div className="brief-date">WORKSPACE</div>
        <div className="page-title">Automations</div>
        <div className="brief-subtitle">{automations.filter(a => a.active).length} running · {automations.filter(a => !a.active).length} available to enable</div>
      </div>

      {/* ARIA Suggested Automations */}
      <div className="suggested-auto-section">
        <div className="section-header">
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div className="aria-orb" style={{ width: 14, height: 14 }}></div>
            ARIA Suggested Automations
          </div>
          <span className="card-action" style={{ color: 'var(--fg)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Based on your patterns this week</span>
        </div>
        <div className="suggested-auto-subtitle">I've noticed a few things you keep doing manually. Here are automations I can handle for you:</div>

        {suggestedAutomations.filter(s => !automations.find(a => a.title === s.title)).map((suggested, i) => (
          <div key={i} className="auto-card suggested-highlight" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="auto-header">
              <div className="auto-icon" style={{ background: 'rgba(201, 168, 76, 0.15)' }}><HarmoneeIcon name={suggested.icon} size={18} color="var(--gold)" /></div>
              <div>
                <div className="auto-title">{suggested.title}</div>
                <div className="auto-desc">{suggested.desc}</div>
                <div className="suggested-reason"><HarmoneeIcon name="bolt" size={11} color="var(--gold)" /> {suggested.reason}</div>
              </div>
            </div>
            <div className="auto-footer">
              <div className="auto-engines">
                {suggested.engines.map(e => (
                  <span key={e} className={`engine-tag ${e}`}>{e === 'align' ? 'ALYGNMENT' : e.toUpperCase()}</span>
                ))}
              </div>
              <button className="auto-btn suggested-enable" onClick={() => enableSuggestedAutomation(suggested)}>
                + Enable
              </button>
            </div>
          </div>
        ))}
        {suggestedAutomations.filter(s => !automations.find(a => a.title === s.title)).length === 0 && (
          <div className="suggested-auto-empty">All suggested automations are active. ARIA will suggest more as new patterns emerge.</div>
        )}
      </div>

      {/* Active Automations */}
      <div className="section-header" style={{ marginTop: 24 }}>
        <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name="bolt" size={16} color="var(--gold)" /> Active Automations</div>
        <span className="card-action">{automations.filter(a => a.active).length} running</span>
      </div>
      {automations.map((auto, i) => (
        <div key={i} className={`auto-card ${!auto.active ? 'suggested' : ''}`}>
          <div className="auto-header">
            <div className="auto-icon"><HarmoneeIcon name={auto.icon} size={18} color="var(--gold)" /></div>
            <div>
              <div className="auto-title">{auto.title}</div>
              <div className="auto-desc">{auto.desc}</div>
            </div>
          </div>
          <div className="auto-footer">
            <div className="auto-engines">
              {auto.engines.map(e => (
                <span key={e} className={`engine-tag ${e}`}>{e === 'align' ? 'ALYGNMENT' : e.toUpperCase()}</span>
              ))}
            </div>
            <button className={`auto-btn ${auto.active ? 'active' : ''}`} onClick={() => toggleAutomation(i)}>
              {auto.active ? '● Running' : '+ Enable'}
            </button>
          </div>
        </div>
      ))}

      {/* Create Custom Automation */}
      <div className="create-auto-section" style={{ marginTop: 20 }}>
        {!showAutoForm ? (
          <button className="create-auto-btn" onClick={() => setShowAutoForm(true)}>
            <HarmoneeIcon name="gear" size={15} color="var(--gold)" />
            Create Automation
          </button>
        ) : (
          <div className="create-auto-form">
            <div className="create-auto-form-title">New Automation</div>
            <input
              className="create-auto-input"
              placeholder="Automation name (e.g., Daily Revenue Report)"
              value={customAutoName}
              onChange={e => setCustomAutoName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCustomAutomation()}
              autoFocus
            />
            <textarea
              className="create-auto-textarea"
              placeholder="Describe what this automation should do..."
              value={customAutoDesc}
              onChange={e => setCustomAutoDesc(e.target.value)}
              rows={3}
            />
            <div className="create-auto-form-actions">
              <button className="auto-btn active" onClick={addCustomAutomation} disabled={!customAutoName.trim()}>
                + Create & Enable
              </button>
              <button className="auto-btn" onClick={() => { setShowAutoForm(false); setCustomAutoName(''); setCustomAutoDesc(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderMeetingPrep = () => (
    <>
      <div className="brief-header">
        <div className="brief-date">{dateStr}</div>
        <div className="page-title">Meeting Prep</div>
        <div className="brief-subtitle">{data.calendar.length} meetings today — prep materials below</div>
      </div>

      {data.calendar.map((event, i) => {
        // Find related emails for this meeting
        const relatedEmails = data.gmail.priorityThreads.filter(t =>
          event.summary.toLowerCase().split(' ').some(word =>
            word.length > 3 && t.subject.toLowerCase().includes(word)
          ) ||
          (event.organizer && t.from.toLowerCase().includes(event.organizer.toLowerCase()))
        );
        // Find related drive docs
        const relatedDocs = (data.drive || []).filter(doc =>
          event.summary.toLowerCase().split(' ').some(word =>
            word.length > 3 && doc.title.toLowerCase().includes(word)
          )
        );

        return (
          <div key={i} className="meeting-prep-card">
            <div className="prep-header">
              <div className="prep-time-badge" style={{ background: `${event.color}20`, color: event.color, border: `1px solid ${event.color}40` }}>
                {event.start} – {event.end}
              </div>
              <div className="prep-meeting-title">{event.summary}</div>
              <div className="prep-meta">{event.sub}</div>
              {event.location && <div className="prep-location">📍 {event.location}</div>}
            </div>

            {relatedEmails.length > 0 && (
              <div className="prep-section">
                <div className="prep-section-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><HarmoneeIcon name="gmail" size={13} color="var(--mid)" /> Related Email Threads</div>
                {relatedEmails.map((email, j) => (
                  <div key={j} className="email-item prep-email-clickable" style={{ padding: '6px 0', cursor: 'pointer' }} onClick={() => {
                    const fromName = email.from.split('·')[0].trim();
                    const key = fromName.split(' ')[0].toLowerCase();
                    handleActionClick(key);
                  }}>
                    <div className="email-avatar" style={{ background: email.color, width: 24, height: 24, fontSize: '9px' }}>{email.initials}</div>
                    <div className="email-body">
                      <div className="email-from" style={{ fontSize: '11px' }}>{email.from}</div>
                      <div className="email-subject" style={{ fontSize: '10px' }}>{email.subject}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {relatedDocs.length > 0 && (
              <div className="prep-section">
                <div className="prep-section-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><HarmoneeIcon name="doc" size={13} color="var(--mid)" /> Related Documents</div>
                {relatedDocs.map((doc, j) => (
                  <a key={j} className="drive-item" href={doc.url} target="_blank" rel="noreferrer" style={{ marginBottom: 4 }}>
                    <div className="drive-icon" style={{ background: `${doc.color}20`, width: 28, height: 28 }}><HarmoneeIcon name={doc.icon} size={14} color={doc.color} /></div>
                    <div className="drive-info">
                      <div className="drive-title" style={{ fontSize: '11px' }}>{doc.title}</div>
                    </div>
                  </a>
                ))}
              </div>
            )}

            {relatedEmails.length === 0 && relatedDocs.length === 0 && (
              <div className="prep-section">
                <div className="prep-empty">No related emails or documents found. ARIA will index more context as you use the platform.</div>
              </div>
            )}

            <div className="prep-actions">
              <button className="prep-action-btn" onClick={() => {
                setActivePanel('aria');
                setIsTyping(true);
                setTimeout(() => {
                  setIsTyping(false);
                  addMessage('aria', 'ARIA · Meeting Prep', `Here's what I know about <span class="em">${esc(event.summary)}</span> at ${esc(event.start)}:\n\n${event.sub ? esc(event.sub) + '.' : ''} ${event.location ? 'Location: ' + esc(event.location) + '.' : ''} ${relatedEmails.length > 0 ? relatedEmails.length + ' related email thread(s) found.' : ''} ${relatedDocs.length > 0 ? relatedDocs.length + ' related document(s) found.' : ''}\n\nWant me to build a one-page prep brief, or add a prep task?`, { topic: `${event.summary} prep`, taskText: `Prep for ${event.summary} at ${event.start}`, tag: event.start, tagColor: 'blue' });
                }, 1200);
              }}>Ask ARIA to prep</button>
              <a className="prep-action-btn outline" href="https://calendar.google.com" target="_blank" rel="noreferrer">Open in Calendar</a>
            </div>
          </div>
        );
      })}
    </>
  );

  // ──── Investor Prep Dashboard ────
  const [prepTarget] = useState(null);

  const renderInvestorPrep = () => {
    const target = prepTarget || {
      name: 'Fusion Fund',
      contact: 'Celeste Mercado',
      role: 'Partner',
      date: 'April 14, 2026 · 11:15 AM',
      type: 'Intro Call — Seed Stage',
      status: 'Call booked Apr 14',
    };

    // Find related docs from drive
    const investorDocs = (data.drive || []).filter(d =>
      d.title.toLowerCase().includes('investor') ||
      d.title.toLowerCase().includes('pitch') ||
      d.title.toLowerCase().includes('one-sheet') ||
      d.title.toLowerCase().includes('harmonee') ||
      d.title.toLowerCase().includes('board') ||
      d.title.toLowerCase().includes('weekly update') ||
      d.project === 'Harmonee AI'
    );

    // Find related emails
    const investorEmails = (data.gmail?.priorityThreads || []).filter(e =>
      e.from.toLowerCase().includes('fusion') ||
      e.from.toLowerCase().includes('celeste') ||
      e.from.toLowerCase().includes('investor') ||
      e.subject.toLowerCase().includes('fund') ||
      e.subject.toLowerCase().includes('invest')
    );

    return (
      <>
        <div className="brief-header">
          <div className="brief-date">ARIA · INVESTOR INTELLIGENCE</div>
          <div className="page-title">{target.name} — Prep Dashboard</div>
          <div className="brief-subtitle">ALYGN has analyzed your pipeline, docs, and signals. Here's everything you need.</div>
        </div>

        {/* Overview Card */}
        <div className="prep-dash-grid">
          <div className="prep-dash-card overview-card">
            <div className="prep-dash-label">MEETING OVERVIEW</div>
            <div className="prep-dash-row">
              <div className="prep-dash-field">
                <span className="prep-field-label">Fund</span>
                <span className="prep-field-value">{target.name}</span>
              </div>
              <div className="prep-dash-field">
                <span className="prep-field-label">Contact</span>
                <span className="prep-field-value">{target.contact}</span>
              </div>
              <div className="prep-dash-field">
                <span className="prep-field-label">Role</span>
                <span className="prep-field-value">{target.role}</span>
              </div>
            </div>
            <div className="prep-dash-row">
              <div className="prep-dash-field">
                <span className="prep-field-label">Date & Time</span>
                <span className="prep-field-value">{target.date}</span>
              </div>
              <div className="prep-dash-field">
                <span className="prep-field-label">Meeting Type</span>
                <span className="prep-field-value">{target.type}</span>
              </div>
              <div className="prep-dash-field">
                <span className="prep-field-label">Pipeline Status</span>
                <span className="prep-field-value" style={{ color: 'var(--gold)' }}>● {target.status}</span>
              </div>
            </div>
          </div>

          {/* ARIA Talking Points */}
          <div className="prep-dash-card">
            <div className="prep-dash-label"><span className="prep-engine-tag aria">ARIA</span> SUGGESTED TALKING POINTS</div>
            <div className="prep-talking-points">
              <div className="talking-point">
                <div className="tp-num">1</div>
                <div className="tp-content">
                  <div className="tp-title">Lead with Ambient Business Intelligence</div>
                  <div className="tp-desc">Position Harmonee as creating a new category — not competing in existing ones. Fusion Fund invests in category-defining companies. Show how ABI differs from traditional BI dashboards.</div>
                </div>
              </div>
              <div className="talking-point">
                <div className="tp-num">2</div>
                <div className="tp-content">
                  <div className="tp-title">Proof of deployment — Government clients</div>
                  <div className="tp-desc">Klamath County Event Center and Oregon Tech (SCORI) are live. Real data flowing, real decisions being made. This isn't a concept — it's deployed infrastructure.</div>
                </div>
              </div>
              <div className="talking-point">
                <div className="tp-num">3</div>
                <div className="tp-content">
                  <div className="tp-title">ALYGN Engine — the technical moat</div>
                  <div className="tp-desc">The alignment scoring engine is proprietary. It continuously measures organizational alignment across communication, decisions, and execution. No competitor has this.</div>
                </div>
              </div>
              <div className="talking-point">
                <div className="tp-num">4</div>
                <div className="tp-content">
                  <div className="tp-title">Revenue path — B2B SaaS for teams of 10-500</div>
                  <div className="tp-desc">Show the OTV deployment as proof of team-scale viability. Each seat generates ongoing intelligence. Path to $1M ARR with 45 enterprise accounts.</div>
                </div>
              </div>
            </div>
          </div>

          {/* ALYGN Readiness Score */}
          <div className="prep-dash-card">
            <div className="prep-dash-label"><span className="prep-engine-tag alygn">ALYGN</span> PITCH READINESS ANALYSIS</div>
            <div className="alygn-readiness">
              <div className="readiness-meter">
                <div className="readiness-score">78</div>
                <div className="readiness-label">Readiness Score</div>
              </div>
              <div className="readiness-breakdown">
                <div className="readiness-item">
                  <span className="ri-label">Pitch materials</span>
                  <div className="ri-bar"><div className="ri-fill" style={{ width: '85%', background: 'var(--green)' }}></div></div>
                  <span className="ri-pct">85%</span>
                </div>
                <div className="readiness-item">
                  <span className="ri-label">Fund thesis alignment</span>
                  <div className="ri-bar"><div className="ri-fill" style={{ width: '72%', background: 'var(--gold)' }}></div></div>
                  <span className="ri-pct">72%</span>
                </div>
                <div className="readiness-item">
                  <span className="ri-label">Competitive positioning</span>
                  <div className="ri-bar"><div className="ri-fill" style={{ width: '90%', background: 'var(--green)' }}></div></div>
                  <span className="ri-pct">90%</span>
                </div>
                <div className="readiness-item">
                  <span className="ri-label">Financial model</span>
                  <div className="ri-bar"><div className="ri-fill" style={{ width: '55%', background: 'var(--red)' }}></div></div>
                  <span className="ri-pct">55%</span>
                </div>
                <div className="readiness-item">
                  <span className="ri-label">Demo readiness</span>
                  <div className="ri-bar"><div className="ri-fill" style={{ width: '95%', background: 'var(--green)' }}></div></div>
                  <span className="ri-pct">95%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Related Documents */}
          <div className="prep-dash-card">
            <div className="prep-dash-label"><HarmoneeIcon name="doc" size={13} color="var(--mid)" /> RELATED DOCUMENTS — {investorDocs.length} found</div>
            <div className="prep-doc-list">
              {investorDocs.map((doc, i) => (
                <a key={i} className="prep-doc-item" href={doc.url} target="_blank" rel="noreferrer">
                  <div className="prep-doc-icon" style={{ background: `${doc.color}20` }}>
                    <HarmoneeIcon name={doc.icon} size={14} color={doc.color} />
                  </div>
                  <div className="prep-doc-info">
                    <div className="prep-doc-title">{doc.title}</div>
                    <div className="prep-doc-meta">{doc.modifiedBy} · {doc.modified}</div>
                  </div>
                  <div className="prep-doc-tag">{doc.project}</div>
                </a>
              ))}
            </div>
          </div>

          {/* Related Email Threads */}
          {investorEmails.length > 0 && (
            <div className="prep-dash-card">
              <div className="prep-dash-label"><HarmoneeIcon name="gmail" size={13} color="var(--mid)" /> EMAIL INTELLIGENCE — {investorEmails.length} threads</div>
              <div className="prep-email-list">
                {investorEmails.map((email, i) => (
                  <div key={i} className="prep-email-item" onClick={() => {
                    const key = email.from.split(' ')[0].toLowerCase();
                    handleActionClick(key);
                  }}>
                    <div className="email-avatar" style={{ background: email.color, width: 28, height: 28, fontSize: '10px' }}>{email.initials}</div>
                    <div className="prep-email-info">
                      <div className="prep-email-from">{email.from}</div>
                      <div className="prep-email-subj">{email.subject}</div>
                    </div>
                    <div className="prep-email-time">{email.time}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ARIA Pre-Call Checklist */}
          <div className="prep-dash-card">
            <div className="prep-dash-label"><span className="prep-engine-tag aria">ARIA</span> PRE-CALL CHECKLIST</div>
            <div className="prep-checklist">
              {[
                { text: 'Review Harmonee One-Sheeter — latest version', done: true },
                { text: 'Update financial projections slide', done: false },
                { text: 'Research Fusion Fund portfolio (recent investments)', done: false },
                { text: 'Prepare 2-min demo of Pulse dashboard (this product)', done: true },
                { text: 'Draft follow-up email template for post-call', done: false },
                { text: 'Confirm call link and calendar invite details', done: true },
              ].map((item, i) => (
                <div key={i} className={`checklist-item ${item.done ? 'done' : ''}`}>
                  <div className={`check-box ${item.done ? 'checked' : ''}`}>{item.done ? '✓' : ''}</div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="prep-dash-actions">
          <button className="prep-action-btn" onClick={() => {
            setActivePanel('aria');
            setIsTyping(true);
            setTimeout(() => {
              setIsTyping(false);
              addMessage('aria', 'ARIA · Investor Prep', `I've analyzed your ${target.name} prep materials. Your pitch readiness is at <strong>78%</strong>. The biggest gap is your financial model — consider updating projections before the call. Your demo readiness is excellent at 95%. Want me to draft a pre-call agenda to send to ${target.contact}, or build a one-page brief you can reference during the call?`, {
                topic: `${target.name} prep`,
                taskText: `Complete ${target.name} prep — update financial model`,
                tag: 'Apr 14',
                tagColor: 'gold',
              });
            }, 1500);
          }}>Ask ARIA to build call brief</button>
          <button className="prep-action-btn outline" onClick={() => {
            const draft = {
              to: '',
              subject: `Pre-call agenda: ${target.name} / Harmonee intro`,
              body: `Hi ${target.contact.split(' ')[0]},\n\nLooking forward to our call on the 14th. Here's a brief agenda I had in mind:\n\n1. Harmonee overview — what we're building and why\n2. Live demo of the Pulse dashboard\n3. Our deployment traction (government clients)\n4. Investment thesis and use of funds\n5. Q&A\n\nHappy to adjust based on what's most useful for you. See you Monday!\n\nBest,\n${userFirstName}`,
            };
            const draftPreview = draft.body.replace(/\n/g, '<br>');
            setActivePanel('aria');
            addMessage('aria', 'ARIA · Draft', `<div class="draft-preview"><div class="draft-to">To: ${target.contact}</div><div class="draft-subject">Subject: ${draft.subject}</div><div class="draft-body">${draftPreview}</div></div><div class="draft-hint">Say "save to drafts" to push to your inbox, or "edit" to modify.</div>`, { draft, topic: `${target.name} pre-call email` });
          }}>Draft pre-call email</button>
          <button className="prep-action-btn outline" onClick={() => setActiveSidebar('Morning Brief')}>← Back to Brief</button>
        </div>
      </>
    );
  };

  const renderDocuments = () => (
    <>
      <div className="brief-header">
        <div className="brief-date">WORKSPACE</div>
        <div className="page-title">Documents</div>
        <div className="brief-subtitle">{(data.drive || []).length} documents indexed · {sheets.length} spreadsheets tracked{activeProject ? ` · Filtered: ${activeProject}` : ''}</div>
      </div>

      {/* Project filter chips */}
      <div className="project-filter-row">
        <div className={`filter-chip ${!activeProject ? 'active' : ''}`} onClick={() => setActiveProject(null)}>All</div>
        {sidebarProjects.map(p => (
          <div key={p.name}
            className={`filter-chip ${activeProject === p.name ? 'active' : ''}`}
            style={activeProject === p.name ? { borderColor: p.color, color: p.color, background: `${p.color}15` } : {}}
            onClick={() => setActiveProject(prev => prev === p.name ? null : p.name)}>
            <span className="project-dot" style={{ background: p.color, width: 6, height: 6 }}></span>
            {p.name}
          </div>
        ))}
      </div>

      {/* Drive docs */}
      <div className="section-full" style={{ animationDelay: '0s' }}>
        <div className="section-header">
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name="doc" size={16} color="var(--mid)" /> Google Drive — Documents</div>
          <a className="card-action" href="https://drive.google.com" target="_blank" rel="noreferrer">Open Drive →</a>
        </div>
        {filteredDrive.map((doc, i) => (
          <a key={i} className="drive-item" href={doc.url} target="_blank" rel="noreferrer">
            <div className="drive-icon" style={{ background: `${doc.color}20` }}><HarmoneeIcon name={doc.icon} size={17} color={doc.color} /></div>
            <div className="drive-info">
              <div className="drive-title">{doc.title}</div>
              <div className="drive-meta">
                <span className="drive-author">{doc.modifiedBy}</span>
                <span className="drive-project" style={{ background: `${doc.color}20`, color: doc.color }}>{doc.project}</span>
              </div>
            </div>
            <span className="drive-time">{doc.modified}</span>
          </a>
        ))}
        {filteredDrive.length === 0 && (
          <div className="empty-state">No documents found{activeProject ? ` for ${activeProject}` : ''}.</div>
        )}
      </div>

      {/* Sheets */}
      {sheets.length > 0 && (
        <div className="section-full">
          <div className="section-header">
            <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name="sheets" size={16} color="var(--mid)" /> Google Sheets — Spreadsheets</div>
            <a className="card-action" href="https://docs.google.com/spreadsheets" target="_blank" rel="noreferrer">Open Sheets →</a>
          </div>
          {sheets.map((sheet, i) => (
            <a key={i} className="sheet-item" href={sheet.url} target="_blank" rel="noreferrer">
              <div className="sheet-icon"><HarmoneeIcon name="sheets" size={15} color="var(--green)" /></div>
              <div className="sheet-info">
                <div className="sheet-name">{sheet.title}</div>
                <div className="sheet-meta">{sheet.modifiedBy}</div>
              </div>
              <span className="sheet-time">{sheet.modified}</span>
            </a>
          ))}
        </div>
      )}
    </>
  );

  const renderWrapUp = () => {
    const doneTasks = tasks.filter(t => t.done);
    const pendingTasks = tasks.filter(t => !t.done);
    const now = new Date();
    const hour = now.getHours();
    const isEvening = hour >= 16;

    return (
      <>
        <div className="brief-header">
          <div className="brief-date">{dateStr}</div>
          <div className="page-title">{isEvening ? 'End-of-Day' : 'Day'} Wrap-Up</div>
          <div className="brief-subtitle">Here's where things stand{isEvening ? ' heading into tomorrow' : ' so far today'}.</div>
        </div>

        {/* Health Score Summary */}
        <div className="wrapup-health">
          <div className="wrapup-health-header">
            <div className="health-orb" style={{ background: healthScore.color, boxShadow: `0 0 12px ${healthScore.color}`, width: 14, height: 14 }}></div>
            <span className="wrapup-health-title">Business Health: <span style={{ color: healthScore.color }}>{healthScore.overall}/100 — {healthScore.label}</span></span>
          </div>
          <div className="wrapup-health-bars">
            {Object.values(healthScore.breakdown).map((item, i) => (
              <div key={i} className="wrapup-bar-row">
                <span className="wrapup-bar-label">{item.label}</span>
                <div className="health-row-bar" style={{ flex: 1 }}>
                  <div className="health-row-fill" style={{
                    width: `${item.score}%`,
                    background: item.score >= 75 ? 'var(--green)' : item.score >= 50 ? 'var(--gold)' : 'var(--red)',
                  }}></div>
                </div>
                <span className="wrapup-bar-score" style={{ color: item.score >= 75 ? 'var(--green)' : item.score >= 50 ? 'var(--gold)' : 'var(--red)' }}>{item.score}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid-2">
          {/* Completed */}
          <div className="card">
            <div className="card-header">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><HarmoneeIcon name="tasks" size={14} color="var(--green)" /> Completed ({doneTasks.length})</span>
            </div>
            {doneTasks.length === 0 && <div className="empty-state">No tasks completed yet.</div>}
            {doneTasks.map((task, i) => (
              <div key={i} className="wrapup-item done" onClick={() => toggleTask(task.id)} style={{ cursor: 'pointer' }} title="Click to undo">
                <span className="wrapup-check" style={{ cursor: 'pointer' }}>✓</span>
                <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{task.text}</span>
              </div>
            ))}
          </div>

          {/* Still Pending */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">⏳ Still Pending ({pendingTasks.length})</span>
            </div>
            {pendingTasks.length === 0 && <div className="empty-state">All caught up!</div>}
            {pendingTasks.map((task, i) => (
              <div key={i} className="wrapup-item pending" onClick={() => toggleTask(task.id)} style={{ cursor: 'pointer' }} title="Click to mark complete">
                <span className="wrapup-check-empty"></span>
                <span>{task.text}</span>
                <span className="task-tag" style={{ background: TAG_COLORS[task.tagColor]?.bg, color: TAG_COLORS[task.tagColor]?.color, marginLeft: 'auto', flexShrink: 0 }}>
                  {task.tag}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Today's Meetings Summary */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><HarmoneeIcon name="cal" size={14} color="var(--mid)" /> Meetings Today — {data.calendar.length} total</span>
          </div>
          {data.calendar.map((event, i) => (
            <div key={i} className="cal-item" style={{ borderColor: event.color }}>
              <div className="cal-time">{event.start}</div>
              <div className="cal-info">
                <div className="cal-title">{event.summary}</div>
                <div className="cal-sub">{event.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ARIA Tomorrow Preview */}
        <div className="aria-brief" style={{ animationDelay: '0.2s' }}>
          <div className="aria-brief-header">
            <div className="aria-orb"></div>
            <span className="aria-label">ARIA · Tomorrow's Preview</span>
          </div>
          <div className="brief-text">
            Based on today's activity, ARIA recommends prioritizing the <strong>pending tasks above</strong> first thing tomorrow.
            {pendingTasks.filter(t => t.tagColor === 'red').length > 0 && (
              <> The <span className="highlight">{pendingTasks.filter(t => t.tagColor === 'red').length} urgent item(s)</span> should be addressed before any meetings.</>
            )}
            {' '}Your business health score will improve as you clear the backlog. ARIA will prepare a fresh intelligence brief overnight.
          </div>
        </div>
      </>
    );
  };

  const renderDrift = () => {
    const pendingTasks = tasks.filter(t => !t.done);
    const urgentTasks = pendingTasks.filter(t => t.tagColor === 'red');
    const coldEmails = data.gmail.priorityThreads.filter(t => t.priority === 'gold');
    const overloadedDays = data.calendar.length >= 4;

    const driftSignals = [
      ...urgentTasks.map(t => ({
        type: 'overdue',
        title: `Overdue: ${t.text}`,
        desc: 'This task has passed its deadline. Downstream work may be blocked.',
        severity: 'high',
        source: ['Task engine', 'Deadline analysis'],
        action: 'Resolve',
      })),
      ...coldEmails.map(t => ({
        type: 'silence',
        title: `Silent thread: ${t.from.split('·')[0].trim()}`,
        desc: `${t.subject} — no response sent. Thread is cooling.`,
        severity: 'medium',
        source: ['Gmail', 'Engagement pattern'],
        action: 'Draft reply',
      })),
      ...(overloadedDays ? [{
        type: 'bottleneck',
        title: 'Decision bottleneck forming',
        desc: `${data.calendar.length} meetings today with ${pendingTasks.length} pending tasks. Context fragmentation likely.`,
        severity: 'medium',
        source: ['Calendar', 'Task engine', 'ALYGN'],
        action: 'Prioritize',
      }] : []),
      ...(data.drive || []).length > 5 ? [{
        type: 'fragmentation',
        title: 'Cross-project context fragmentation',
        desc: `${(data.drive || []).length} documents modified across ${sidebarProjects.length} deployments. Alignment check recommended.`,
        severity: 'low',
        source: ['Drive', 'Cross-project scan', 'LCM'],
        action: 'Review',
      }] : [],
      {
        type: 'duplication',
        title: 'Repeated coordination pattern detected',
        desc: 'Meeting prep + email follow-up + task creation happening manually for every meeting. Automation candidate.',
        severity: 'low',
        source: ['Pattern engine', 'NEERO'],
        action: 'Automate',
      },
    ];

    return (
      <>
        <div className="brief-header">
          <div className="brief-date">ALIGNMENT MONITOR</div>
          <div className="page-title">Operational Drift</div>
          <div className="brief-subtitle">Where alignment is slipping — overdue decisions, silent threads, repeated bottlenecks, context fragmentation.</div>
        </div>

        {/* Drift Health */}
        <div className="drift-summary">
          <div className="drift-score">
            <div className="drift-score-value" style={{ color: driftSignals.filter(d => d.severity === 'high').length > 0 ? 'var(--red)' : driftSignals.length > 3 ? 'var(--gold)' : 'var(--green)' }}>
              {driftSignals.length}
            </div>
            <div className="drift-score-label">drift signals</div>
          </div>
          <div className="drift-breakdown">
            <div className="drift-stat"><span className="drift-dot high"></span>{driftSignals.filter(d => d.severity === 'high').length} critical</div>
            <div className="drift-stat"><span className="drift-dot medium"></span>{driftSignals.filter(d => d.severity === 'medium').length} warning</div>
            <div className="drift-stat"><span className="drift-dot low"></span>{driftSignals.filter(d => d.severity === 'low').length} advisory</div>
          </div>
        </div>

        {/* Drift Signals */}
        {driftSignals.map((signal, i) => (
          <div key={i} className={`drift-card ${signal.severity}`}>
            <div className="drift-type-badge">{signal.type.replace('_', ' ')}</div>
            <div className="drift-card-title">{signal.title}</div>
            <div className="drift-card-desc">{signal.desc}</div>
            <div className="drift-provenance">
              {signal.source.map((s, j) => (
                <span key={j} className="prov-chip">{s}</span>
              ))}
            </div>
            <button className="attn-act" onClick={() => {
              setActivePanel('aria');
              setIsTyping(true);

              if (signal.action === 'Draft reply') {
                // Immediately compose and show the draft
                const context = {
                  topic: signal.title,
                  taskText: signal.title,
                  tag: 'Today',
                  tagColor: 'gold',
                };
                const draft = composeDraft(context);
                const draftPreview = esc(draft.body).replace(/\n/g, '<br>');
                const draftHtml = `
                  <div class="email-draft">
                    <div class="draft-header">
                      <div class="draft-field"><span class="draft-label">To:</span> ${esc(draft.to || '(fill in recipient)')}</div>
                      <div class="draft-field"><span class="draft-label">Subject:</span> ${esc(draft.subject)}</div>
                    </div>
                    <div class="draft-body">${draftPreview}</div>
                  </div>`;
                setTimeout(() => {
                  setIsTyping(false);
                  addMessage('aria', 'ARIA · Draft Ready', `Here's your draft for <span class="em">${esc(signal.title)}</span>:${draftHtml}`, {
                    ...context,
                    draft,
                    isDraft: true,
                  });
                  // Auto-save to Gmail if authenticated
                  if (isAuthed && draft.to) {
                    saveDraftToGmail(draft).then(result => {
                      if (result) {
                        addMessage('aria', 'ARIA · Saved', `Draft saved to your Gmail Drafts. Open Gmail to review and send it.`);
                      }
                    });
                  }
                }, 1200);
              } else {
                // Other actions: show the investigation message
                setTimeout(() => {
                  setIsTyping(false);
                  addMessage('aria', 'ARIA · Drift Analysis', `Investigating drift signal: <span class="em">${esc(signal.title)}</span>. ${esc(signal.desc)} I can help resolve this — want me to take action or add it to your queue?`, {
                    topic: signal.title,
                    taskText: signal.title,
                    tag: signal.severity === 'high' ? 'Urgent' : 'Today',
                    tagColor: signal.severity === 'high' ? 'red' : 'gold',
                  });
                }, 1200);
              }
            }}>{signal.action}</button>
          </div>
        ))}

        {/* End-of-day status */}
        {renderWrapUp()}
      </>
    );
  };

  const renderWorkflows = () => (
    <>
      <div className="brief-header">
        <div className="brief-date">NEERO ENGINE</div>
        <div className="page-title">Operations & Systems</div>
        <div className="brief-subtitle">NEERO orchestrates how your tools, data, and intelligence engines connect across deployments.</div>
      </div>

      {/* NEERO Connection Map */}
      <div className="neero-map">
        <div className="neero-center">
          <div className="neero-hub">
            <LogoSVG />
            <div className="neero-hub-label">NEERO</div>
          </div>
        </div>
        <div className="neero-nodes">
          {[
            { name: 'Gmail', icon: 'gmail', color: '#EA4335', status: 'Connected', count: `${data.gmail.priorityThreads.length} threads` },
            { name: 'Calendar', icon: 'cal', color: '#4285F4', status: 'Connected', count: `${data.calendar.length} events` },
            { name: 'Drive', icon: 'drive', color: '#0F9D58', status: 'Connected', count: `${(data.drive || []).length} docs` },
            { name: 'Tasks', icon: 'tasks', color: '#4285F4', status: isAuthed ? 'Connected' : 'Pending', count: `${tasks.length} tasks` },
            { name: 'Sheets', icon: 'sheets', color: '#0F9D58', status: isAuthed && sheets.length > 0 ? 'Connected' : 'Pending', count: `${sheets.length} sheets` },
            { name: 'ARIA', icon: 'brain', color: '#C9A84C', status: 'Active', count: 'AI Engine' },
            { name: 'ALYGN', icon: 'bolt', color: '#C9A84C', status: 'Active', count: `Score: ${healthScore.overall}` },
            { name: 'LCM', icon: 'link', color: '#4CAF7C', status: 'Active', count: 'Context Engine' },
          ].map((node, i) => (
            <div key={i} className="neero-node" style={{ '--node-color': node.color }}>
              <div className="neero-node-icon"><HarmoneeIcon name={node.icon} size={20} color={node.color} /></div>
              <div className="neero-node-name">{node.name}</div>
              <div className={`neero-node-status ${node.status === 'Connected' || node.status === 'Active' ? 'active' : ''}`}>
                {node.status}
              </div>
              <div className="neero-node-count">{node.count}</div>
              <div className="neero-connector" style={{ background: `linear-gradient(to right, ${node.color}, transparent)` }}></div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Workflows */}
      <div className="section-header" style={{ marginTop: 24 }}>
        <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name="bolt" size={16} color="var(--gold)" /> Active Workflows</div>
        <span className="card-action" onClick={() => { setActiveNav('Intelligence'); setActiveSidebar('Automations'); }} style={{ cursor: 'pointer' }}>Manage Automations →</span>
      </div>
      {automations.filter(a => a.active).map((auto, i) => (
        <div key={i} className="workflow-card">
          <div className="workflow-flow">
            <span className="workflow-step trigger">Trigger</span>
            <span className="workflow-arrow">→</span>
            <span className="workflow-step process">{auto.engines.map(e => e === 'align' ? 'ALYGN' : e.toUpperCase()).join(' + ')}</span>
            <span className="workflow-arrow">→</span>
            <span className="workflow-step action">Action</span>
          </div>
          <div className="auto-title" style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name={auto.icon} size={16} color="var(--gold)" /> {auto.title}</div>
          <div className="auto-desc">{auto.desc}</div>
        </div>
      ))}
    </>
  );

  const renderDeployments = () => {
    const deployments = [
      {
        name: 'Klamath County Event Center', status: 'Active', statusColor: 'var(--green)', project: 'On The Verge', color: '#4a9eff',
        desc: 'Event management, space planning, and scheduling automation.',
        health: 87, adoption: '78%', models: ['ARIA', 'LCM'], automations: 3, timeSaved: '12h/week',
        systems: ['Google Calendar', 'Drive', 'Gmail'], departments: ['Operations', 'Events'],
        insight: 'Scheduling conflicts reduced 60% since deployment. Space utilization up 23%.',
        bottleneck: null,
      },
      {
        name: 'Oregon Tech — SCORI Hub', status: 'In Progress', statusColor: 'var(--gold)', project: 'SCORI Hub', color: '#C9A84C',
        desc: 'Lab space configuration and equipment placement intelligence.',
        health: 62, adoption: '34%', models: ['ARIA', 'ALYGN'], automations: 1, timeSaved: '4h/week',
        systems: ['Drive', 'Sheets'], departments: ['Engineering', 'Facilities'],
        insight: 'Equipment placement model trained on 3 prior configurations.',
        bottleneck: 'Waiting on OIT partnership terms finalization',
      },
      {
        name: 'Harmonee AI — Internal', status: 'Active', statusColor: 'var(--green)', project: 'Harmonee AI', color: '#4CAF7C',
        desc: 'Internal productivity dashboard, investor pipeline tracking.',
        health: 91, adoption: '100%', models: ['ARIA', 'LCM', 'ALYGN'], automations: 4, timeSaved: '18h/week',
        systems: ['Gmail', 'Calendar', 'Drive', 'Tasks', 'Sheets'], departments: ['Executive', 'Sales', 'Operations'],
        insight: 'Investor response rate improved 35% with automated triage.',
        bottleneck: null,
      },
      {
        name: 'K Lounge / 4FC', status: 'Planning', statusColor: '#8b7cf6', project: 'K Lounge / 4FC', color: '#8b7cf6',
        desc: 'Lease management and venue operations automation.',
        health: 28, adoption: '0%', models: ['ARIA'], automations: 0, timeSaved: '—',
        systems: ['Drive'], departments: ['Operations'],
        insight: 'Pre-deployment assessment complete. Lease doc analysis ready.',
        bottleneck: 'Pending lease agreement finalization with Jean Turgesen',
      },
    ];

    return (
      <>
        <div className="brief-header">
          <div className="brief-date">DEPLOYMENT INTELLIGENCE</div>
          <div className="page-title">Deployments</div>
          <div className="brief-subtitle">{deployments.filter(d => d.status === 'Active').length} active · {deployments.length} total environments across {new Set(deployments.flatMap(d => d.departments)).size} departments</div>
        </div>

        {deployments.map((dep, i) => (
          <div key={i} className="dep-mission-card">
            <div className="dep-mission-header">
              <div className="dep-mission-left">
                <span className="project-dot" style={{ background: dep.color, width: 10, height: 10 }}></span>
                <div className="dep-name">{dep.name}</div>
                <div className="dep-status" style={{ color: dep.statusColor, background: `${dep.statusColor}15`, border: `1px solid ${dep.statusColor}30` }}>
                  {dep.status}
                </div>
              </div>
              <div className="dep-health-mini">
                <div className="dep-health-bar">
                  <div className="dep-health-fill" style={{ width: `${dep.health}%`, background: dep.health >= 75 ? 'var(--green)' : dep.health >= 50 ? 'var(--gold)' : 'var(--red)' }}></div>
                </div>
                <span className="dep-health-value" style={{ color: dep.health >= 75 ? 'var(--green)' : dep.health >= 50 ? 'var(--gold)' : 'var(--red)' }}>{dep.health}</span>
              </div>
            </div>

            <div className="dep-desc">{dep.desc}</div>

            <div className="dep-metrics-grid">
              <div className="dep-metric">
                <div className="dep-metric-value">{dep.adoption}</div>
                <div className="dep-metric-label">Adoption</div>
              </div>
              <div className="dep-metric">
                <div className="dep-metric-value">{dep.automations}</div>
                <div className="dep-metric-label">Automations</div>
              </div>
              <div className="dep-metric">
                <div className="dep-metric-value">{dep.timeSaved}</div>
                <div className="dep-metric-label">Time Saved</div>
              </div>
              <div className="dep-metric">
                <div className="dep-metric-value">{dep.departments.length}</div>
                <div className="dep-metric-label">Departments</div>
              </div>
            </div>

            <div className="dep-detail-row">
              <div className="dep-detail-section">
                <div className="dep-detail-label">Models Active</div>
                <div className="dep-model-tags">
                  {dep.models.map(m => <span key={m} className={`engine-tag ${m.toLowerCase()}`}>{m}</span>)}
                </div>
              </div>
              <div className="dep-detail-section">
                <div className="dep-detail-label">Connected Systems</div>
                <div className="dep-systems">{dep.systems.join(' · ')}</div>
              </div>
            </div>

            {dep.insight && (
              <div className="dep-insight">
                <span className="dep-insight-label">Latest Insight:</span> {dep.insight}
              </div>
            )}

            {dep.bottleneck && (
              <div className="dep-bottleneck">
                <span className="dep-bottleneck-label">Unresolved:</span> {dep.bottleneck}
              </div>
            )}
          </div>
        ))}
      </>
    );
  };

  const renderVault = () => (
    <>
      <div className="brief-header">
        <div className="brief-date">SECURE STORAGE</div>
        <div className="page-title">Vault</div>
        <div className="brief-subtitle">Encrypted storage for credentials, API keys, and sensitive documents.</div>
      </div>
      <div className="coming-soon-card">
        <div className="cs-icon">🔐</div>
        <div className="cs-title">Secure Credential Vault</div>
        <div className="cs-desc">Store API keys, OAuth tokens, passwords, and sensitive documents with end-to-end encryption. Access control per project and team member. Vault integrates with all Harmonee automations securely.</div>
        <div className="vault-items">
          {[
            { name: 'Google OAuth Token', type: 'OAuth', status: isAuthed ? 'Active' : 'Expired', color: isAuthed ? 'var(--green)' : 'var(--red)' },
            { name: 'Gmail API Access', type: 'API Key', status: isAuthed ? 'Connected' : 'Not connected', color: isAuthed ? 'var(--green)' : 'var(--mid)' },
            { name: 'Drive API Access', type: 'API Key', status: isAuthed ? 'Connected' : 'Not connected', color: isAuthed ? 'var(--green)' : 'var(--mid)' },
          ].map((item, i) => (
            <div key={i} className="vault-item">
              <div className="vault-item-icon">🔑</div>
              <div className="vault-item-info">
                <div className="vault-item-name">{item.name}</div>
                <div className="vault-item-type">{item.type}</div>
              </div>
              <div className="vault-item-status" style={{ color: item.color }}>{item.status}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  // ──── OTV Clients Dashboard ────
  const [otvUpdates, setOtvUpdates] = useState({});
  const [otvRequesting, setOtvRequesting] = useState(null);

  const otvClients = [
    {
      id: 'kwua',
      name: 'KWUA',
      fullName: 'Klamath Water Users Association',
      desc: 'Water resource management and irrigation district coordination across the Klamath Basin.',
      status: 'Active',
      color: '#4a9eff',
      icon: 'calendar',
    },
    {
      id: 'scori',
      name: 'SCORI',
      fullName: 'Southern Oregon Regional Innovation Hub',
      desc: 'Regional innovation hub supporting tech transfer, lab operations, and Oregon Tech partnerships.',
      status: 'Active',
      color: '#C9A84C',
      icon: 'gear',
    },
    {
      id: 'fairgrounds',
      name: 'Fair Grounds',
      fullName: 'Klamath County Fairgrounds',
      desc: 'Event management, vendor coordination, and seasonal programming for county fairgrounds.',
      status: 'Active',
      color: '#4CAF7C',
      icon: 'calendar',
    },
    {
      id: 'scored',
      name: 'SCORED',
      fullName: 'Southern Central Oregon Economic Development',
      desc: 'Economic development initiatives, grant management, and regional growth strategy.',
      status: 'Active',
      color: '#8b7cf6',
      icon: 'chart',
    },
    {
      id: 'event-center',
      name: 'Event Center',
      fullName: 'Klamath County Event Center',
      desc: 'Space planning, event scheduling, and automation deployment for county events.',
      status: 'Active',
      color: '#E57373',
      icon: 'bolt',
    },
  ];

  const requestOTVUpdate = async (client) => {
    if (!isAuthed) {
      addMessage('aria', 'ARIA', 'Connect Google or Outlook to send update requests to Zach.');
      setActivePanel('aria');
      return;
    }
    setOtvRequesting(client.id);
    const subject = `Harmonee Pulse — ${client.name} Status Update Request`;
    const body = `Hey Zach,\n\nQuick check-in from Harmonee Pulse — what's the latest on ${client.fullName} (${client.name})?\n\nAnything I should know about:\n- Current status and active work\n- Any blockers or decisions needed\n- Upcoming deadlines or deliverables\n- Client communications or meetings\n\nThis feeds directly into my dashboard so I can stay aligned.\n\nThanks,\n${userFirstName}`;
    try {
      const result = await createGmailDraft('zach@vergemktg.com', subject, body);
      if (result) {
        setOtvUpdates(prev => ({ ...prev, [client.id]: { requested: new Date().toLocaleString(), status: 'Sent to Gmail Drafts' } }));
        addMessage('aria', 'ARIA · OTV', `Update request for <strong>${client.name}</strong> saved to your Gmail Drafts. Open Gmail to review and send to Zach.`);
      }
    } catch (e) {
      console.warn('Failed to draft OTV update:', e);
      addMessage('aria', 'ARIA', `Couldn't create draft for ${client.name}. Try again.`);
    }
    setOtvRequesting(null);
    setActivePanel('aria');
  };

  const requestAllOTVUpdates = async () => {
    if (!isAuthed) {
      addMessage('aria', 'ARIA', 'Connect Google or Outlook to send update requests to Zach.');
      setActivePanel('aria');
      return;
    }
    setOtvRequesting('all');
    const clientNames = otvClients.map(c => c.name).join(', ');
    const clientList = otvClients.map(c => `• ${c.fullName} (${c.name})`).join('\n');
    const subject = `Harmonee Pulse — OTV Full Client Update Request`;
    const body = `Hey Zach,\n\nQuick check-in from Harmonee Pulse — can you give me a status update on all our OTV clients?\n\n${clientList}\n\nFor each, I'd love to know:\n- Current status and active work\n- Any blockers or decisions needed\n- Upcoming deadlines or deliverables\n- Client communications or meetings\n\nThis feeds directly into my dashboard so I can stay aligned across all accounts.\n\nThanks,\n${userFirstName}`;
    try {
      const result = await createGmailDraft('zach@vergemktg.com', subject, body);
      if (result) {
        const now = new Date().toLocaleString();
        const updates = {};
        otvClients.forEach(c => { updates[c.id] = { requested: now, status: 'Sent to Gmail Drafts' }; });
        setOtvUpdates(prev => ({ ...prev, ...updates }));
        addMessage('aria', 'ARIA · OTV', `Full update request for all ${otvClients.length} OTV clients (${clientNames}) saved to your Gmail Drafts. Open Gmail to review and send to Zach.`);
      }
    } catch (e) {
      console.warn('Failed to draft OTV updates:', e);
      addMessage('aria', 'ARIA', `Couldn't create the update request draft. Try again.`);
    }
    setOtvRequesting(null);
    setActivePanel('aria');
  };

  const renderOTVClients = () => (
    <>
      <div className="brief-header">
        <div className="brief-date">ON THE VERGE · CLIENT PORTFOLIO</div>
        <div className="page-title">OTV Clients</div>
        <div className="brief-subtitle">{otvClients.length} active clients · Managed by Zach · <span style={{ color: 'var(--gold)', cursor: 'pointer' }} onClick={requestAllOTVUpdates}>{otvRequesting === 'all' ? 'Sending...' : 'Request All Updates →'}</span></div>
      </div>

      <div className="section-full" style={{ animationDelay: '0s' }}>
        <div className="section-header">
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HarmoneeIcon name="bolt" size={16} color="#4a9eff" /> Client Accounts</div>
          <span className="card-action">zach@vergemktg.com</span>
        </div>

        {otvClients.map((client, i) => {
          const update = otvUpdates[client.id];
          return (
            <div key={client.id} className="otv-client-card" style={{ animationDelay: `${i * 60}ms`, borderLeftColor: client.color }}>
              <div className="otv-client-header">
                <div className="otv-client-icon" style={{ background: `${client.color}20`, color: client.color }}>
                  <HarmoneeIcon name={client.icon} size={16} color={client.color} />
                </div>
                <div className="otv-client-info">
                  <div className="otv-client-name">{client.name}</div>
                  <div className="otv-client-full">{client.fullName}</div>
                </div>
                <div className="otv-client-status" style={{ color: client.color }}>{client.status}</div>
              </div>
              <div className="otv-client-desc">{client.desc}</div>
              {update && (
                <div className="otv-update-status">
                  <span className="otv-update-dot"></span>
                  Update requested {update.requested} · {update.status}
                </div>
              )}
              <div className="otv-client-actions">
                <button className="otv-btn request" onClick={() => requestOTVUpdate(client)} disabled={otvRequesting === client.id}>
                  <HarmoneeIcon name="mail" size={13} color="var(--gold)" />
                  {otvRequesting === client.id ? 'Sending...' : 'Request Update'}
                </button>
                <button className="otv-btn view" onClick={() => {
                  setActiveProject('On The Verge');
                  setActiveSidebar('Documents');
                }}>
                  <HarmoneeIcon name="doc" size={13} color="var(--mid)" />
                  View Docs
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  // ──── Command Center — C-Suite floating dashboard ────
  const renderCommandCenter = () => (
    <PortfolioCommand
      data={portfolio}
      status={portfolioStatus}
      onRetry={loadPortfolio}
      onAskAria={(prompt) => {
        setActivePanel('aria');
        setChatInput(prompt);
        setTimeout(() => inputRef.current?.focus(), 200);
      }}
    />
  );

  const renderSuggestionsPage = () => (
    <>
      <div className="onboarding-banner">
        <div className="ob-header">
          <div className="aria-orb"></div>
          <span className="ob-title">30-Day Intelligence Onboarding</span>
          <span className="ob-days">Day 7 of 30</span>
        </div>
        <div className="ob-text">ARIA is learning your patterns. The more you approve or dismiss, the more precisely it learns what to automate vs. surface to you.</div>
        <div className="progress-bar"><div className="progress-fill"></div></div>
      </div>
      <div className="section-title" style={{ marginBottom: 12, marginTop: 16 }}>Today's Suggestions</div>
      {suggestions.length === 0 && (
        <div className="empty-state">All caught up! No pending suggestions. ARIA will surface new ones as signals come in.</div>
      )}
      {suggestions.map((sug, i) => (
        <div key={i} className="suggestion-item">
          <div className="sug-header">
            <span className={`sug-type ${sug.type}`}>{sug.typeLabel}</span>
            <span className="sug-impact">{sug.impact}</span>
          </div>
          <div className="sug-title">{sug.title}</div>
          <div className="sug-desc">{sug.desc}</div>
          <div className="sug-actions">
            <button className="sug-btn" onClick={() => handleDismissSuggestion(i)}>Dismiss</button>
            <button className="sug-btn primary" onClick={() => handleReviewSuggestion(sug)}>Review →</button>
          </div>
        </div>
      ))}
    </>
  );

  // ──── Tasks Page (moved from right panel) ────
  const renderTasksPage = () => (
    <>
      <div className="task-header-row">
        <div className="section-title">Today's Focus</div>
        {isAuthed && tasks.some(t => !t.synced && !t.listId) && (
          <button className="sync-all-btn" onClick={syncAllTasks}>
            <HarmoneeIcon name="sync" size={13} color="var(--mid)" /> Sync {tasks.filter(t => !t.synced && !t.listId).length} to Google
          </button>
        )}
      </div>
      <div className="task-stats">
        <span className="task-stat-done">{tasks.filter(t => t.done).length} done</span>
        <span className="task-stat-remaining">{tasks.filter(t => !t.done).length} remaining</span>
        {isAuthed && <span className="task-stat-synced"><HarmoneeIcon name="sync" size={12} color="var(--mid)" /> {tasks.filter(t => t.synced || t.listId).length} synced</span>}
      </div>
      {tasks.filter(t => !t.done).map(task => (
        <div key={task.id} className="task-item">
          <div className="task-check" onClick={() => toggleTask(task.id)}></div>
          <div className={`task-text ${task.sourceKey ? 'task-linked' : ''}`} onClick={() => {
            if (task.sourceKey) handleActionClick(task.sourceKey);
          }} title={task.sourceKey ? 'Click to open source' : ''}>{task.text}</div>
          <div className="task-sync-status">
            {(task.synced || task.listId) ? (
              <span className="sync-icon synced" title="Synced to Google Tasks"><HarmoneeIcon name="sync" size={13} color="var(--green)" /></span>
            ) : isAuthed ? (
              <span className="sync-icon unsynced" title="Not synced" onClick={() => syncTaskToGoogle(task.id, task.text, task.tag)}><HarmoneeIcon name="upload" size={13} color="var(--mid)" /></span>
            ) : null}
          </div>
          <div className="task-tag" style={{ background: TAG_COLORS[task.tagColor]?.bg, color: TAG_COLORS[task.tagColor]?.color }}>
            {task.tag}
          </div>
        </div>
      ))}
      {tasks.filter(t => t.done).length > 0 && (
        <>
          <div className="task-completed-divider">
            <span className="task-completed-label">✓ Completed ({tasks.filter(t => t.done).length})</span>
          </div>
          {tasks.filter(t => t.done).map(task => (
            <div key={task.id} className="task-item completed">
              <div className="task-check done" onClick={() => toggleTask(task.id)}></div>
              <div className="task-text">{task.text}</div>
              <div className="task-tag" style={{ background: TAG_COLORS[task.tagColor]?.bg, color: TAG_COLORS[task.tagColor]?.color, opacity: 0.5 }}>
                {task.tag}
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );

  const renderMainContent = () => {
    // Top-level nav takes priority for non-Intelligence tabs
    if (activeNav === 'Command Center') return renderCommandCenter();
    if (activeNav === 'Workflows') return renderWorkflows();
    if (activeNav === 'Deployments') return renderDeployments();
    if (activeNav === 'Vault') return renderVault();

    // Sidebar views within Intelligence
    switch (activeSidebar) {
      case 'Suggestions': return renderSuggestionsPage();
      case 'Tasks': return renderTasksPage();
      case 'Automations': return renderAutomations();
      case 'Meeting Prep': return renderMeetingPrep();
      case 'Documents': return renderDocuments();
      case 'Wrap-Up': return renderDrift();
      case 'OTV Clients': return renderOTVClients();
      case 'Investor Prep': return renderInvestorPrep();
      case 'ARIA Chat': return renderMorningBrief();
      default: return renderMorningBrief();
    }
  };

  // Password gate screen
  if (!isUnlocked) {
    return (
      <div className="password-gate">
        <div className="password-card">
          <div className="password-logo"><LogoSVG /></div>
          <h1>Harmonee <span style={{ color: 'var(--gold)' }}>Pulse</span></h1>
          <p>Strategic Intelligence Platform</p>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              className="password-input"
              placeholder="Enter access code"
              value={passwordInput}
              onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
              autoFocus
            />
            <button type="submit" className="password-btn" disabled={unlocking}>{unlocking ? 'Checking…' : 'Enter'}</button>
          </form>
          {passwordError && <div className="password-error">Invalid access code</div>}
        </div>
      </div>
    );
  }

  // ──── Onboarding Tour Steps ────
  const tourSteps = [
    {
      title: 'Welcome to Harmonee Pulse',
      body: 'This is your strategic intelligence dashboard. Everything about your business — emails, calendar, documents, tasks, and AI — lives on one screen. Let me walk you through it.',
      icon: '✦',
      highlight: null,
      location: null,
    },
    {
      title: 'Navigation Sidebar',
      body: 'On the left side of your screen is the sidebar. This is how you switch between your Morning Brief, Inbox, Calendar, Documents, Tasks, and more. Click any item to jump to that section.',
      icon: '◧',
      highlight: '.sidebar',
      location: '👈 Look at the left side of your screen',
    },
    {
      title: 'Your Morning Intelligence Brief',
      body: 'The center of your dashboard shows your live intelligence feed. Right now it\'s showing your Morning Brief — a summary of everything that needs your attention today: priority emails, meetings, and action items.',
      icon: '◉',
      highlight: '.content',
      location: '👀 Look at the center of your screen',
    },
    {
      title: 'Meet ARIA — Your AI Assistant',
      body: 'On the right side is ARIA, your AI chief of staff. Type any question and she\'ll answer using your real data. Try things like "Draft a follow-up to Caroline" or "What meetings do I have today?" or "Summarize my recent emails."',
      icon: '◈',
      highlight: '.right-panel',
      location: '👉 Look at the right panel',
    },
    {
      title: 'Business Health Score',
      body: 'In the top-right corner, you\'ll see your ALYGN score — a single number that tracks your business health across revenue, pipeline, operations, and engagement. Click it to see the breakdown.',
      icon: '●',
      highlight: '.health-score-widget',
      location: '👆 Look at the top-right corner',
    },
    {
      title: 'Connect Your Accounts',
      body: 'See the Google and Outlook buttons at the top? Click one to connect your email account. Once connected, the dashboard pulls your real emails, calendar events, and documents — live. Your data never leaves your browser.',
      icon: '⊕',
      highlight: '.sign-in-group',
      location: '👆 Look at the top bar — Google & Outlook buttons',
    },
    {
      title: 'You\'re Ready to Go',
      body: 'That\'s the full tour! Start by connecting your Google or Outlook account at the top, then try asking ARIA a question in the right panel. She gets smarter the more you use her.',
      icon: '✦',
      highlight: null,
      location: null,
      isFinal: true,
    },
  ];

  const handleTourNext = () => {
    if (tourStep >= tourSteps.length - 1) {
      setShowTour(false);
      localStorage.setItem('harmonee_tour_seen', 'true');
      posthog.capture('tour_completed');
    } else {
      posthog.capture('tour_step', { step: tourStep + 1, title: tourSteps[tourStep + 1].title });
      setTourStep(tourStep + 1);
    }
  };

  const handleTourSkip = () => {
    setShowTour(false);
    localStorage.setItem('harmonee_tour_seen', 'true');
    posthog.capture('tour_skipped', { skipped_at_step: tourStep, title: tourSteps[tourStep].title });
  };

  // Expose restart for settings panel (future)
  // eslint-disable-next-line
  window.__restartTour = () => { setTourStep(0); setShowTour(true); };

  return (
    <>
      {/* ──── Onboarding Tour ──── */}
      {showTour && <div className="tour-overlay" />}
      {showTour && (
        <div className={`tour-card tour-card-center ${tourSteps[tourStep].isFinal ? 'tour-final' : ''}`} key={`tour-${tourStep}`}>
          <div className="tour-step-counter">Step {tourStep + 1} of {tourSteps.length}</div>
          <div className="tour-card-icon">{tourSteps[tourStep].icon}</div>
          <h2 className="tour-card-title">{tourSteps[tourStep].title}</h2>
          <p className="tour-card-body">{tourSteps[tourStep].body}</p>
          {tourSteps[tourStep].location && (
            <div className="tour-location-hint">{tourSteps[tourStep].location}</div>
          )}
          <div className="tour-card-footer">
            <div className="tour-dots">
              {tourSteps.map((_, i) => (
                <span key={i} className={`tour-dot ${i === tourStep ? 'active' : ''} ${i < tourStep ? 'done' : ''}`} />
              ))}
            </div>
            <div className="tour-actions">
              <button className="tour-skip" onClick={handleTourSkip}>
                {tourSteps[tourStep].isFinal ? '' : 'Skip tour'}
              </button>
              <button className="tour-next" onClick={handleTourNext}>
                {tourStep === 0 ? 'Start tour →' : tourSteps[tourStep].isFinal ? 'Let\'s go →' : 'Next →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOPBAR */}
      <div className="topbar">
        <div className="topbar-left">
          <button className="logo" onClick={() => { setActiveNav('Intelligence'); setActiveSidebar('Morning Brief'); }}>
            <div className="logo-mark"><LogoSVG /></div>
            <span className="logo-name">Harmonee<span className="logo-tm">™</span></span>
          </button>
          <nav className="nav-tabs">
            {navTabs.map(tab => (
              <div key={tab} className={`nav-tab ${activeNav === tab ? 'active' : ''}`} onClick={() => {
                setActiveNav(tab);
                if (tab === 'Intelligence') setActiveSidebar('Morning Brief');
              }}>
                {tab}
              </div>
            ))}
          </nav>
        </div>
        <div className="topbar-right">
          {/* Business Health Score - ALYGN */}
          <div className={`health-score-widget ${showTour && tourSteps[tourStep]?.highlight === '.health-score-widget' ? 'tour-spotlight-active' : ''}`} onClick={() => setHealthExpanded(!healthExpanded)}>
            <div className="health-orb" style={{ background: healthScore.color, boxShadow: `0 0 8px ${healthScore.color}` }}></div>
            <div className="health-info">
              <div className="health-value" style={{ color: healthScore.color }}>{healthScore.overall}</div>
              <div className="health-label">{healthScore.label}</div>
            </div>
            <div className="health-bar-mini">
              <div className="health-bar-fill" style={{ width: `${healthScore.overall}%`, background: healthScore.color }}></div>
            </div>
            {healthExpanded && (
              <div className="health-dropdown">
                <div className="health-dropdown-title">
                  <span>ALYGN · Business Health Score</span>
                  <span className="health-score-big" style={{ color: healthScore.color }}>{healthScore.overall}/100</span>
                </div>
                {Object.values(healthScore.breakdown).map((item, i) => (
                  <div key={i} className="health-row">
                    <div className="health-row-label">{item.label}</div>
                    <div className="health-row-bar">
                      <div className="health-row-fill" style={{
                        width: `${item.score}%`,
                        background: item.score >= 75 ? 'var(--green)' : item.score >= 50 ? 'var(--gold)' : 'var(--red)',
                      }}></div>
                    </div>
                    <div className="health-row-detail">{item.detail}</div>
                  </div>
                ))}
                <div className="health-footer">Powered by ALYGN · Updates in real-time</div>
              </div>
            )}
          </div>
          <div className="theme-fader-wrap">
            <span className={`theme-fader-icon ${theme === 'dark' ? 'active' : ''}`}>●</span>
            <button className="theme-fader" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} />
            <span className={`theme-fader-icon ${theme === 'light' ? 'active' : ''}`}>○</span>
          </div>
          <div className="engine-status">
            <div className="engine-pill active"><div className="dot"></div>ARIA</div>
            <div className="engine-pill active"><div className="dot"></div>ALYGNMENT</div>
            <div className="engine-pill active"><div className="dot"></div>LCM</div>
          </div>
          {/* Last-checked status moved to bottom of sidebar — see sidebar-footer */}
          <div className={`sign-in-group ${showTour && tourSteps[tourStep]?.highlight === '.sign-in-group' ? 'tour-spotlight-active' : ''}`}>
            {authReady && isConfigured() && googleAccounts.length === 0 && (
              <button className="sign-in-btn" onClick={handleSignIn}>
                <GIcon /> Google
              </button>
            )}
            {googleAccounts.length > 0 && (
              <button
                className="sign-in-btn connected-btn"
                onClick={() => setAccountHubOpen(v => !v)}
                title={`${googleAccounts.length} Google account${googleAccounts.length === 1 ? '' : 's'} connected — manage in the account menu`}
              >
                <GIcon /> {googleAccounts.length > 1 ? `✓ ${googleAccounts.length}` : '✓'}
              </button>
            )}
            {!msAuthedState && (
              <button className="sign-in-btn ms-btn" onClick={handleMsSignIn}>
                <MsIcon /> Outlook
              </button>
            )}
            {msAuthedState && (
              <button className="sign-in-btn ms-btn connected-btn" onClick={() => setAccountHubOpen(v => !v)} title="Outlook connected — manage in the account menu">
                <MsIcon /> ✓
              </button>
            )}
          </div>
          <div className="user-chip-wrap hm-gacct">
            <div
              className="user-chip"
              ref={userChipRef}
              role="button"
              tabIndex={0}
              aria-haspopup="menu"
              aria-expanded={accountHubOpen}
              title="Accounts & connections"
              onClick={() => setAccountHubOpen(v => !v)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAccountHubOpen(v => !v); } }}
            >
              {isAuthed && userName ? (
                <>
                  {userPicture ? (
                    <img className="avatar avatar-img" src={userPicture} alt={userName} referrerPolicy="no-referrer" />
                  ) : (
                    <div className="avatar">{userInitials}</div>
                  )}
                  <span className="user-name">{userName}</span>
                </>
              ) : (
                <>
                  <div className="avatar">H</div>
                  <span className="user-name">Harmonee</span>
                </>
              )}
              <span className="user-chip-caret" aria-hidden="true">▾</span>
            </div>
            <AccountMenu
              open={accountHubOpen}
              onClose={() => setAccountHubOpen(false)}
              anchorRef={userChipRef}
              googleAccounts={googleAccounts}
              adding={addingAccount}
              onConnectGoogle={handleSignIn}
              onAddGoogle={handleAddGoogleAccount}
              onRemoveGoogle={handleRemoveGoogleAccount}
              msAuthed={msAuthedState}
              msUser={msUser}
              onConnectMs={handleMsSignIn}
              onDisconnectMs={handleMsSignOut}
            />
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div className="main" data-mobile-panel={mobilePanel} data-mobile-sidebar={mobileSidebarOpen ? 'open' : 'closed'} data-aria-drawer={ariaDrawerOpen ? 'open' : 'closed'}>
        {/* LEFT SIDEBAR */}
        <div className={`sidebar ${showTour && tourSteps[tourStep]?.highlight === '.sidebar' ? 'tour-spotlight-active' : ''}`}>
          <div className="sidebar-section">
            <div className="sidebar-label">Workspace</div>
            {sidebarWorkspace.map(item => (
              <div key={item.name} role="button" tabIndex={0} className={`sidebar-item ${activeSidebar === item.name && activeNav === 'Intelligence' ? 'active' : ''}`} onClick={() => {
                setActiveNav('Intelligence');
                handleSidebarClick(item.name);
              }} onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveNav('Intelligence'); handleSidebarClick(item.name); }
              }}>
                <span style={{ display: 'flex', width: 16, justifyContent: 'center', flexShrink: 0 }}><HarmoneeIcon name={item.icon} size={15} color="var(--mid)" /></span>
                <span className="sidebar-item-label">{item.name}</span>
                {item.badge != null && item.badge > 0 && <div className={`badge ${item.badgeType === 'green' ? 'green' : item.badgeType === 'blue' ? 'blue' : item.badgeType === 'red' ? 'red' : ''}`}>{item.badge}</div>}
              </div>
            ))}
          </div>

          <div className="sidebar-section">
            <div className="sidebar-label">Projects</div>
            {sidebarProjects.map(p => (
              <div key={p.name} role="button" tabIndex={0} className={`sidebar-item ${activeProject === p.name ? 'active' : ''}`} onClick={() => handleProjectClick(p.name)} onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleProjectClick(p.name); }
              }} style={activeProject === p.name ? { background: `${p.color}15`, color: p.color } : {}}>
                <span className="project-dot" style={{ background: p.color }}></span>
                <span className="sidebar-item-label">{p.name}</span>
              </div>
            ))}
          </div>

          <div className="sidebar-section">
            <div className="sidebar-label">Today's Schedule</div>
            <div className="sidebar-calendar">
              {data.calendar.length === 0 ? (
                <div className="cal-empty">No events today</div>
              ) : (
                data.calendar.map((event, i) => (
                  <div key={i} className="sidebar-cal-item" style={{ borderLeftColor: event.color }} onClick={() => { setActiveNav('Intelligence'); setActiveSidebar('Meeting Prep'); }}>
                    <div className="sidebar-cal-time">{event.start}</div>
                    <div className="sidebar-cal-info">
                      <div className="sidebar-cal-title">{event.summary}</div>
                      {event.location && <div className="sidebar-cal-loc">{event.location}</div>}
                      {event.sub && <div className="sidebar-cal-sub">{event.sub}</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ──── Sidebar footer — Last-checked status ──── */}
          {isAuthed && (
            <div className="sidebar-footer">
              <div className={`sidebar-check ${isLoading ? 'scanning' : ''}`} title={lastChecked ? `Last checked: ${new Date(lastChecked).toLocaleTimeString()}` : 'Not yet checked'}>
                <div className="sidebar-check-status">
                  <div className="live-dot"></div>
                  <div className="sidebar-check-text">
                    {checkingStatus ? (
                      <>
                        <span className="sidebar-check-label">ARIA is scanning</span>
                        <span className="sidebar-check-detail">{checkingStatus}</span>
                      </>
                    ) : lastChecked ? (
                      <>
                        <span className="sidebar-check-label">Last checked</span>
                        <span className="sidebar-check-time">{formatRelativeTime(lastChecked)}</span>
                      </>
                    ) : (
                      <>
                        <span className="sidebar-check-label">Ready</span>
                        <span className="sidebar-check-time">Standing by</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  className={`sidebar-check-btn ${isLoading ? 'loading' : ''}`}
                  onClick={loadLiveData}
                  disabled={isLoading}
                  title="Check now"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M21 12a9 9 0 1 1-3-6.7"/>
                    <polyline points="21 4 21 10 15 10"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* MAIN CONTENT */}
        <div className={`content ${showTour && tourSteps[tourStep]?.highlight === '.content' ? 'tour-spotlight-active' : ''}`} key={`${activeNav}-${activeSidebar}`}>
          {renderMainContent()}
        </div>

        {/* ARIA drawer affordances (tablet range only — CSS-gated) */}
        <button
          className="aria-fab"
          onClick={() => setAriaDrawerOpen(true)}
          aria-label="Open ARIA chat"
          title="Open ARIA"
        >
          <span className="aria-fab-orb" aria-hidden="true" />
          <span className="aria-fab-label">ARIA</span>
        </button>
        {ariaDrawerOpen && (
          <div className="aria-drawer-overlay" onClick={() => setAriaDrawerOpen(false)} />
        )}

        {/* RIGHT PANEL */}
        <div
          ref={rightPanelRef}
          className={`right-panel ${showTour && tourSteps[tourStep]?.highlight === '.right-panel' ? 'tour-spotlight-active' : ''}`}
          // When the panel is off-screen (closed drawer / other mobile tab),
          // inert removes it from tab order and silences its aria-live line.
          // React 18 passes the attribute through when given a string.
          inert={((viewportTier === 'tablet' && !ariaDrawerOpen) || (viewportTier === 'mobile' && mobilePanel !== 'aria')) ? '' : undefined}
        >
          <div className="panel-tabs">
            <div className="panel-tab active">ARIA Chat</div>
          </div>

          {/* ARIA Chat Pane */}
          <div className="panel-content panel-pane active" ref={chatRef}>
            {/* Recent Context */}
            {(() => {
              const rcTopics = [
                ...(data.gmail?.priorityThreads || []).slice(0, 3).map(t => ({
                  label: t.from.split(' ')[0] + ': ' + (t.subject.length > 28 ? t.subject.slice(0, 28) + '...' : t.subject),
                  key: t.from.split(' ')[0].toLowerCase(),
                  type: 'gmail'
                })),
                ...(data.calendar || []).slice(0, 1).map(e => ({
                  label: e.summary.length > 30 ? e.summary.slice(0, 30) + '...' : e.summary,
                  key: e.summary.split(' ')[0].toLowerCase(),
                  type: 'calendar'
                }))
              ];
              return rcTopics.length > 0 ? (
                <div className="recent-context">
                  <div className="rc-header" onClick={() => setRcExpanded(!rcExpanded)}>
                    <span className="rc-label">RECENT CONTEXT{!rcExpanded ? ` \u00b7 ${rcTopics.length} topics` : ''}</span>
                    <span className="rc-toggle">{rcExpanded ? '\u25B4' : '\u25BE'}</span>
                  </div>
                  {rcExpanded && (
                    <div className="rc-chips">
                      {rcTopics.map((topic, i) => (
                        <div key={i} className={`rc-chip ${topic.type}`} onClick={() => handleActionClick(topic.key)}>
                          {topic.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null;
            })()}
            <div className="chat-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`msg msg-${msg.type}`}>
                  <div className="msg-avatar">{msg.type === 'aria' ? 'A' : 'M'}</div>
                  <div className="msg-bubble">
                    <div className="msg-sender">{msg.sender}</div>
                    <div className="msg-text" dangerouslySetInnerHTML={{ __html: msg.text }} />
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="msg msg-aria" style={{ padding: '0 0 8px 32px' }}>
                  <div className="typing"><span></span><span></span><span></span></div>
                </div>
              )}
            </div>
          </div>

          {/* Chat Input */}
          <div className="chat-input-area">
            <AriaStateIndicator
              state={isTyping ? 'thinking' : isListening ? 'listening' : 'watching'}
              sources={[
                ...(googleAuthed ? [googleAccounts.length > 1 ? `Gmail ×${googleAccounts.length}` : 'Gmail', 'Calendar', 'Drive'] : []),
                ...(msAuthedState ? ['Outlook'] : []),
              ]}
              lastSync={lastChecked ? formatRelativeTime(lastChecked) : null}
            />
            <div className="voice-bar">
              <button className={`voice-toggle ${voiceEnabled ? 'on' : ''}`} onClick={toggleVoiceMode} title={voiceEnabled ? 'ARIA voice replies on — click to mute' : 'Enable voice replies'}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {voiceEnabled ? (
                    <><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></>
                  ) : (
                    <><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></>
                  )}
                </svg>
                <span>{voiceEnabled ? 'ARIA speaks' : 'Voice off'}</span>
              </button>
              <span className="voice-hint">⌘ + ⇧ + Space to talk</span>
            </div>
            <div className="chat-input-wrap">
              <textarea
                ref={inputRef}
                className="chat-input"
                placeholder={isListening ? 'Listening...' : 'Ask ARIA anything about your day…'}
                rows="2"
                value={chatInput}
                onChange={e => {
                  setChatInput(e.target.value);
                  // Auto-grow textarea
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                }}
                onKeyDown={handleKeyDown}
              />
              <button className={`chat-mic ${isListening ? 'listening' : ''}`} onClick={startVoiceInput} title="Push to talk (⌘ + ⇧ + Space)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <rect x="9" y="2" width="6" height="12" rx="3"/>
                  <path d="M19 11a7 7 0 0 1-14 0M12 18v4M8 22h8"/>
                </svg>
              </button>
              <button className="chat-send" onClick={() => handleChatSubmit()}>
                <SendIcon />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ──── ARIA Intelligence Brief Lightbox ──── */}
      {briefModal.open && (
        <div
          className={`brief-lightbox-overlay ${briefModal.closing ? 'brief-closing' : ''}`}
          onClick={() => {
            setBriefModal(prev => ({ ...prev, closing: true }));
            setTimeout(() => {
              setBriefModal({ open: false, text: '', closing: false });
              setActivePanel('aria');
              setTimeout(() => inputRef.current?.focus(), 300);
            }, 600);
          }}
        >
          <div
            className={`brief-lightbox ${briefModal.closing ? 'brief-closing' : ''}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="brief-lb-glow" />
            <div className="brief-lb-header">
              <div className="brief-lb-icon"><LogoSVG /></div>
              <div className="brief-lb-title">
                <span className="brief-lb-label">ARIA</span>
                <span className="brief-lb-subtitle">Intelligence Brief</span>
              </div>
            </div>
            <div className="brief-lb-divider" />
            <div className="brief-lb-body">
              {briefModal.text.split('\n').map((line, i) => {
                // Convert markdown-style **bold** to HTML
                const formatted = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
                if (line.startsWith('•') || line.startsWith('-')) {
                  return <div key={i} className="brief-lb-bullet" dangerouslySetInnerHTML={{ __html: formatted }} />;
                }
                if (formatted.includes('<strong>')) {
                  return <p key={i} className="brief-lb-para" dangerouslySetInnerHTML={{ __html: formatted }} />;
                }
                return line.trim() ? <p key={i} className="brief-lb-para">{line}</p> : null;
              })}
            </div>
            <div className="brief-lb-divider" />
            <div className="brief-lb-footer">
              <button
                className="brief-lb-dismiss"
                onClick={() => {
                  setBriefModal(prev => ({ ...prev, closing: true }));
                  setTimeout(() => {
                    setBriefModal({ open: false, text: '', closing: false });
                    setActivePanel('aria');
                    setTimeout(() => inputRef.current?.focus(), 300);
                  }, 600);
                }}
              >
                Let's get to work
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ──── Mobile Bottom Navigation ──── */}
      <nav className="mobile-bottom-nav">
        <button className={`mobile-nav-btn ${mobilePanel === 'home' ? 'active' : ''}`} onClick={() => { setMobilePanel('home'); setMobileSidebarOpen(false); posthog.capture('mobile_nav', { tab: 'home' }); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          <span>Home</span>
        </button>
        <button className={`mobile-nav-btn ${mobilePanel === 'aria' ? 'active' : ''}`} onClick={() => { setMobilePanel('aria'); setMobileSidebarOpen(false); posthog.capture('mobile_nav', { tab: 'aria' }); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <span>ARIA</span>
        </button>
        <button className={`mobile-nav-btn ${mobilePanel === 'menu' ? 'active' : ''}`} onClick={() => { setMobileSidebarOpen(!mobileSidebarOpen); setMobilePanel('menu'); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          <span>Menu</span>
        </button>
      </nav>

      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && <div className="mobile-sidebar-overlay" onClick={() => { setMobileSidebarOpen(false); setMobilePanel('home'); }} />}
    </>
  );
};

export default HarmoneePulse;
